import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Plus, Minus, TrendingUp, TrendingDown, DollarSign, BarChart3, Calendar, Settings, Wallet, Sun, Moon, Tag, Database, LogOut, User, X, ArrowRight, Palette, Filter, Trash2, AlertCircle, CheckCircle, Upload, Euro, Edit, ArrowDown, Store, ArrowLeft, Eye, EyeOff, Users, ChevronDown, ArrowUpDown, Check, Copy } from 'lucide-react';
import ExpenseForm from './components/ExpenseForm';
import ExpenseList from './components/ExpenseList';
import CategoryManager from './components/CategoryManager';
import WalletManager from './components/WalletManager';
import Statistics from './components/Statistics';
import DataManager from './components/DataManager';
import LoginForm from './components/LoginForm';
import UserProfile from './components/UserProfile';
import ConfirmDialog from './components/ConfirmDialog';
import BulkEditDialog from './components/BulkEditDialog';
import FilterDialog from './components/FilterDialog';
import TransactionDetailDialog from './components/TransactionDetailDialog';
import StoreTransactionsPanel from './components/StoreTransactionsPanel';
import DebtList from './components/DebtList';
import DebtForm from './components/DebtForm';
import PersonDetail from './components/PersonDetail';
import SalaryWizard from './components/SalaryWizard';
import DebtTransactionDetailDialog from './components/DebtTransactionDetailDialog';
import RecoverCategoriesDialog from './components/RecoverCategoriesDialog';
import InvestmentDashboard from './components/InvestmentDashboard';
import { useAuth } from './hooks/useAuth';
import { useFirestore, useCollectionData } from './hooks/useFirestore';
import { useSeedUserDocs, ensureDefaultDoc } from './features/categories/useSeedUserDocs';
import { useTheme } from './hooks/useTheme';
import { useScrollLock } from './hooks/useScrollLock';
import { useRecurringTransactions } from './hooks/useRecurringTransactions';
import { useBackup } from './hooks/useBackup';
import useIsMobileViewport from './hooks/useIsMobileViewport';
import { formatCurrency, getCurrentRomeTime } from './utils/formatters';
import { extractMoneyDigits, formatMoneyInput, amountToMoneyDigits, moneyDigitsToNumber } from './utils/moneyInput';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { PopupProvider, usePopup } from './contexts/PopupContext';
import { getEasterEgg, activateEasterEgg, saveEasterEggCompletion, getEasterEggsWithCompletionStatus } from './utils/easterEggs';
import CustomSelect from './components/CustomSelect';
import { addCategory, editCategory, deleteCategory, findMissingCategories, withCanonicalCategory } from './features/categories/categoryLogic';
import { addWallet as addWalletLogic } from './features/wallets/walletLogic';
import { isFutureDate } from './features/expenses/expenseLogic';
import { addDebtTransaction, editDebtTransaction, deleteDebtTransaction, getUniquePersons, createResolutionTransaction, deletePersonWithAutomaticTransfers } from './features/debts/debtLogic';
import FilterButton from './components/FilterButton';
import ModelsDialog from './components/ModelsDialog';
import ModelForm from './components/ModelForm';
import { addModel, editModel, deleteModel, reorderModels } from './features/models/modelLogic';
import { generateFinancialSummaryText } from './utils/exportUtils';

import { generateId } from './utils/id';
import { DEBT_WALLET_ID, CREDIT_WALLET_ID, VIRTUAL_DEBT_WALLETS } from './constants/debtWallets';
import MobileAmountKeypad from './components/MobileAmountKeypad';

// Categoria predefinita per i conti senza tipologia
const DEFAULT_WALLET_TYPE_NAME = 'Conto bancario';

// Categorie predefinite
const CATEGORIES_NOT_LOADED = 'Le categorie non sono ancora caricate: riprova tra qualche secondo.';

const defaultCategories = {
  expense: [
    { id: 1, name: 'Alimentari', icon: '🍽️' },
    { id: 2, name: 'Trasporti', icon: '🚗' },
    { id: 3, name: 'Intrattenimento', icon: '🎮' },
    { id: 4, name: 'Shopping', icon: '🛍️' },
    { id: 5, name: 'Bollette', icon: '💡' },
    { id: 6, name: 'Salute', icon: '🏥' },
    { id: 7, name: 'Educazione', icon: '📚' },
    { id: 8, name: 'Trasferimento', icon: '💸' },
    { id: 9, name: 'Abbonamenti', icon: '📱' },
    { id: 10, name: 'Altro', icon: '📦' }
  ],
  income: [
    { id: 11, name: 'Stipendio', icon: '💼' },
    { id: 12, name: 'Freelance', icon: '💻' },
    { id: 13, name: 'Investimenti', icon: '📈' },
    { id: 14, name: 'Regali', icon: '🎁' },
    { id: 15, name: 'Vendite', icon: '🛒' },
    { id: 16, name: 'Bonus', icon: '🎯' },
    { id: 17, name: 'Trasferimento', icon: '💸' },
    { id: 18, name: 'Altro', icon: '📦' }
  ],
  wallet: [
    { id: 'wallet-cash', name: 'Contanti', icon: '💶', color: '#a855f7' }, // Purple
    { id: 'wallet-bank', name: DEFAULT_WALLET_TYPE_NAME, icon: '🏦', color: '#0ea5e9' }, // Sky/Azure
    { id: 'wallet-stocks', name: 'Azioni', icon: '📈', color: '#8b5cf6' }, // Violet
    { id: 'wallet-bonds', name: 'Obbligazioni', icon: '📜', color: '#f59e0b' }, // Amber
    { id: 'wallet-etf', name: 'ETF', icon: '🧺', color: '#eab308' } // Yellow
  ]
};

// Palette colori per portafogli
const WALLET_COLORS = [
  '#6366f1', // indigo
  '#f59e42', // orange
  '#10b981', // green
  '#ef4444', // red
  '#3b82f6', // blue
  '#f43f5e', // pink
  '#eab308', // yellow
  '#a21caf', // purple
  '#0ea5e9', // sky
  '#f97316', // amber
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f472b6', // rose
  'rainbow', // colore personalizzato
];

const normalizeCategoriesData = (data = {}) => ({
  expense: Array.isArray(data.expense) ? data.expense : defaultCategories.expense,
  income: Array.isArray(data.income) ? data.income : defaultCategories.income,
  wallet: Array.isArray(data.wallet) ? data.wallet : defaultCategories.wallet
});

const STORE_NAME_COLLATOR = new Intl.Collator('it', { sensitivity: 'base' });

const STORE_SORT_CONFIG = {
  balance_desc: {
    label: 'Saldo (decrescente)',
    description: 'Dal saldo migliore al peggiore',
    compare: (a, b) => b.netTotal - a.netTotal
  },
  balance_asc: {
    label: 'Saldo (crescente)',
    description: 'Dal saldo peggiore al migliore',
    compare: (a, b) => a.netTotal - b.netTotal
  },
  alphabetical_asc: {
    label: 'Alfabetico (A-Z)',
    description: 'Dalla A alla Z',
    compare: (a, b) => STORE_NAME_COLLATOR.compare(a.storeName, b.storeName)
  },
  alphabetical_desc: {
    label: 'Alfabetico (Z-A)',
    description: 'Dalla Z alla A',
    compare: (a, b) => STORE_NAME_COLLATOR.compare(b.storeName, a.storeName)
  },
  transactions_desc: {
    label: 'Numero transazioni (decrescente)',
    description: 'Dal negozio più attivo',
    compare: (a, b) => b.transactionCount - a.transactionCount
  },
  transactions_asc: {
    label: 'Numero transazioni (crescente)',
    description: 'Dal negozio meno attivo',
    compare: (a, b) => a.transactionCount - b.transactionCount
  },
  last_transaction_desc: {
    label: 'Ultima transazione (recente)',
    description: 'Dal movimento più recente',
    compare: (a, b) => b.lastTransactionDate - a.lastTransactionDate
  },
  last_transaction_asc: {
    label: 'Ultima transazione (meno recente)',
    description: 'Dalla movimentazione più datata',
    compare: (a, b) => a.lastTransactionDate - b.lastTransactionDate
  }
};

const STORE_SORT_ORDER = [
  'balance_desc',
  'balance_asc',
  'alphabetical_asc',
  'alphabetical_desc',
  'transactions_desc',
  'transactions_asc',
  'last_transaction_desc',
  'last_transaction_asc'
];

const STORE_SORT_OPTIONS = STORE_SORT_ORDER.map((value) => ({
  value,
  label: STORE_SORT_CONFIG[value].label,
  description: STORE_SORT_CONFIG[value].description
}));

const DEFAULT_STORE_SORT = 'alphabetical_asc';
const DATE_TILE_EXTRA_OFFSET = -28;
const TRANSACTIONS_PAGE_SIZE = 20;
const BANKNOTE_DENOMINATIONS = [5, 10, 20, 50, 100];

const addViewportResizeListener = (handler) => {
  if (typeof window === 'undefined') {
    return () => { };
  }

  window.addEventListener('resize', handler);
  const viewport = window.visualViewport;

  if (viewport) {
    viewport.addEventListener('resize', handler);
    viewport.addEventListener('scroll', handler);
  }

  return () => {
    window.removeEventListener('resize', handler);
    if (viewport) {
      viewport.removeEventListener('resize', handler);
      viewport.removeEventListener('scroll', handler);
    }
  };
};

const getSafeAreaInset = (side = 'top') => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0;
  }
  const root = document.documentElement;
  if (!root) {
    return 0;
  }
  const rawValue = getComputedStyle(root).getPropertyValue(`--safe-area-${side}`);
  const parsed = Number.parseFloat(rawValue);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getPrimaryHeaderHeight = (isHeaderExpanded) => {
  if (typeof document === 'undefined') {
    return isHeaderExpanded ? 96 : 72;
  }
  const header = document.querySelector('header.header-transition');
  if (header) {
    return header.offsetHeight;
  }
  return isHeaderExpanded ? 96 : 72;
};

const createEmptyWalletCheckCounts = () => BANKNOTE_DENOMINATIONS.reduce((accumulator, value) => ({
  ...accumulator,
  [value]: ''
}), {});

const sanitizeIntegerInput = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).replace(/\D/g, '');
};

const extractTransactionDateParts = (storedValue) => {
  const fallbackNow = getCurrentRomeTime();
  const fallbackDate = fallbackNow.toISOString().split('T')[0];
  const fallbackTime = fallbackNow.toTimeString().slice(0, 5);

  if (!storedValue) {
    return { date: fallbackDate, time: fallbackTime };
  }

  const parsedDate = new Date(storedValue);

  if (!Number.isNaN(parsedDate?.getTime?.())) {
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const hours = String(parsedDate.getHours()).padStart(2, '0');
    const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`
    };
  }

  if (typeof storedValue === 'string') {
    const [datePart, timePartRaw] = storedValue.split('T');
    const timePart = timePartRaw ? timePartRaw.slice(0, 5) : null;
    return {
      date: datePart || fallbackDate,
      time: timePart || fallbackTime
    };
  }

  return { date: fallbackDate, time: fallbackTime };
};

const createDefaultFilters = () => ({
  timeRange: 'all',
  startDate: '',
  endDate: '',
  selectedCategories: [],
  selectedStores: [],
  selectedWallets: [],
  transactionType: 'all',
  showTransfers: false,
  showDebtsAndCredits: false
});

const normalizeFilters = (filters) => {
  const defaults = createDefaultFilters();

  if (!filters || typeof filters !== 'object') {
    return defaults;
  }

  return {
    ...defaults,
    ...filters,
    selectedCategories: Array.isArray(filters.selectedCategories) ? [...filters.selectedCategories] : [],
    selectedStores: Array.isArray(filters.selectedStores) ? [...filters.selectedStores] : [],
    selectedWallets: Array.isArray(filters.selectedWallets) ? filters.selectedWallets.filter(Boolean) : [],
    transactionType: filters.transactionType || 'all',
    showTransfers: typeof filters.showTransfers === 'boolean' ? filters.showTransfers : false,
    showDebtsAndCredits: typeof filters.showDebtsAndCredits === 'boolean' ? filters.showDebtsAndCredits : false,
    startDate: filters.startDate || '',
    endDate: filters.endDate || ''
  };
};

const createDefaultStatisticsPreferences = () => ({
  trendView: 'week',
  comparisonRange: 'week',
  comparisonMetric: 'balance',
  customStartDate: '',
  customEndDate: '',
});

const normalizeStatisticsPreferences = (preferences) => {
  const defaults = createDefaultStatisticsPreferences();

  if (!preferences || typeof preferences !== 'object') {
    return defaults;
  }

  const validTrendViews = new Set(['week', 'month']);
  const validComparisonRanges = new Set(['week', 'month', 'year', 'custom']);
  const validComparisonMetrics = new Set(['balance', 'income', 'expense']);

  return {
    trendView: validTrendViews.has(preferences.trendView) ? preferences.trendView : defaults.trendView,
    comparisonRange: validComparisonRanges.has(preferences.comparisonRange) ? preferences.comparisonRange : defaults.comparisonRange,
    comparisonMetric: validComparisonMetrics.has(preferences.comparisonMetric) ? preferences.comparisonMetric : defaults.comparisonMetric,
    customStartDate: preferences.customStartDate || '',
    customEndDate: preferences.customEndDate || '',
  };
};



// FILE LOCAL HELPER
const getRandomVibrantColor = () => {
  let hue;
  do {
    hue = Math.floor(Math.random() * 360);
  } while (
    (hue >= 340 || hue <= 20) || // Exclude Red (Debts)
    (hue >= 80 && hue <= 160)    // Exclude Green (Credits)
  );
  return `hsl(${hue}, 70%, 50%)`;
};

function App() {
  const lastRunRef = useRef(0);
  const { theme, toggleTheme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const { showAlert, showSuccess, showError, showEasterEgg, showConfirm } = usePopup();
  const { processRecurringTransactions } = useRecurringTransactions();
  const { createBackup } = useBackup();
  const isMobileViewport = useIsMobileViewport();



  const {

    addDocument,
    updateDocument,
    deleteDocument,
    deleteMultipleDocuments,
    loadAllUserData,
    importUserData,
    setEasterEggCompleted,
    getCompletedEasterEggs,
    deleteAllUserData,
    loadUserSettings,
    saveUserSettings,
  } = useFirestore();

  // Funzione per esportare tutti i dati dell'utente
  const exportData = useCallback(async () => {
    try {
      const firestoreData = await loadAllUserData();
      return {
        metadata: {
          version: '2.0',
          exportedAt: new Date().toISOString()
        },
        user: user ? {
          uid: user.uid,
          email: user.email || null
        } : null,
        theme,
        data: firestoreData
      };
    } catch (error) {
      console.error('Errore durante l\'esportazione dei dati:', error);
      throw error;
    }
  }, [loadAllUserData, user, theme]);


  const loadUserSettingsRef = useRef(loadUserSettings);
  const saveUserSettingsRef = useRef(saveUserSettings);

  // Stati per il tracking della sincronizzazione
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [userSettings, setUserSettings] = useState(null);

  // Stati per i dati Firebase
  const { data: expensesRaw, loading: expensesLoading } = useCollectionData('expenses');
  const { data: incomesRaw, loading: incomesLoading } = useCollectionData('incomes');
  const { data: recurringRaw, loading: recurringLoading } = useCollectionData('recurringTransactions', 'nextOccurrence');
  const { data: categoriesData, loading: categoriesLoading } = useCollectionData('categories', null);
  const { data: storesData, loading: storesLoading } = useCollectionData('stores', null);
  const { data: walletsRaw, loading: walletsLoading } = useCollectionData('wallets', 'createdAt');
  const { data: modelsData, loading: modelsLoading } = useCollectionData('models', 'createdAt');
  const { data: debtsData, loading: debtsLoading } = useCollectionData('debts', 'createdAt');
  const { data: investmentPlansData } = useCollectionData('investmentPlans', 'createdAt');

  const isDataLoading = expensesLoading || incomesLoading || recurringLoading || categoriesLoading || storesLoading || walletsLoading || modelsLoading || debtsLoading;
  const isBackupInProgressRef = useRef(false);

  // Automatic Cloud Backup Logic
  useEffect(() => {
    if (!user || isDataLoading || isBackupInProgressRef.current) return;

    const performAutoBackup = async () => {
      if (isBackupInProgressRef.current) return;
      
      try {
        const settings = await loadUserSettings();
        const isCloudBackupUnlocked = settings?.isCloudBackupUnlocked === true;
        
        if (!isCloudBackupUnlocked) return;

        const lastBackupTime = settings?.lastBackupTime;
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;

        if (!lastBackupTime || (now - new Date(lastBackupTime).getTime()) > twentyFourHours) {
          isBackupInProgressRef.current = true;
          const dataToBackup = await exportData();
          await createBackup(dataToBackup);
          
          // Aggiorna il timestamp del backup nelle impostazioni
          await saveUserSettings({
            ...settings,
            lastBackupTime: new Date().toISOString()
          });
        }
      } catch (err) {
        console.error('Errore durante il backup automatico:', err);
      } finally {
        // Non resettiamo isBackupInProgressRef a false se ha avuto successo o se vogliamo 
        // evitare altri tentativi in questa sessione specifica in caso di errore CORS.
        // In questo caso lo lasciamo true per evitare il loop infinito visto nei log.
      }
    };

    const timeoutId = setTimeout(performAutoBackup, 5000);
    return () => clearTimeout(timeoutId);
  }, [user, isDataLoading, createBackup, exportData, loadUserSettings, saveUserSettings]);



  // Stati locali
  const [categories, setCategories] = useState(defaultCategories);
  const [stores, setStores] = useState([]);
  const [wallets, setWallets] = useState([]);

  // Collega i movimenti alla categoria in lista anche se il nome differisce per spazi/maiuscole.
  // Solo in lettura: il nome salvato su Firestore cambia solo se l'utente modifica e salva.
  const expenses = useMemo(() => withCanonicalCategory(expensesRaw, categories.expense), [expensesRaw, categories.expense]);
  const incomes = useMemo(() => withCanonicalCategory(incomesRaw, categories.income), [incomesRaw, categories.income]);
  const recurringTransactions = useMemo(() => (Array.isArray(recurringRaw)
    ? recurringRaw.map(tx => withCanonicalCategory([tx], tx.type === 'income' ? categories.income : categories.expense)[0])
    : recurringRaw), [recurringRaw, categories.expense, categories.income]);
  const walletsData = useMemo(() => withCanonicalCategory(walletsRaw, categories.wallet, 'type'), [walletsRaw, categories.wallet]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [transactionType, setTransactionType] = useState('expense'); // 'expense' or 'income'
  const [activeTab, setActiveTab] = useState('transactions');
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const dateRange = null;
  const [activeWalletId, setActiveWalletId] = useState(null);
  const [balanceCollapsed, setBalanceCollapsed] = useState(() => {
    // Su mobile (layout verticale) è chiusa, su tablet/desktop (layout orizzontale) è aperta
    return typeof window !== 'undefined' ? window.innerWidth < 1024 : true; // lg breakpoint
  });
  const balanceViewportCategoryRef = useRef(
    typeof window === 'undefined' ? 'mobile' : (window.innerWidth < 1024 ? 'mobile' : 'desktop')
  );
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showWalletForm, setShowWalletForm] = useState(false);
  const [editingWallet, setEditingWallet] = useState(null);
  const [walletFormData, setWalletFormData] = useState({
    name: '',
    color: WALLET_COLORS[0],
    balance: '',
    type: DEFAULT_WALLET_TYPE_NAME,
    grossAnnualRate: '',
    isinCode: '',
    isinName: '',
    expectedRate: ''
  });
  const [tickerSearchError, setTickerSearchError] = useState(null);
  const [isTickerSearching, setIsTickerSearching] = useState(false);

  const searchTicker = async (query) => {
    if (!query || query.trim().length < 2) {
      setTickerSearchError(null);
      return;
    }
    setIsTickerSearching(true);
    setTickerSearchError(null);
    try {
      const url = `/api/yahoo/v1/finance/search?q=${encodeURIComponent(query)}`;
      const response = await fetch(url);
      if (response.status === 429) {
        setTickerSearchError('Limite di ricerche Yahoo raggiunto. Inserisci il nome del titolo manualmente.');
        return;
      }
      if (!response.ok) throw new Error('Errore di rete');
      const parsedData = await response.json();
      if (parsedData && Array.isArray(parsedData.quotes) && parsedData.quotes.length > 0) {
        const quote = parsedData.quotes.find(q => q.symbol?.toUpperCase() === query.toUpperCase()) || parsedData.quotes[0];
        if (quote && (quote.shortname || quote.longname || quote.symbol)) {
          const fetchedName = quote.shortname || quote.longname || quote.symbol;
          setWalletFormData(prev => ({
            ...prev,
            isinName: fetchedName,
            isinCode: quote.symbol || query.toUpperCase()
          }));
        } else {
          setTickerSearchError('Codice trovato ma senza nome valido su Yahoo Finance. Puoi inserire il nome del titolo manualmente.');
        }
      } else {
        setTickerSearchError('Ticker non trovato su Yahoo Finance. Puoi comunque salvare inserendo il nome del titolo manualmente.');
      }
    } catch (error) {
      console.error('Errore durante il recupero del ticker:', error);
      setTickerSearchError('Impossibile recuperare il titolo da Yahoo. Puoi inserire il nome del titolo manualmente.');
    } finally {
      setIsTickerSearching(false);
    }
  };
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFormData, setTransferFormData] = useState({
    fromWalletId: '',
    toWalletId: '',
    amount: ''
  });
  const [showTransferKeypad, setShowTransferKeypad] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const categoriesSnapshotRef = useRef('');
  const [customColor, setCustomColor] = useState('#6366f1');
  const walletCategories = useMemo(() => {
    if (Array.isArray(categories.wallet)) {
      return categories.wallet.length > 0 ? categories.wallet : [];
    }
    return defaultCategories.wallet;
  }, [categories.wallet]);
  const walletCategoryOptions = useMemo(
    () => walletCategories.map((category) => ({
      value: category.name,
      label: category.name,
      icon: category.icon
    })),
    [walletCategories]
  );
  const getDefaultWalletType = useCallback(
    () => {
      if (Array.isArray(categories.wallet) && categories.wallet.length === 0) {
        return '';
      }
      return walletCategories[0]?.name || DEFAULT_WALLET_TYPE_NAME;
    },
    [walletCategories, categories.wallet]
  );
  useEffect(() => {
    if (!showWalletForm) {
      return;
    }
    const defaultType = getDefaultWalletType();
    const hasValidType = walletCategoryOptions.some(option => option.value === walletFormData.type);
    if (!hasValidType && walletFormData.type !== defaultType) {
      setWalletFormData(prev => ({ ...prev, type: defaultType }));
    }
  }, [walletCategoryOptions, walletFormData.type, getDefaultWalletType, showWalletForm]);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [activeFilters, setActiveFilters] = useState(createDefaultFilters);
  const [statisticsPreferences, setStatisticsPreferences] = useState(createDefaultStatisticsPreferences);
  const [storeSortOption, setStoreSortOption] = useState(DEFAULT_STORE_SORT);
  const [showStoreSortMenu, setShowStoreSortMenu] = useState(false);
  const [showFloatingMenu, setShowFloatingMenu] = useState(true);
  const lastScrollY = useRef(window.scrollY);
  const scrollFrameRef = useRef(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true); // Di default è espanso
  const [rainbowMode, setRainbowMode] = useState(false);
  const [partyMode, setPartyMode] = useState(false);
  const [retroMode, setRetroMode] = useState(false);
  const walletTapCount = useRef(0);
  const lastTapTime = useRef(0);
  const storeSortMenuRef = useRef(null);
  const balanceHeaderGestureRef = useRef({ x: 0, y: 0, allowToggle: false });
  const hasHighlightedFilters = useMemo(
    () =>
      activeFilters.timeRange !== 'all' ||
      activeFilters.selectedCategories.length > 0 ||
      activeFilters.selectedStores.length > 0,
    [activeFilters]
  );
  const bulkEditCategoryOptions = useMemo(() => {
    const combined = [
      ...(Array.isArray(categories.expense) ? categories.expense : []),
      ...(Array.isArray(categories.income) ? categories.income : [])
    ];
    const seen = new Set();
    return combined.filter(category => {
      if (!category?.name || seen.has(category.name)) {
        return false;
      }
      seen.add(category.name);
      return true;
    });
  }, [categories.expense, categories.income]);

  useEffect(() => {
    if (!showStoreSortMenu) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (storeSortMenuRef.current && !storeSortMenuRef.current.contains(event.target)) {
        setShowStoreSortMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowStoreSortMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showStoreSortMenu]);
  const [longPressProgress, setLongPressProgress] = useState(0);
  const longPressTimer = useRef(null);
  const progressTimer = useRef(null);
  const [showDevModal, setShowDevModal] = useState(false);

  useEffect(() => {
    loadUserSettingsRef.current = loadUserSettings;
  }, [loadUserSettings]);

  useEffect(() => {
    saveUserSettingsRef.current = saveUserSettings;
  }, [saveUserSettings]);

  useEffect(() => {
    let isMounted = true;

    const fetchUserSettings = async () => {
      const defaultFilters = createDefaultFilters();
      const defaultStats = createDefaultStatisticsPreferences();

      if (isMounted) setSettingsLoading(true);

      if (!user) {
        if (isMounted) {
          setActiveFilters(defaultFilters);
          setStatisticsPreferences(defaultStats);
          setSettingsLoading(false);
        }
        return;
      }

      try {
        const settings = await loadUserSettingsRef.current?.();
        if (!isMounted) {
          return;
        }

        setUserSettings(settings || {});
        const normalizedFilters = settings?.filters
          ? normalizeFilters(settings.filters)
          : defaultFilters;
        const normalizedStatistics = settings?.statisticsPreferences
          ? normalizeStatisticsPreferences(settings.statisticsPreferences)
          : defaultStats;

        setActiveFilters(normalizedFilters);
        setStatisticsPreferences(normalizedStatistics);
      } catch (error) {
        console.error('Errore durante il caricamento delle impostazioni salvate:', error);
      } finally {
        if (isMounted) setSettingsLoading(false);
      }
    };

    fetchUserSettings();

    return () => {
      isMounted = false;
    };
  }, [user]);

  // Stati per modal dei componenti figli
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', icon: '📦' });
  const [categoryType, setCategoryType] = useState('expense');
  const [showCategoryFormFromDialog, setShowCategoryFormFromDialog] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isImportingData, setIsImportingData] = useState(false);
  const [importFile, setImportFile] = useState(null);
  // Stato per il dialog di dettaglio transazione
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [expandedStores, setExpandedStores] = useState([]);
  const [easterEggsWithStatus, setEasterEggsWithStatus] = useState([]);

  // Stati per i dialog dei wallet
  const [showWalletActions, setShowWalletActions] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showWalletCheck, setShowWalletCheck] = useState(false);
  const [walletCheckCounts, setWalletCheckCounts] = useState(createEmptyWalletCheckCounts);
  const [walletCheckExtra, setWalletCheckExtra] = useState('');
  const [walletCheckActiveField, setWalletCheckActiveField] = useState(null);
  const [showWalletCheckKeypad, setShowWalletCheckKeypad] = useState(false);
  const [showWalletConfirmDelete, setShowWalletConfirmDelete] = useState(false);
  const [deletingWallet, setDeletingWallet] = useState(false);
  const [isWalletSortingMode, setIsWalletSortingMode] = useState(false);
  const [walletOrderChanged, setWalletOrderChanged] = useState(false);
  const [skipNextWalletsSync, setSkipNextWalletsSync] = useState(false);
  const [pendingWalletOrder, setPendingWalletOrder] = useState(null);

  // Stati per i dialog delle categorie
  const [showCategoryActions, setShowCategoryActions] = useState(false);

  // Stati per debiti e crediti
  const [debts, setDebts] = useState([]);
  const uniquePersons = useMemo(() => getUniquePersons(debts), [debts]);
  const processingPendingExpenses = useRef(new Set());
  const processingPendingIncomes = useRef(new Set());
  const [showDebtForm, setShowDebtForm] = useState(false);
  const [editingDebt, setEditingDebt] = useState(null);
  const [debtTransactionType, setDebtTransactionType] = useState('debt'); // 'debt' or 'credit'
  const [prefilledDebtPerson, setPrefilledDebtPerson] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showPersonDetail, setShowPersonDetail] = useState(false);
  // Stato per il dialog di dettaglio movimento debito
  const [selectedDebtTransaction, setSelectedDebtTransaction] = useState(null);
  const [showDebtTransactionDialog, setShowDebtTransactionDialog] = useState(false);
  // Stato per la conferma eliminazione movimento debito
  const [showDebtConfirmDelete, setShowDebtConfirmDelete] = useState(false);
  const [debtToDelete, setDebtToDelete] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryConfirmDelete, setShowCategoryConfirmDelete] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(false);

  // Stati per il dialog di recupero categorie mancanti
  const [showRecoverCategoriesDialog, setShowRecoverCategoriesDialog] = useState(false);
  const [missingCategories, setMissingCategories] = useState({ expense: [], income: [], wallet: [] });
  const [isRecoveringCategories, setIsRecoveringCategories] = useState(false);
  const [showSalaryWizard, setShowSalaryWizard] = useState(false);
  const [salaryWizardPerson, setSalaryWizardPerson] = useState(null);

  // Stato per la visibilità del saldo
  const [showBalance, setShowBalance] = useState(false); // di default nascosto

  // Stati per il dialog dei modelli
  const [showModelsDialog, setShowModelsDialog] = useState(false);
  const [modelsDialogType, setModelsDialogType] = useState('expense');
  const buttonLongPressTimer = useRef(null);
  const [models, setModels] = useState([]);
  const [showModelForm, setShowModelForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [showModelConfirmDelete, setShowModelConfirmDelete] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [modelFormData, setModelFormData] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState({});
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const transactionsHeaderRef = useRef(null);
  const selectionBarRef = useRef(null);
  const [dateTileOffset, setDateTileOffset] = useState(() => 104);
  const [visibleTransactionsCount, setVisibleTransactionsCount] = useState(TRANSACTIONS_PAGE_SIZE);
  const isTransactionsHeaderCollapsed = !isHeaderExpanded;

  // Determina se almeno un modal è aperto
  const isAnyModalOpen = showForm || showConfirmDelete || showBulkEditDialog || showUserProfile ||
    showWalletForm || showTransferModal || showColorPicker || showCategoryForm ||
    showImportModal || showFilterDialog || showTransactionDialog ||
    showWalletActions || showWalletCheck || showWalletConfirmDelete || showCategoryActions || showCategoryConfirmDelete ||
    showModelsDialog || showModelForm || showModelConfirmDelete || showDebtForm || showPersonDetail ||
    showDebtTransactionDialog || showDebtConfirmDelete || showSalaryWizard || showRecoverCategoriesDialog;

  // Blocca lo scroll quando un modal è aperto
  useScrollLock(isAnyModalOpen);

  const selectedTransactionKeys = useMemo(
    () => new Set(Object.keys(selectedTransactions)),
    [selectedTransactions]
  );
  const selectedTransactionCount = selectedTransactionKeys.size;
  const isSelectionModeActive = selectedTransactionCount > 0;

  const getSelectionKey = (transaction) => `${transaction._type || 'expense'}:${transaction.id}`;
  const createSelectionPayload = (transaction) => ({
    id: transaction.id,
    type: transaction._type || 'expense',
    linkedDebtId: transaction.linkedDebtId || null
  });


  const handleSelectionStart = (transaction) => {
    if (!transaction?.id) {
      return;
    }
    setSelectedTransactions((previous) => {
      const key = getSelectionKey(transaction);
      if (previous[key]) {
        return previous;
      }
      return {
        ...previous,
        [key]: createSelectionPayload(transaction)
      };
    });
  };

  const handleSelectionToggle = (transaction) => {
    if (!transaction?.id) {
      return;
    }
    setSelectedTransactions((previous) => {
      const key = getSelectionKey(transaction);
      if (previous[key]) {
        const { [key]: _removed, ...rest } = previous;
        return rest;
      }
      return {
        ...previous,
        [key]: createSelectionPayload(transaction)
      };
    });
  };

  const clearSelectedTransactions = () => {
    setSelectedTransactions({});
  };

  const getSelectedTransactionsDetails = useCallback(() => {
    const selections = Object.values(selectedTransactions);
    if (selections.length === 0) {
      return [];
    }

    const expenseMap = new Map(expenses.map(item => [item.id, item]));
    const incomeMap = new Map(incomes.map(item => [item.id, item]));

    return selections
      .map(selection => {
        const source = selection.type === 'income'
          ? incomeMap.get(selection.id)
          : expenseMap.get(selection.id);
        if (!source) {
          return null;
        }
        return { ...source, _type: selection.type };
      })
      .filter(Boolean);
  }, [selectedTransactions, expenses, incomes]);

  useEffect(() => {
    if (activeTab !== 'transactions' && selectedTransactionCount > 0) {
      setSelectedTransactions({});
    }
  }, [activeTab, selectedTransactionCount]);

  useEffect(() => {
    if (typeof window === 'undefined' || activeTab !== 'transactions') {
      return undefined;
    }

    const getBaseTopOffset = () => {
      const isDesktop = typeof window.matchMedia === 'function' &&
        window.matchMedia('(min-width: 1024px)').matches;

      if (isDesktop) {
        // Su desktop lo scroll è interno al contenitore.
        return 36;
      }

      const safeAreaTop = getSafeAreaInset('top');
      const primaryHeaderHeight = getPrimaryHeaderHeight(isHeaderExpanded);
      const gap = 12;
      return Math.max(48, primaryHeaderHeight + gap - safeAreaTop);
    };

    const updateOffset = () => {
      const headerHeight = transactionsHeaderRef.current?.offsetHeight ?? 0;
      const selectionHeight = selectionBarRef.current?.offsetHeight ?? 0;
      const spacing = selectionHeight > 0 ? 32 : 20;
      const computedOffset = getBaseTopOffset() + headerHeight + selectionHeight + spacing;
      const newValue = Math.max(48, computedOffset + DATE_TILE_EXTRA_OFFSET);
      setDateTileOffset(prev => (Math.abs(prev - newValue) < 1 ? prev : newValue));
    };

    const handleResize = () => {
      updateOffset();
    };

    updateOffset();
    const initialTimeout = setTimeout(updateOffset, 100);
    const initialTimeout2 = setTimeout(updateOffset, 500); // Secondo tentativo per sicurezza
    const initialTimeout3 = setTimeout(updateOffset, 1000); // Terzo tentativo per layout pesanti
    const detachViewportListeners = addViewportResizeListener(handleResize);

    let resizeObserver;
    if ('ResizeObserver' in window) {
      resizeObserver = new window.ResizeObserver(updateOffset);
      if (transactionsHeaderRef.current) {
        resizeObserver.observe(transactionsHeaderRef.current);
      }
      if (selectionBarRef.current) {
        resizeObserver.observe(selectionBarRef.current);
      }
    }
    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(initialTimeout2);
      clearTimeout(initialTimeout3);
      detachViewportListeners();
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [activeTab, isHeaderExpanded, isSelectionModeActive, selectedTransactionCount]);

  useEffect(() => {
    if (!showImportModal) {
      setImportFile(null);
    }
  }, [showImportModal]);

  // Event listener per la modifica delle transazioni future
  useEffect(() => {
    const handleEditFutureTransaction = (event) => {
      const { transaction } = event.detail;
      // Apri il form di modifica con la transazione selezionata
      setEditingItem(transaction);
      setTransactionType(transaction._type || 'expense');
      setShowForm(true);
    };

    window.addEventListener('editFutureTransaction', handleEditFutureTransaction);

    return () => {
      window.removeEventListener('editFutureTransaction', handleEditFutureTransaction);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    if (!Array.isArray(recurringTransactions) || recurringTransactions.length === 0) {
      return undefined;
    }

    let isMounted = true;
    let processing = false;

    const handleStoresUpdate = (updatedStores) => {
      if (!isMounted) {
        return;
      }
      if (Array.isArray(updatedStores)) {
        setStores(updatedStores);
      }
    };

    const runProcessing = async () => {
      const now = Date.now();
      // Throttle: processa al massimo una volta ogni 5 secondi per evitare loop infiniti se le dipendenze cambiano rapidamente
      if (processing || !isMounted || (now - lastRunRef.current < 5000)) {
        console.log('[DEBUG] Skipping recurring processing (throttled or busy)');
        return;
      }

      processing = true;
      lastRunRef.current = now;
      try {
        const result = await processRecurringTransactions(recurringTransactions, {
          onStoresUpdate: handleStoresUpdate
        });

        if (isMounted && result.totalGenerated > 0) {
          const message = result.totalGenerated === 1
            ? 'È stata generata 1 transazione ripetuta.'
            : `Sono state generate ${result.totalGenerated} transazioni ripetute.`;

          showAlert(message, 'Transazioni ripetute create');
        }
      } catch (error) {
        console.error('Errore durante l\'elaborazione delle transazioni ripetute:', error);
      } finally {
        processing = false;
      }
    };

    runProcessing();
    const intervalId = setInterval(runProcessing, 60 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [user, recurringTransactions, processRecurringTransactions, showAlert, setStores]);

  const getViewportHeight = () => {
    if (typeof window === 'undefined') {
      return 0;
    }
    return window.visualViewport?.height ?? window.innerHeight;
  };

  // Utility: controlla se la pagina è scrollabile verticalmente
  const checkIfScrollable = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }

    // Su desktop/tablet con layout orizzontale, controlla solo la zona destra
    if (window.innerWidth >= 1024) { // lg breakpoint
      const rightPanel = document.querySelector('.lg\\:flex-1');
      if (rightPanel) {
        return rightPanel.scrollHeight > rightPanel.clientHeight + 2;
      }
    }
    // Su mobile, controlla l'intera pagina
    const doc = document.documentElement;
    const viewportHeight = getViewportHeight();
    return doc.scrollHeight > viewportHeight + 2;
  };

  // Effetto: gestione header e menu per mobile
  useEffect(() => {
    // Su tablet/desktop (layout orizzontale), mantieni sempre header compatto e menu visibile
    if (window.innerWidth >= 1024) {
      setIsHeaderExpanded(false);
      setShowFloatingMenu(true);
    } else {
      // Su mobile, usa la logica originale
      if (!checkIfScrollable() && !isAnyModalOpen) {
        setIsHeaderExpanded(true);
        setShowFloatingMenu(true);
      }
      // Se invece la pagina diventa scrollabile, lascia che la logica di scroll faccia il suo lavoro
    }
  }, [expenses, incomes, activeTab, activeFilters, isAnyModalOpen]);

  // Inizializza i filtri con tutti i wallets selezionati quando i wallets cambiano
  useEffect(() => {
    const virtualWalletIds = VIRTUAL_DEBT_WALLETS.map(wallet => wallet.id);
    const walletIds = Array.from(new Set([
      ...wallets.map(wallet => wallet.id),
      ...virtualWalletIds
    ]));

    const availableIds = new Set(walletIds);

    setActiveFilters(prev => {
      const currentSelection = Array.isArray(prev.selectedWallets) ? prev.selectedWallets : [];
      const filteredSelection = currentSelection.filter(walletId => availableIds.has(walletId));

      const allWalletsSelected = walletIds.length > 0
        && filteredSelection.length === walletIds.length
        && walletIds.every(id => filteredSelection.includes(id));

      if (allWalletsSelected && prev.selectedWallets.length !== 0) {
        return {
          ...prev,
          selectedWallets: []
        };
      }

      if (filteredSelection.length !== currentSelection.length) {
        return {
          ...prev,
          selectedWallets: filteredSelection
        };
      }

      return prev;
    });
  }, [wallets]);

  // 1. Tap segreto portafoglio — contatore via ref per evitare setState dentro setState
  const isRainbowToggling = useRef(false);
  const handleWalletTap = () => {
    const now = Date.now();
    if (now - lastTapTime.current > 2000) {
      walletTapCount.current = 1;
      lastTapTime.current = now;
      return;
    }
    walletTapCount.current += 1;
    lastTapTime.current = now;
    if (walletTapCount.current >= 5) {
      walletTapCount.current = 0;
      if (!isRainbowToggling.current) {
        isRainbowToggling.current = true;
        activateExclusiveMode('rainbow');
        setTimeout(() => { isRainbowToggling.current = false; }, 500);
      }
    }
  };

  // 2. Doppio tap/click footer custom — contatore via ref
  const footerTapCount = useRef(0);
  const footerTapTimer = useRef(null);
  const isRetroToggling = useRef(false);
  const handleFooterTap = () => {
    if (footerTapCount.current === 0) {
      footerTapCount.current = 1;
      if (footerTapTimer.current) clearTimeout(footerTapTimer.current);
      footerTapTimer.current = setTimeout(() => { footerTapCount.current = 0; }, 400);
    } else {
      footerTapCount.current = 0;
      if (footerTapTimer.current) clearTimeout(footerTapTimer.current);
      if (!isRetroToggling.current) {
        isRetroToggling.current = true;
        activateExclusiveMode('retro');
        setTimeout(() => { isRetroToggling.current = false; }, 500);
      }
    }
  };

  // 3. Long press titolo robusto
  const [isLongPressing, setIsLongPressing] = useState(false);
  const handleLongPressStart = (event) => {
    if (event.target.closest('.app-title')) {
      setIsLongPressing(true);
      setLongPressProgress(0);
      progressTimer.current = setInterval(() => {
        setLongPressProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressTimer.current);
            return 100;
          }
          return prev + 3.33;
        });
      }, 100);
      longPressTimer.current = setTimeout(() => {
        activateExclusiveMode('party');
        setIsLongPressing(false);
        setLongPressProgress(0);
      }, 3000);
    }
  };
  const handleLongPressEnd = () => {
    setIsLongPressing(false);
    setLongPressProgress(0);
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    if (progressTimer.current) clearInterval(progressTimer.current);
  };

  // Funzione per aggiornare il colore della Dynamic Island e barra di stato
  const updateThemeColor = (isRainbow, currentTheme) => {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) return;

    let newColor;
    if (isRainbow && currentTheme === 'dark') {
      // Tema scuro + arcobaleno
      newColor = '#271925'; // Colore personalizzato per il tema arcobaleno scuro
    } else if (isRainbow && currentTheme === 'light') {
      // Tema chiaro + arcobaleno
      newColor = '#FEEDD2'; // Colore personalizzato per il tema arcobaleno chiaro
    } else if (currentTheme === 'dark') {
      // Tema scuro normale
      newColor = '#111828'; // Colore scuro originale
    } else {
      // Tema chiaro normale
      newColor = '#FFFFFF'; // Colore bianco per il tema light
    }

    themeColorMeta.setAttribute('content', newColor);
  };

  // Sincronizza i dati Firebase con gli stati locali
  useEffect(() => {
    const loadFromBackup = () => {
      try {
        const backup = localStorage.getItem('categories_backup');
        if (backup) {
          return normalizeCategoriesData(JSON.parse(backup));
        }
      } catch (e) {
        console.error('Errore nel caricamento del backup:', e);
      }
      return defaultCategories;
    };

    const applyCategoriesIfChanged = (nextCategories) => {
      try {
        const snapshot = JSON.stringify(nextCategories);
        if (snapshot === categoriesSnapshotRef.current) {
          return;
        }
        categoriesSnapshotRef.current = snapshot;
        setCategories(nextCategories);
      } catch (error) {
        console.error('Errore durante il confronto categorie:', error);
        setCategories(nextCategories);
      }
    };

    if (categoriesData && categoriesData.length > 0) {
      const categoriesDoc = categoriesData[0];
      const normalizedCategories = normalizeCategoriesData(categoriesDoc);

      // Deep equality check to prevent infinite loop
      const currentCategoriesStr = JSON.stringify(categories);
      const newCategoriesStr = JSON.stringify(normalizedCategories);

      if (currentCategoriesStr !== newCategoriesStr) {
        setCategories(normalizedCategories);
        setLastSyncTime(new Date().toISOString());
        try {
          localStorage.setItem('categories_backup', JSON.stringify(normalizedCategories));
        } catch (e) {
          console.warn('LocalStorage quota exceeded or unavailable', e);
        }

        // Aggiorna Firestore per aggiungere le nuove categorie conti se mancanti (Safe check)
        if (!Array.isArray(categoriesDoc.wallet) && categoriesDoc.id) {
          // Ensure we don't loop here. This update should happen once. 
          // But if it triggers a snapshot update, we rely on the equality check above to stop state update.
          updateDocument('categories', categoriesDoc.id, normalizedCategories).catch((e) => {
            console.error('Errore durante l\'aggiornamento delle categorie con le tipologie di conto:', e);
          });
        }
      }
      return;
    }

    if (categoriesData && categoriesData.length === 0) {
      // Logic for empty data (load from backup or default)
      const backup = loadFromBackup();
      const target = backup || defaultCategories;
      const currentStr = JSON.stringify(categories);
      const targetStr = JSON.stringify(target);

      if (currentStr !== targetStr) {
        setCategories(target);
        // Don't update LastSyncTime for local default to avoid noise? Or do it.
        // setLastSyncTime(new Date().toISOString()); 
      }
      return;
    }
  }, [categoriesData, updateDocument]);

  useEffect(() => {
    try {
      categoriesSnapshotRef.current = JSON.stringify(categories);
    } catch (e) {
      // Ignora errori di serializzazione
    }
  }, [categories]);

  useEffect(() => {
    let storesDoc = null;
    if (storesData && storesData.length > 0) {
      // Cerca il documento con id === 'default'
      storesDoc = storesData.find(doc => doc.id === 'default') || storesData[0];
      if (storesDoc.stores) {
        // Prevent infinite loop: check if data changed
        if (JSON.stringify(stores) !== JSON.stringify(storesDoc.stores)) {
          setStores(storesDoc.stores);
          setLastSyncTime(new Date().toISOString());
        }
      } else {
        if (stores.length > 0) setStores([]);
      }
    } else if (storesData && storesData.length === 0) {
      if (stores.length > 0) setStores([]);
    } else {
      // setStores([]); // Avoid clearing repeatedly if already empty
    }
  }, [storesData]);

  const sortWalletsForDisplay = (items) => {
    if (!Array.isArray(items)) {
      return [];
    }
    const parseTimestamp = (value) => {
      if (!value) return Number.MAX_SAFE_INTEGER;
      const parsed = new Date(value).getTime();
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
    };
    return [...items].sort((a, b) => {
      const aHasOrder = Number.isFinite(a?.order);
      const bHasOrder = Number.isFinite(b?.order);
      if (aHasOrder && bHasOrder) return a.order - b.order;
      if (aHasOrder) return -1;
      if (bHasOrder) return 1;
      return parseTimestamp(a?.createdAt) - parseTimestamp(b?.createdAt);
    }).map(wallet => ({
      ...wallet,
      isFavorite: Boolean(wallet?.isFavorite)
    }));
  };

  useEffect(() => {
    if (isWalletSortingMode) {
      return;
    }

    if (skipNextWalletsSync) {
      setSkipNextWalletsSync(false);
      return;
    }

    // LOGGING ADDED
    // If we have a pending order update, check if incoming data matches expectation
    if (pendingWalletOrder) {
      const sortedIncoming = sortWalletsForDisplay(walletsData || []);
      const incomingOrderHash = JSON.stringify(sortedIncoming.map(w => w.id));

      if (incomingOrderHash !== pendingWalletOrder) {
        // Incoming data is still stale (does not match our local optimistic order).
        // Ignore it to prevent premature overwriting.
        // We do NOT clear pendingWalletOrder here, we wait.
        return;
      }
      // If matches (or we decide it's safe), clear pending
      setPendingWalletOrder(null);
    }

    if (walletsData && walletsData.length > 0) {
      const sortedWallets = sortWalletsForDisplay(walletsData).map(wallet => ({
        ...wallet,
        type: wallet.type || DEFAULT_WALLET_TYPE_NAME
      }));

      // Prevent infinite loop: check if data changed (naive JSON check is cheaper than infinite render)
      const currentWalletsStr = JSON.stringify(wallets);
      const newWalletsStr = JSON.stringify(sortedWallets);

      if (currentWalletsStr !== newWalletsStr) {
        setWallets(sortedWallets);
        setLastSyncTime(new Date().toISOString());
      }
      if (sortedWallets.length > 0 && !activeWalletId) {
        setActiveWalletId(sortedWallets[0].id);
      }
      const walletsWithoutType = walletsData.filter(wallet => !wallet.type);
      // Migration logic removed as per user request (wallets are now correct)
    } else if (walletsData && walletsData.length === 0) {
      setWallets([]);
      setActiveWalletId(null);
    }
  }, [walletsData, activeWalletId, isWalletSortingMode, skipNextWalletsSync, updateDocument]);

  // Sincronizza i modelli dal database
  useEffect(() => {
    if (modelsData && modelsData.length > 0) {
      const modelsDoc = modelsData[0];

      if (modelsDoc.models) {
        setModels(modelsDoc.models);
      } else {
        setModels([]);
      }
      setLastSyncTime(new Date().toISOString());
    } else if (modelsData && modelsData.length === 0) {

      setModels([]);
    }
  }, [modelsData]);

  // Sincronizza i debiti dal database
  useEffect(() => {
    if (debtsData && debtsData.length > 0) {
      setDebts(debtsData);
      setLastSyncTime(new Date().toISOString());
    } else if (debtsData && debtsData.length === 0) {
      setDebts([]);
    }
  }, [debtsData]);

  useEffect(() => {
    const processPendingExpenseDebts = async () => {
      if (!Array.isArray(expenses) || expenses.length === 0) return;

      const cleanupIds = [];
      processingPendingExpenses.current.forEach(id => {
        const tx = expenses.find(expense => expense.id === id);
        if (!tx || !tx.debtPending || isFutureDate(tx.date)) {
          cleanupIds.push(id);
        }
      });
      cleanupIds.forEach(id => processingPendingExpenses.current.delete(id));

      const pendingExpenses = expenses.filter(expense =>
        expense &&
        expense.isDebtTransaction &&
        expense.debtPending &&
        !isFutureDate(expense.date) &&
        !processingPendingExpenses.current.has(expense.id)
      );

      if (pendingExpenses.length === 0) {
        return;
      }

      for (const expense of pendingExpenses) {
        try {
          processingPendingExpenses.current.add(expense.id);
          const debtId = generateId('debt');
          const debtData = buildLinkedDebtData({
            transactionKind: 'expense',
            debtId,
            transactionId: expense.id,
            isoDate: expense.date,
            amount: expense.amount,
            personName: expense.debtPersonName,
            debtType: expense.debtType || 'debt',
            store: expense.store,
            note: expense.note
          });

          await addDocument('debts', debtData);
          await updateDocument('expenses', expense.id, {
            linkedDebtId: debtId,
            debtPending: false
          });
        } catch (error) {
          console.error('Errore durante la creazione del debito per una spesa futura:', error);
          processingPendingExpenses.current.delete(expense.id);
        }
      }
    };

    processPendingExpenseDebts();
  }, [expenses, addDocument, updateDocument]);

  useEffect(() => {
    const processPendingIncomeDebts = async () => {
      if (!Array.isArray(incomes) || incomes.length === 0) return;

      const cleanupIds = [];
      processingPendingIncomes.current.forEach(id => {
        const tx = incomes.find(income => income.id === id);
        if (!tx || !tx.debtPending || isFutureDate(tx.date)) {
          cleanupIds.push(id);
        }
      });
      cleanupIds.forEach(id => processingPendingIncomes.current.delete(id));

      const pendingIncomes = incomes.filter(income =>
        income &&
        income.isDebtTransaction &&
        income.debtPending &&
        !isFutureDate(income.date) &&
        !processingPendingIncomes.current.has(income.id)
      );

      if (pendingIncomes.length === 0) {
        return;
      }

      for (const income of pendingIncomes) {
        try {
          processingPendingIncomes.current.add(income.id);
          const debtId = generateId('debt');
          const debtData = buildLinkedDebtData({
            transactionKind: 'income',
            debtId,
            transactionId: income.id,
            isoDate: income.date,
            amount: income.amount,
            personName: income.debtPersonName,
            debtType: income.debtType || 'credit',
            store: income.store,
            note: income.note
          });

          await addDocument('debts', debtData);
          await updateDocument('incomes', income.id, {
            linkedDebtId: debtId,
            debtPending: false
          });
        } catch (error) {
          console.error('Errore durante la creazione del credito per una entrata futura:', error);
          processingPendingIncomes.current.delete(income.id);
        }
      }
    };

    processPendingIncomeDebts();
  }, [incomes, addDocument, updateDocument]);

  // Inizializza i dati di default se l'utente è nuovo
  useSeedUserDocs({ user, categoriesData, storesData, defaultCategories });




  // MIGRATION: Ensure all wallet categories have a color and fix bad ones (Red/Green)
  useEffect(() => {
    if (!categories.wallet || categories.wallet.length === 0) return;

    const isRestrictedColor = (color) => {
      if (!color) return true; // Missing is restricted/bad
      const match = color.match(/hsl\((\d+)/);
      if (match) {
        const hue = parseInt(match[1], 10);
        // Check Red (340-360, 0-20) and Green (80-160)
        if ((hue >= 340 || hue <= 20) || (hue >= 80 && hue <= 160)) {
          return true;
        }
      }
      return false;
    };

    const needsMigration = categories.wallet.some(cat => isRestrictedColor(cat.color));

    if (needsMigration) {
      console.log('Migrating wallet categories colors (fixing prohibited hues)...');
      const updatedWalletCategories = categories.wallet.map(cat => {
        if (isRestrictedColor(cat.color)) {
          return { ...cat, color: getRandomVibrantColor() };
        }
        return cat;
      });

      const newCategories = { ...categories, wallet: updatedWalletCategories };
      setCategories(newCategories);

      // Force sync to Firestore
      if (categoriesData && categoriesData.length > 0) {
        const doc = categoriesData[0];
        updateDocument('categories', doc.documentId || doc.id, newCategories)
          .then(() => console.log('Categories color migration (fix bad hues) saved.'))
          .catch(e => console.error('Categories color migration failed', e));
      }
    }
  }, [categories.wallet, categoriesData]); // Depend on wallet array specifically

  // Aggiorna il colore della Dynamic Island quando cambia il tema
  useEffect(() => {
    updateThemeColor(rainbowMode, theme);
  }, [rainbowMode, theme]);

  // Aggiorna lo stato della sezione liquidità quando cambia la dimensione dello schermo
  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') {
        return;
      }
      const isMobile = window.innerWidth < 1024; // lg breakpoint
      const nextCategory = isMobile ? 'mobile' : 'desktop';
      if (balanceViewportCategoryRef.current !== nextCategory) {
        balanceViewportCategoryRef.current = nextCategory;
        setBalanceCollapsed(isMobile);
      }

      // Aggiorna anche header e menu fluttuante
      if (window.innerWidth >= 1024) {
        setIsHeaderExpanded(false);
        setShowFloatingMenu(true);
      }
    };

    handleResize();
    const detachViewportListeners = addViewportResizeListener(handleResize);
    return () => detachViewportListeners();
  }, []);

  // Gestione classe party-mode sul body
  useEffect(() => {
    const body = document.body;
    if (partyMode) {
      body.classList.add('party-mode');
    } else {
      body.classList.remove('party-mode');
    }
  }, [partyMode]);

  // Gestione classe retro-mode sul body
  useEffect(() => {
    const body = document.body;
    if (retroMode) {
      body.classList.add('retro-mode');
    } else {
      body.classList.remove('retro-mode');
    }
  }, [retroMode]);



  // Aggiorna il colore della Dynamic Island all'avvio dell'app
  useEffect(() => {
    updateThemeColor(rainbowMode, theme);
  }, []);

  const favoriteWallet = useMemo(
    () => wallets.find(wallet => wallet.isFavorite),
    [wallets]
  );
  const preferredWalletId = favoriteWallet?.id || activeWalletId || (wallets[0]?.id ?? '');

  // Calcola il saldo di un conto dinamicamente
  const calculateWalletBalance = (walletId) => {
    const wallet = wallets.find(w => w.id === walletId);
    if (!wallet) return 0;

    // Filtra le transazioni future per non includerle nel calcolo del saldo
    const currentIncomes = incomes.filter(i => i.walletId === walletId && !isFutureDate(i.date));
    const currentExpenses = expenses.filter(e => e.walletId === walletId && !isFutureDate(e.date));

    const totalIn = currentIncomes.reduce((sum, i) => sum + parseFloat(i.amount), 0);
    const totalOut = currentExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);

    // Calcola l'impatto dei movimenti di debito/credito
    const walletDebts = debts.filter(d => d.walletId === walletId);
    const totalDebtImpact = walletDebts.reduce((sum, debt) => {
      // Se è un credito (ho dato soldi), il conto diminuisce
      // Se è un debito (ho ricevuto soldi), il conto aumenta
      return sum + (debt.type === 'credit' ? -debt.amount : debt.amount);
    }, 0);

    // Usa il saldo iniziale se disponibile, altrimenti il saldo corrente
    const baseBalance = wallet.initialBalance !== undefined ? wallet.initialBalance : wallet.balance;
    return baseBalance + totalIn - totalOut + totalDebtImpact;
  };

  // Calcola i saldi di tutti i conti dinamicamente (memoizzato per evitare ricalcoli inutili)
  const walletsWithCalculatedBalance = useMemo(() => wallets.map(wallet => ({
    ...wallet,
    balance: calculateWalletBalance(wallet.id)
  })), [wallets, expenses, incomes, debts]);

  const handleExportStats = useCallback(async () => {
    try {
      return generateFinancialSummaryText({
        expenses,
        incomes,
        wallets: walletsWithCalculatedBalance,
        debts
      });
    } catch (error) {
      console.error('Errore durante la generazione delle statistiche:', error);
      throw error;
    }
  }, [expenses, incomes, walletsWithCalculatedBalance, debts]);

  const walletsForDisplay = useMemo(() => {
    const virtualWallets = VIRTUAL_DEBT_WALLETS.map(wallet => ({
      ...wallet,
      balance: 0
    }));
    return [...walletsWithCalculatedBalance, ...virtualWallets];
  }, [walletsWithCalculatedBalance]);

  const walletCheckGridItems = useMemo(() => ([
    ...BANKNOTE_DENOMINATIONS.map((value) => ({ type: 'note', value })),
    { type: 'extra', value: 'extra' }
  ]), []);

  const walletCheckWallet = useMemo(() => {
    if (!selectedWallet) {
      return null;
    }
    return walletsWithCalculatedBalance.find(wallet => wallet.id === selectedWallet.id) || selectedWallet;
  }, [selectedWallet, walletsWithCalculatedBalance]);

  const walletCheckNotesTotal = useMemo(() => BANKNOTE_DENOMINATIONS.reduce((sum, denomination) => {
    const count = Number.parseInt(walletCheckCounts[denomination], 10);
    return sum + denomination * (Number.isFinite(count) ? count : 0);
  }, 0), [walletCheckCounts]);

  const walletCheckExtraAmount = useMemo(
    () => moneyDigitsToNumber(walletCheckExtra),
    [walletCheckExtra]
  );

  const walletCheckTotal = useMemo(
    () => Math.round((walletCheckNotesTotal + walletCheckExtraAmount) * 100) / 100,
    [walletCheckExtraAmount, walletCheckNotesTotal]
  );

  const walletCheckDifference = useMemo(() => {
    const targetBalance = walletCheckWallet?.balance ?? 0;
    return Math.round((walletCheckTotal - targetBalance) * 100) / 100;
  }, [walletCheckTotal, walletCheckWallet]);

  const walletCheckIsMatching = Math.abs(walletCheckDifference) < 0.005;

  const walletCheckStatus = useMemo(() => {
    if (!walletCheckWallet) {
      return {
        tone: 'neutral',
        title: 'Controllo saldo'
      };
    }

    if (walletCheckIsMatching) {
      return {
        tone: 'success',
        title: 'Saldo allineato'
      };
    }

    return {
      tone: 'missing',
      title: 'Scostamento'
    };
  }, [walletCheckIsMatching, walletCheckWallet]);

  const walletCheckToneClasses = {
    success: {
      container: 'bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700',
      icon: 'bg-green-100 text-green-700 dark:bg-green-800/80 dark:text-green-100',
      accent: 'text-green-700 dark:text-green-200'
    },
    extra: {
      container: 'bg-amber-50 border-amber-200 dark:bg-amber-900/30 dark:border-amber-700',
      icon: 'bg-amber-100 text-amber-700 dark:bg-amber-800/80 dark:text-amber-100',
      accent: 'text-amber-700 dark:text-amber-200'
    },
    missing: {
      container: 'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-700',
      icon: 'bg-red-100 text-red-700 dark:bg-red-800/80 dark:text-red-100',
      accent: 'text-red-700 dark:text-red-200'
    },
    neutral: {
      container: 'bg-gray-50 border-gray-200 dark:bg-gray-800/60 dark:border-gray-700',
      icon: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200',
      accent: 'text-gray-700 dark:text-gray-200'
    }
  };

  const walletCheckTone = walletCheckToneClasses[walletCheckStatus.tone] || walletCheckToneClasses.neutral;
  const walletCheckKeypadValue = walletCheckActiveField === 'extra'
    ? walletCheckExtra
    : walletCheckActiveField
      ? walletCheckCounts[walletCheckActiveField] || ''
      : '';
  const walletCheckDifferenceLabel = `${walletCheckDifference > 0 ? '+' : ''}${formatCurrency(walletCheckDifference)}`;

  const persistWalletOrder = async (orderedWallets) => {
    const walletsWithOrder = orderedWallets.map((wallet, index) => ({
      ...wallet,
      order: index
    }));

    setWallets(walletsWithOrder);
    // Create a hash of the expected order (list of IDs) to ignore stale updates
    setPendingWalletOrder(JSON.stringify(walletsWithOrder.map(w => w.id)));

    const updates = walletsWithOrder
      .filter((wallet, index) => wallet.id && wallet.order !== (Number.isFinite(orderedWallets[index]?.order) ? orderedWallets[index].order : null))
      .map(wallet => {
        // Prioritize documentId (Firestore key) over id
        let documentId = wallet.documentId;

        // Fallback: search in remote data if local wallet object is missing documentId
        if (!documentId) {
          const remoteWallet = walletsData.find(remote => remote.id === wallet.id || remote.documentId === wallet.id);
          documentId = remoteWallet?.documentId || remoteWallet?.id;
        }

        if (!documentId) {
          console.warn('[persistWalletOrder] Skipping wallet without ID:', wallet);
          return null; // Skip invalid
        }

        console.log(`[persistWalletOrder] Updating order for ${wallet.name} (${documentId}) to ${wallet.order}`);
        return updateDocument('wallets', documentId, { order: wallet.order });
      })
      .filter(Boolean); // Remove nulls

    if (updates.length > 0) {
      await Promise.all(updates);
    }
  };

  const exitWalletSortingMode = async () => {
    if (!isWalletSortingMode) {
      return true;
    }

    if (walletOrderChanged) {
      try {
        await persistWalletOrder(wallets);
        setWalletOrderChanged(false);
        setSkipNextWalletsSync(true);
      } catch (error) {
        console.error('Errore nel salvataggio dell\'ordine dei conti:', error);
        showError('Impossibile salvare l\'ordine dei conti. Riprova.');
        return false;
      }
    }

    setIsWalletSortingMode(false);
    return true;
  };

  const handleToggleWalletSorting = async () => {
    if (isWalletSortingMode) {
      const success = await exitWalletSortingMode();
      if (!success) {
        return;
      }
    } else {
      setWalletOrderChanged(false);
      setIsWalletSortingMode(true);
    }
  };

  const handleToggleBalanceCollapsed = async () => {
    if (!balanceCollapsed) {
      const success = await exitWalletSortingMode();
      if (!success) {
        return;
      }
    }
    setBalanceCollapsed(prev => !prev);
  };

  const BALANCE_TOGGLE_DRAG_THRESHOLD = 8;

  const handleBalanceHeaderPointerDown = (event) => {
    const target = event.target;
    if (target && target.closest && target.closest('button')) {
      balanceHeaderGestureRef.current = { x: 0, y: 0, allowToggle: false };
      return;
    }
    balanceHeaderGestureRef.current = {
      x: event.clientX,
      y: event.clientY,
      allowToggle: true
    };
  };

  const handleBalanceHeaderPointerUp = (event) => {
    const gesture = balanceHeaderGestureRef.current;
    if (!gesture.allowToggle) {
      return;
    }
    const deltaX = Math.abs(event.clientX - gesture.x);
    const deltaY = Math.abs(event.clientY - gesture.y);
    if (deltaX <= BALANCE_TOGGLE_DRAG_THRESHOLD && deltaY <= BALANCE_TOGGLE_DRAG_THRESHOLD) {
      handleToggleBalanceCollapsed();
    }
    balanceHeaderGestureRef.current.allowToggle = false;
  };

  const handleBalanceHeaderPointerLeave = () => {
    balanceHeaderGestureRef.current.allowToggle = false;
  };

  const handleMoveWallet = (walletId, direction) => {
    // Correct logic: Calculate move based on current 'wallets' state
    const index = wallets.findIndex(wallet => wallet.id === walletId);
    if (index === -1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= wallets.length) return;

    // Apply change
    const reordered = [...wallets];
    const [wallet] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, wallet);

    setWallets(reordered);
    setWalletOrderChanged(true); // Signal change immediately
  };

  const getWalletDocumentId = (wallet) => {
    if (!wallet) {
      return null;
    }
    return wallet.documentId || wallet.id || null;
  };

  const handleToggleFavoriteWallet = async (walletId) => {
    const targetWallet = wallets.find(wallet => wallet.id === walletId);
    if (!targetWallet) {
      showError('Conto non trovato. Riprova.');
      return;
    }

    const previousWallets = wallets;
    const willBeFavorite = !targetWallet.isFavorite;
    const updatedWallets = willBeFavorite
      ? previousWallets.map(wallet => ({
        ...wallet,
        isFavorite: wallet.id === walletId
      }))
      : previousWallets.map(wallet =>
        wallet.id === walletId ? { ...wallet, isFavorite: false } : wallet
      );

    setWallets(updatedWallets);

    try {
      const updates = updatedWallets.reduce((acc, wallet) => {
        const prevWallet = previousWallets.find(item => item.id === wallet.id);
        const prevFavorite = Boolean(prevWallet?.isFavorite);
        const nextFavorite = Boolean(wallet.isFavorite);
        if (prevFavorite !== nextFavorite) {
          let documentId = getWalletDocumentId(wallet);
          if (!documentId) {
            const remoteWallet = walletsData.find(remote => remote.id === wallet.id || remote.documentId === wallet.id);
            documentId = getWalletDocumentId(remoteWallet);
          }
          if (!documentId) {
            console.warn('Impossibile determinare l\'ID del documento per il wallet', wallet);
            throw new Error('ID documento wallet non trovato');
          }
          acc.push(updateDocument('wallets', documentId, { isFavorite: nextFavorite }));
        }
        return acc;
      }, []);

      if (updates.length > 0) {
        await Promise.all(updates);
      }
    } catch (error) {
      console.error('Errore durante l\'aggiornamento del conto preferito:', error);
      showError('Impossibile aggiornare il conto preferito. Riprova.');
      setWallets([...previousWallets]);
    }
  };

  const getDebtDateTime = (isoString) => {
    const now = getCurrentRomeTime();
    let baseDate = isoString ? new Date(isoString) : new Date(now);

    if (Number.isNaN(baseDate.getTime())) {
      baseDate = new Date(now);
    }

    if (baseDate > now) {
      baseDate = new Date(now);
    }

    const debtDate = baseDate.toISOString().split('T')[0];
    const timeString = baseDate.toTimeString();
    const debtTime = timeString ? timeString.slice(0, 5) : '00:00';

    return { debtDate, debtTime };
  };

  const buildLinkedDebtData = ({
    transactionKind,
    debtId,
    transactionId,
    isoDate,
    amount,
    personName,
    debtType,
    store,
    note
  }) => {
    const { debtDate, debtTime } = getDebtDateTime(isoDate);
    const descriptionParts = [];

    if (store) {
      descriptionParts.push(store);
    }
    if (note) {
      descriptionParts.push(note);
    }

    const description = descriptionParts.join(' - ') ||
      (transactionKind === 'income' ? 'Collegato a entrata' : 'Collegato a spesa');

    return {
      id: debtId,
      personName: personName.trim(),
      walletId: debtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID,
      amount: Number.parseFloat(amount),
      description,
      date: debtDate,
      time: debtTime,
      type: debtType === 'credit' ? 'credit' : 'debt',
      linkedTransactionId: transactionId,
      sourceTransactionType: transactionKind,
      createdFromTransaction: true,
      _type: 'debt'
    };
  };

  // Aggiungi spesa/entrata associata a conto
  const addExpense = async (expense) => {
    const transactionId = generateId('expense');
    const isDebtTransaction = Boolean(expense.isDebtTransaction);
    const isoDate = expense.date ? new Date(expense.date).toISOString() : new Date().toISOString();
    const isFutureTransactionFlag = Boolean(expense.isFutureTransaction);
    const resolvedDebtType = isDebtTransaction ? (expense.debtType || 'debt') : null;
    let debtId = null;

    const newExpense = {
      ...expense,
      id: transactionId,
      date: isoDate,
      isDebtTransaction,
      isFutureTransaction: isFutureTransactionFlag
    };

    const shouldCreateDebtNow = isDebtTransaction && !isFutureTransactionFlag;

    if (isDebtTransaction) {
      newExpense.debtPersonName = expense.debtPersonName?.trim() || '';
      newExpense.debtType = resolvedDebtType;
      newExpense.walletId = resolvedDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID;
      if (shouldCreateDebtNow) {
        debtId = generateId('debt');
        newExpense.linkedDebtId = debtId;
        newExpense.debtPending = false;
      } else {
        newExpense.linkedDebtId = null;
        newExpense.debtPending = true;
      }
    } else {
      delete newExpense.debtPersonName;
      delete newExpense.debtType;
      newExpense.linkedDebtId = null;
      newExpense.debtPending = false;
    }

    // Controlla se la transazione attiva un easter egg (solo per salvare il completamento)
    const transactionDate = new Date(isoDate);
    const isTimeTravelDate = transactionDate.getFullYear() === 1999 &&
      transactionDate.getMonth() === 11 &&
      transactionDate.getDate() === 31;
    const isNataleDate = transactionDate.getMonth() === 11 &&
      transactionDate.getDate() === 25;
    const isCompleannoDate = transactionDate.getMonth() === 5 &&
      transactionDate.getDate() === 5;

    let activatedEgg = null;
    if (isTimeTravelDate) {
      activatedEgg = 'timeTravel';
    } else if (isNataleDate) {
      activatedEgg = 'nataleMagico';
    } else if (isCompleannoDate) {
      activatedEgg = 'compleannoSpeciale';
    } else if (parseFloat(expense.amount) === 888) {
      activatedEgg = 'entrataAngelica';
    } else if (parseFloat(expense.amount) === 666) {
      activatedEgg = 'uscitaDiabolica';
    } else if (parseFloat(expense.amount) === 777) {
      activatedEgg = 'quadrifoglioFortunato';
    }

    // Salva il completamento easter egg se attivato
    if (activatedEgg) {
      const easterEgg = getEasterEgg(activatedEgg);
      if (easterEgg) {
        await saveEasterEggCompletion(activatedEgg, setEasterEggCompleted);
        await new Promise(resolve => setTimeout(resolve, 100));
        await loadEasterEggsWithStatus();
        setTimeout(() => {
          showEasterEgg(easterEgg);
        }, 100);
      }
    }

    try {
      await addDocument('expenses', newExpense, (updatedStores) => {
        if (Array.isArray(updatedStores)) setStores(updatedStores);
      });

      if (isDebtTransaction && shouldCreateDebtNow && debtId) {
        const debtData = buildLinkedDebtData({
          transactionKind: 'expense',
          debtId,
          transactionId,
          isoDate,
          amount: newExpense.amount,
          personName: newExpense.debtPersonName,
          debtType: resolvedDebtType,
          store: newExpense.store,
          note: newExpense.note
        });
        await addDocument('debts', debtData);
      }

      setShowForm(false);
      setEditingItem(null);
      setModelFormData(null);
    } catch (error) {
      console.error('Errore durante l\'aggiunta della spesa:', error);
      showError(error.message || 'Errore durante l\'aggiunta della spesa.');
    }
  };

  const addIncome = async (income) => {
    const transactionId = generateId('income');
    const isDebtTransaction = Boolean(income.isDebtTransaction);
    const isoDate = income.date ? new Date(income.date).toISOString() : new Date().toISOString();
    const isFutureTransactionFlag = Boolean(income.isFutureTransaction);
    const resolvedDebtType = isDebtTransaction ? (income.debtType || 'credit') : null;
    let debtId = null;

    const newIncome = {
      ...income,
      id: transactionId,
      date: isoDate,
      isDebtTransaction,
      isFutureTransaction: isFutureTransactionFlag
    };

    const shouldCreateDebtNow = isDebtTransaction && !isFutureTransactionFlag;

    if (isDebtTransaction) {
      newIncome.debtPersonName = income.debtPersonName?.trim() || '';
      newIncome.debtType = resolvedDebtType;
      newIncome.walletId = resolvedDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID;
      if (shouldCreateDebtNow) {
        debtId = generateId('debt');
        newIncome.linkedDebtId = debtId;
        newIncome.debtPending = false;
      } else {
        newIncome.linkedDebtId = null;
        newIncome.debtPending = true;
      }
    } else {
      delete newIncome.debtPersonName;
      delete newIncome.debtType;
      newIncome.linkedDebtId = null;
      newIncome.debtPending = false;
    }

    // Controlla se la transazione attiva un easter egg (solo per salvare il completamento)
    const transactionDate = new Date(isoDate);
    const isTimeTravelDate = transactionDate.getFullYear() === 1999 &&
      transactionDate.getMonth() === 11 &&
      transactionDate.getDate() === 31;
    const isNataleDate = transactionDate.getMonth() === 11 &&
      transactionDate.getDate() === 25;
    const isCompleannoDate = transactionDate.getMonth() === 5 &&
      transactionDate.getDate() === 5;

    let activatedEgg = null;
    if (isTimeTravelDate) {
      activatedEgg = 'timeTravel';
    } else if (isNataleDate) {
      activatedEgg = 'nataleMagico';
    } else if (isCompleannoDate) {
      activatedEgg = 'compleannoSpeciale';
    } else if (parseFloat(income.amount) === 888) {
      activatedEgg = 'entrataAngelica';
    } else if (parseFloat(income.amount) === 666) {
      activatedEgg = 'uscitaDiabolica';
    } else if (parseFloat(income.amount) === 777) {
      activatedEgg = 'quadrifoglioFortunato';
    }

    // Salva il completamento easter egg se attivato
    if (activatedEgg) {
      const easterEgg = getEasterEgg(activatedEgg);
      if (easterEgg) {
        await saveEasterEggCompletion(activatedEgg, setEasterEggCompleted);
        await new Promise(resolve => setTimeout(resolve, 100));
        await loadEasterEggsWithStatus();
        setTimeout(() => {
          showEasterEgg(easterEgg);
        }, 100);
      }
    }

    try {
      await addDocument('incomes', newIncome, (updatedStores) => {
        if (Array.isArray(updatedStores)) setStores(updatedStores);
      });

      if (isDebtTransaction && shouldCreateDebtNow && debtId) {
        const debtData = buildLinkedDebtData({
          transactionKind: 'income',
          debtId,
          transactionId,
          isoDate,
          amount: newIncome.amount,
          personName: newIncome.debtPersonName,
          debtType: resolvedDebtType,
          store: newIncome.store,
          note: newIncome.note
        });
        await addDocument('debts', debtData);
      }

      setShowForm(false);
      setEditingItem(null);
      setModelFormData(null);
    } catch (error) {
      console.error('Errore durante l\'aggiunta dell\'entrata:', error);
      showError(error.message || 'Errore durante l\'aggiunta dell\'entrata.');
    }
  };

  // Modifica transazione
  const updateItem = async (updatedItem) => {
    if (!editingItem) return;

    const collectionName = editingItem._type === 'expense' ? 'expenses' : 'incomes';
    const transactionKind = editingItem._type === 'expense' ? 'expense' : 'income';
    const wasDebtTransaction = Boolean(editingItem.linkedDebtId);
    const incomingDebt = Boolean(updatedItem.isDebtTransaction);
    let linkedDebtId = editingItem.linkedDebtId;

    if (incomingDebt && !linkedDebtId) {
      linkedDebtId = generateId('debt');
    }

    const resolvedDebtType = incomingDebt
      ? (updatedItem.debtType || (transactionKind === 'income' ? 'credit' : 'debt'))
      : null;

    const isoDate = updatedItem.date ? new Date(updatedItem.date).toISOString() : new Date().toISOString();

    const payload = {
      ...updatedItem,
      date: isoDate,
      isDebtTransaction: incomingDebt
    };

    const shouldCreateDebtNow = incomingDebt && !payload.isFutureTransaction;

    if (incomingDebt) {
      payload.debtPersonName = updatedItem.debtPersonName?.trim() || '';
      payload.debtType = resolvedDebtType;
      payload.walletId = resolvedDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID;
      payload.debtPending = !shouldCreateDebtNow;
      payload.linkedDebtId = shouldCreateDebtNow ? linkedDebtId : null;
    } else {
      delete payload.debtPersonName;
      delete payload.debtType;
      payload.linkedDebtId = null;
      payload.debtPending = false;
    }

    try {
      await updateDocument(collectionName, editingItem.id, payload);

      if (wasDebtTransaction && (!incomingDebt || !shouldCreateDebtNow) && editingItem.linkedDebtId) {
        await deleteDocument('debts', editingItem.linkedDebtId);
      }

      if (incomingDebt && shouldCreateDebtNow) {
        const debtData = buildLinkedDebtData({
          transactionKind,
          debtId: linkedDebtId,
          transactionId: editingItem.id,
          isoDate,
          amount: payload.amount,
          personName: payload.debtPersonName,
          debtType: resolvedDebtType,
          store: payload.store,
          note: payload.note
        });

        if (wasDebtTransaction && editingItem.linkedDebtId) {
          await updateDocument('debts', editingItem.linkedDebtId, debtData);
        } else {
          await addDocument('debts', debtData);
        }
      }

      setShowForm(false);
      setEditingItem(null);
      setModelFormData(null);
    } catch (error) {
      console.error('Errore durante l\'aggiornamento della transazione:', error);
      showError(error.message || 'Errore durante l\'aggiornamento della transazione.');
    }
  };

  // Elimina transazione (non wallet)
  const handleDelete = async () => {
    if (!itemToDelete) return;

    if (itemToDelete.type === 'bulk-transactions') {
      const itemsToDelete = Array.isArray(itemToDelete.items) ? itemToDelete.items : [];
      if (itemsToDelete.length === 0) {
        setItemToDelete(null);
        setShowConfirmDelete(false);
        return;
      }

      const expenseIds = itemsToDelete
        .filter(item => item.type === 'expense')
        .map(item => item.id);
      const incomeIds = itemsToDelete
        .filter(item => item.type === 'income')
        .map(item => item.id);
      const linkedDebtIds = Array.from(new Set(
        itemsToDelete
          .map(item => item.linkedDebtId)
          .filter(Boolean)
      ));

      try {
        if (expenseIds.length > 0) {
          await deleteMultipleDocuments('expenses', expenseIds);
        }
        if (incomeIds.length > 0) {
          await deleteMultipleDocuments('incomes', incomeIds);
        }
        if (linkedDebtIds.length > 0) {
          await deleteMultipleDocuments('debts', linkedDebtIds);
        }
        clearSelectedTransactions();
        setItemToDelete(null);
        setShowConfirmDelete(false);
        if (itemsToDelete.length === 1) {
          showSuccess('Transazione eliminata con successo.');
        } else {
          showSuccess(`${itemsToDelete.length} transazioni eliminate con successo.`);
        }
      } catch (error) {
        console.error('Errore durante l\'eliminazione delle transazioni selezionate:', error);
        showError(error.message || 'Errore durante l\'eliminazione delle transazioni selezionate.');
      }

      return;
    }

    const collection = itemToDelete._type === 'expense' ? 'expenses' : 'incomes';
    try {
      await deleteDocument(collection, itemToDelete.id);
      if (itemToDelete.linkedDebtId) {
        await deleteDocument('debts', itemToDelete.linkedDebtId);
      }
      setShowConfirmDelete(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Errore durante l\'eliminazione della transazione:', error);
      showError(error.message || 'Errore durante l\'eliminazione della transazione.');
    }
  };
  // Elimina conto e tutte le transazioni collegate
  const deleteWallet = async (walletId) => {
    const wallet = wallets.find(w => w.id === walletId);

    if (!wallet) {
      showError('Wallet non trovato. Riprova.');
      return;
    }

    // Ora l'ID del wallet è direttamente l'ID del documento Firestore

    try {
      await performWalletDeletion(walletId);
    } catch (error) {
      console.error('Errore in deleteWallet:', error);
      showError('Errore durante l\'eliminazione del conto: ' + error.message);
    }
  };

  const performWalletDeletion = async (walletId) => {
    try {
      // Trova il wallet per ottenere l'ID del documento Firestore
      const wallet = wallets.find(w => w.id === walletId);

      if (!wallet) {
        throw new Error('Wallet non trovato per eliminazione');
      }

      // Usa l'ID del documento Firestore per eliminare il wallet
      let documentId = getWalletDocumentId(wallet);
      if (!documentId) {
        const remoteWallet = walletsData.find(remote => remote.id === wallet.id || remote.documentId === wallet.id);
        documentId = getWalletDocumentId(remoteWallet);
      }
      if (!documentId) {
        throw new Error('Impossibile determinare l\'ID del documento del wallet da eliminare');
      }
      await deleteDocument('wallets', documentId);

      // Elimina le transazioni collegate usando l'ID del wallet
      const walletExpenses = expenses.filter(e => e.walletId === walletId);
      const walletIncomes = incomes.filter(i => i.walletId === walletId);

      if (walletExpenses.length > 0) {
        const expenseIds = walletExpenses.map(e => e.id);
        await deleteMultipleDocuments('expenses', expenseIds);
      }

      if (walletIncomes.length > 0) {
        const incomeIds = walletIncomes.map(i => i.id);
        await deleteMultipleDocuments('incomes', incomeIds);
      }

      if (activeWalletId === walletId) {
        const remainingWallets = wallets.filter(w => w.id !== walletId);
        setActiveWalletId(remainingWallets[0]?.id || null);
      }


    } catch (error) {
      console.error('Errore durante l\'eliminazione del wallet:', error);
      console.error('Codice errore:', error.code);
      console.error('Messaggio errore:', error.message);
      throw error; // Rilancia l'errore per gestirlo nel chiamante
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setShowConfirmDelete(true);
  };

  const handleBulkDeleteRequest = () => {
    if (!isSelectionModeActive) {
      return;
    }
    setItemToDelete({
      type: 'bulk-transactions',
      items: Object.values(selectedTransactions)
    });
    setShowConfirmDelete(true);
  };

  const duplicateTransaction = async (transaction) => {
    const transactionKind = transaction._type === 'income' ? 'income' : 'expense';
    const collectionName = transactionKind === 'income' ? 'incomes' : 'expenses';
    const transactionId = generateId(transactionKind);
    const isDebtTransaction = Boolean(
      transaction.isDebtTransaction ||
      transaction.linkedDebtId ||
      transaction.walletId === DEBT_WALLET_ID ||
      transaction.walletId === CREDIT_WALLET_ID
    );
    const isoDate = transaction.date ? new Date(transaction.date).toISOString() : new Date().toISOString();
    const isFutureTransactionFlag = Boolean(transaction.isFutureTransaction);
    const resolvedDebtType = isDebtTransaction
      ? (transaction.debtType || (transactionKind === 'income' ? 'credit' : 'debt'))
      : null;
    let debtId = null;

    const duplicatedTransaction = {
      ...transaction,
      id: transactionId,
      date: isoDate,
      isDebtTransaction,
      isFutureTransaction: isFutureTransactionFlag
    };

    delete duplicatedTransaction._type;
    delete duplicatedTransaction.documentId;
    delete duplicatedTransaction.createdAt;
    delete duplicatedTransaction.updatedAt;

    const shouldCreateDebtNow = isDebtTransaction && !isFutureTransactionFlag;

    if (isDebtTransaction) {
      duplicatedTransaction.debtPersonName = transaction.debtPersonName?.trim() || '';
      duplicatedTransaction.debtType = resolvedDebtType;
      duplicatedTransaction.walletId = resolvedDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID;
      if (shouldCreateDebtNow) {
        debtId = generateId('debt');
        duplicatedTransaction.linkedDebtId = debtId;
        duplicatedTransaction.debtPending = false;
      } else {
        duplicatedTransaction.linkedDebtId = null;
        duplicatedTransaction.debtPending = true;
      }
    } else {
      delete duplicatedTransaction.debtPersonName;
      delete duplicatedTransaction.debtType;
      duplicatedTransaction.linkedDebtId = null;
      duplicatedTransaction.debtPending = false;
    }

    await addDocument(collectionName, duplicatedTransaction);

    if (isDebtTransaction && shouldCreateDebtNow && debtId) {
      const debtData = buildLinkedDebtData({
        transactionKind,
        debtId,
        transactionId,
        isoDate,
        amount: duplicatedTransaction.amount,
        personName: duplicatedTransaction.debtPersonName,
        debtType: resolvedDebtType,
        store: duplicatedTransaction.store,
        note: duplicatedTransaction.note
      });
      await addDocument('debts', debtData);
    }
  };

  const handleBulkDuplicateRequest = () => {
    if (!isSelectionModeActive) {
      return;
    }
    const itemsToDuplicate = getSelectedTransactionsDetails();
    if (itemsToDuplicate.length === 0) {
      return;
    }
    const count = itemsToDuplicate.length;
    const message = count === 1
      ? 'Vuoi duplicare la transazione selezionata?'
      : `Vuoi duplicare ${count} transazioni selezionate?`;

    showConfirm(
      message,
      'Duplica transazioni',
      async () => {
        try {
          for (const transaction of itemsToDuplicate) {
            await duplicateTransaction(transaction);
          }
          clearSelectedTransactions();
          showSuccess(count === 1
            ? 'Transazione duplicata con successo.'
            : `${count} transazioni duplicate con successo.`);
        } catch (error) {
          console.error('Errore durante la duplicazione delle transazioni selezionate:', error);
          showError(error.message || 'Errore durante la duplicazione delle transazioni selezionate.');
        }
      }
    );
  };

  const handleBulkEditRequest = () => {
    if (!isSelectionModeActive) {
      return;
    }
    setShowBulkEditDialog(true);
  };

  const applyBulkEditToTransaction = async (transaction, changes) => {
    const transactionKind = transaction._type === 'income' ? 'income' : 'expense';
    const collectionName = transactionKind === 'income' ? 'incomes' : 'expenses';
    const currentIsDebtTransaction = Boolean(
      transaction.isDebtTransaction ||
      transaction.linkedDebtId ||
      transaction.walletId === DEBT_WALLET_ID ||
      transaction.walletId === CREDIT_WALLET_ID
    );

    const updates = {};

    if (changes.amountEnabled) {
      updates.amount = changes.amountValue;
    }
    if (changes.categoryEnabled) {
      updates.category = changes.categoryValue;
    }
    if (changes.storeEnabled) {
      updates.store = changes.storeValue;
    }
    if (changes.dateEnabled || changes.timeEnabled) {
      const { date: currentDate, time: currentTime } = extractTransactionDateParts(transaction.date);
      const nextDate = changes.dateEnabled ? changes.dateValue : currentDate;
      const nextTime = changes.timeEnabled ? changes.timeValue : currentTime;
      updates.date = `${nextDate}T${nextTime}:00`;
    }

    const incomingDebt = changes.destinationEnabled
      ? changes.destinationType === 'debt'
      : currentIsDebtTransaction;

    const nextItem = {
      ...transaction,
      ...updates,
      isDebtTransaction: incomingDebt
    };

    if (changes.destinationEnabled) {
      if (changes.destinationType === 'wallet') {
        nextItem.walletId = changes.walletId;
        nextItem.debtPersonName = '';
        nextItem.debtType = null;
      } else {
        nextItem.debtPersonName = changes.personName;
        nextItem.debtType = transactionKind === 'income' ? 'credit' : 'debt';
      }
    }

    let linkedDebtId = transaction.linkedDebtId;
    if (incomingDebt && !linkedDebtId) {
      linkedDebtId = generateId('debt');
    }

    const resolvedDebtType = incomingDebt
      ? (nextItem.debtType || (transactionKind === 'income' ? 'credit' : 'debt'))
      : null;

    const isoDate = nextItem.date ? new Date(nextItem.date).toISOString() : new Date().toISOString();

    const payload = {
      ...nextItem,
      date: isoDate,
      isDebtTransaction: incomingDebt
    };

    const shouldCreateDebtNow = incomingDebt && !payload.isFutureTransaction;

    if (incomingDebt) {
      payload.debtPersonName = nextItem.debtPersonName?.trim() || '';
      payload.debtType = resolvedDebtType;
      payload.walletId = resolvedDebtType === 'credit' ? CREDIT_WALLET_ID : DEBT_WALLET_ID;
      payload.debtPending = !shouldCreateDebtNow;
      payload.linkedDebtId = shouldCreateDebtNow ? linkedDebtId : null;
    } else {
      delete payload.debtPersonName;
      delete payload.debtType;
      payload.linkedDebtId = null;
      payload.debtPending = false;
    }

    await updateDocument(collectionName, transaction.id, payload);

    if (transaction.linkedDebtId && (!incomingDebt || !shouldCreateDebtNow)) {
      await deleteDocument('debts', transaction.linkedDebtId);
    }

    if (incomingDebt && shouldCreateDebtNow) {
      const debtData = buildLinkedDebtData({
        transactionKind,
        debtId: linkedDebtId,
        transactionId: transaction.id,
        isoDate,
        amount: payload.amount,
        personName: payload.debtPersonName,
        debtType: resolvedDebtType,
        store: payload.store,
        note: payload.note
      });

      if (transaction.linkedDebtId) {
        await updateDocument('debts', transaction.linkedDebtId, debtData);
      } else {
        await addDocument('debts', debtData);
      }
    }
  };

  const handleBulkEditApply = async (changes) => {
    const itemsToEdit = getSelectedTransactionsDetails();
    if (itemsToEdit.length === 0) {
      setShowBulkEditDialog(false);
      return;
    }

    try {
      if (changes.storeEnabled && changes.storeValue) {
        await addStore(changes.storeValue);
      }

      for (const transaction of itemsToEdit) {
        await applyBulkEditToTransaction(transaction, changes);
      }

      clearSelectedTransactions();
      setShowBulkEditDialog(false);
      showSuccess(itemsToEdit.length === 1
        ? 'Transazione aggiornata con successo.'
        : `${itemsToEdit.length} transazioni aggiornate con successo.`);
    } catch (error) {
      console.error('Errore durante la modifica delle transazioni selezionate:', error);
      showError(error.message || 'Errore durante la modifica delle transazioni selezionate.');
    }
  };

  const isBulkDelete = itemToDelete?.type === 'bulk-transactions';
  const bulkDeleteCount = isBulkDelete
    ? (Array.isArray(itemToDelete.items) ? itemToDelete.items.length : selectedTransactionCount)
    : 0;
  const deleteDialogTitle = itemToDelete?.type === 'wallet'
    ? 'Conferma eliminazione conto'
    : isBulkDelete
      ? (bulkDeleteCount === 1 ? 'Elimina transazione selezionata' : `Elimina ${bulkDeleteCount} transazioni`)
      : 'Conferma eliminazione';
  const deleteDialogMessage = itemToDelete?.type === 'wallet'
    ? `Sei sicuro di voler eliminare il conto "${itemToDelete?.wallet?.name}"? Questa azione eliminerà anche ${itemToDelete?.expenses?.length || 0} spese e ${itemToDelete?.incomes?.length || 0} entrate collegate. Questa azione non può essere annullata.`
    : isBulkDelete
      ? (bulkDeleteCount === 1
        ? 'Sei sicuro di voler eliminare questa transazione selezionata? Questa azione non può essere annullata.'
        : `Sei sicuro di voler eliminare ${bulkDeleteCount} transazioni selezionate? Questa azione non può essere annullata.`)
      : 'Sei sicuro di voler eliminare questa transazione? Questa azione non può essere annullata.';

  const handleEdit = (item) => {
    setEditingItem(item);
    setTransactionType(item._type || inferTypeFromCategory(item.category));
    setShowForm(true);
  };

  // Funzioni per gestire l'apertura del form per spese e entrate
  const handleAddExpense = () => {
    setEditingItem(null);
    setTransactionType('expense');
    setShowForm(true);
  };

  const handleAddIncome = () => {
    setEditingItem(null);
    setTransactionType('income');
    setShowForm(true);
  };

  // Funzioni per gestire il long press sui pulsanti
  const handleButtonLongPressStart = (type) => {
    const timer = setTimeout(() => {
      setModelsDialogType(type);
      setShowModelsDialog(true);
    }, 500); // 500ms per il long press
    buttonLongPressTimer.current = timer;
  };

  const handleButtonLongPressEnd = () => {
    if (buttonLongPressTimer.current) {
      clearTimeout(buttonLongPressTimer.current);
      buttonLongPressTimer.current = null;
    }
  };

  const handleButtonClick = (type) => {
    // Se c'è un timer attivo, significa che è un long press
    if (buttonLongPressTimer.current) {
      clearTimeout(buttonLongPressTimer.current);
      buttonLongPressTimer.current = null;
      return; // Non eseguire il click normale
    }

    // Click normale
    if (type === 'expense') {
      handleAddExpense();
    } else {
      handleAddIncome();
    }
  };

  const handleCloseModelsDialog = () => {
    setShowModelsDialog(false);
  };

  // Funzioni per gestire i modelli
  const handleAddModel = () => {
    setEditingModel(null);
    setShowModelForm(true);
  };

  const handleEditModel = (model) => {
    setEditingModel(model);
    setShowModelForm(true);
  };

  const handleDeleteModel = (model) => {
    setModelToDelete(model);
    setShowModelConfirmDelete(true);
  };

  const confirmDeleteModel = async () => {
    try {
      const updatedModels = deleteModel(models, modelToDelete.id);
      setModels(updatedModels);

      // Sincronizza con Firestore
      if (modelsData && modelsData.length > 0) {
        const modelsDoc = modelsData[0];
        await updateDocument('models', modelsDoc.id, { models: updatedModels });
      }

      setShowModelConfirmDelete(false);
      setModelToDelete(null);
      showSuccess('Modello eliminato con successo!');
    } catch (error) {
      console.error('Errore durante l\'eliminazione del modello:', error);
      showError(error.message || 'Errore durante l\'eliminazione del modello.');
    }
  };

  const closeModelConfirmDelete = () => {
    setShowModelConfirmDelete(false);
    setModelToDelete(null);
  };

  const handleMoveModel = async (model, direction) => {
    try {
      const updatedModels = reorderModels(models, model.id, direction);
      setModels(updatedModels);

      // Sincronizza con Firestore
      if (modelsData && modelsData.length > 0) {
        const modelsDoc = modelsData[0];
        await updateDocument('models', modelsDoc.id, { models: updatedModels });
      }
    } catch (error) {
      console.error('Errore durante il riordino del modello:', error);
      showError('Errore durante il riordino dei modelli');
    }
  };


  const handleSubmitModel = async (modelData) => {
    try {
      let updatedModels;

      // Aggiungi il tipo al modello
      const modelWithType = {
        ...modelData,
        type: modelsDialogType
      };

      if (editingModel) {
        // Modifica modello esistente
        updatedModels = editModel(models, editingModel.id, modelWithType);
      } else {
        // Aggiungi nuovo modello
        updatedModels = addModel(models, modelWithType);
      }


      setModels(updatedModels);

      // Sincronizza con Firestore
      if (modelsData && modelsData.length > 0) {
        const modelsDoc = modelsData[0];
        await updateDocument('models', modelsDoc.id, { models: updatedModels });
      } else {
        await addDocument('models', { models: updatedModels });
      }

      setShowModelForm(false);
      setEditingModel(null);

      // Non mostrare popup di successo per i modelli
    } catch (error) {
      console.error('Errore durante il salvataggio del modello:', error);
      showError(error.message || 'Errore durante il salvataggio del modello.');
    }
  };

  const handleUseModel = (model) => {
    // Prepara i dati del modello per il form
    const modelData = {
      amount: model.amount || 0,
      category: model.category || '',
      store: model.store || '',
      walletId: model.walletId || preferredWalletId,
      note: model.note || '',
      isDebtTransaction: Boolean(model.isDebtTransaction),
      debtPersonName: model.debtPersonName || '',
      debtType: model.debtType || null
    };

    // Imposta il tipo di transazione
    setTransactionType(model.type);

    // Salva i dati del modello per il form (non come editingItem)
    setModelFormData(modelData);

    // Chiudi il dialog dei modelli
    setShowModelsDialog(false);

    // Apri il form di nuova transazione
    setShowForm(true);
  };

  const handleCloseModelForm = () => {
    setShowModelForm(false);
    setEditingModel(null);
  };

  // Scansiona transazioni, transazioni ricorrenti e conti per trovare categorie usate ma non presenti
  const handleRecoverCategories = useCallback(() => {
    const recurring = recurringTransactions || [];
    const names = (list, field = 'category') => (list || []).map(tx => tx[field]);

    setMissingCategories({
      expense: findMissingCategories(categories.expense, [
        ...names(expenses),
        ...names(recurring.filter(tx => tx.type !== 'income'))
      ]),
      income: findMissingCategories(categories.income, [
        ...names(incomes),
        ...names(recurring.filter(tx => tx.type === 'income'))
      ]),
      // campo `type` = tipologia conto
      wallet: findMissingCategories(categories.wallet, names(wallets, 'type'))
    });
    setShowRecoverCategoriesDialog(true);
  }, [categories, expenses, incomes, recurringTransactions, wallets]);

  // Crea le categorie selezionate dall'utente nel dialog di recupero
  const handleConfirmRecoverCategories = async (toCreate) => {
    setIsRecoveringCategories(true);
    try {
      let updatedCategories = { ...categories };

      for (const { type, name, icon } of toCreate) {
        try {
          const newCat = type === 'wallet'
            ? { name, icon, color: getRandomVibrantColor() }
            : { name, icon };
          updatedCategories = {
            ...updatedCategories,
            [type]: addCategory(updatedCategories[type] || [], newCat)
          };
        } catch (e) {
          // Ignora duplicati o errori su singola categoria
          console.warn(`Skip '${name}' (${type}):`, e.message);
        }
      }

      setCategories(updatedCategories);
      localStorage.setItem('categories_backup', JSON.stringify(updatedCategories));

      // Salva su Firestore
      if (categoriesData && categoriesData.length > 0) {
        const categoriesDoc = categoriesData[0];
        await updateDocument('categories', categoriesDoc.documentId || categoriesDoc.id, updatedCategories);
      } else if (!(await ensureDefaultDoc(user.uid, 'categories', updatedCategories))) {
        // lista locale vuota ma server pieno: la copia locale non deve sostituire quella sul server
        throw new Error(CATEGORIES_NOT_LOADED);
      }

      const created = toCreate.length;
      showSuccess(`${created} categori${created === 1 ? 'a creata' : 'e create'} con successo!`, 'Recupero completato');
      setShowRecoverCategoriesDialog(false);
    } catch (error) {
      console.error('Errore durante il recupero delle categorie:', error);
      showError(error.message || 'Errore durante la creazione delle categorie.');
    } finally {
      setIsRecoveringCategories(false);
    }
  };

  // Gestione categorie (usando funzioni pure)
  const handleAddCategory = async (type, newCategory) => {
    // Salva lo stato precedente per il ripristino in caso di errore
    const previousCategories = categories;

    try {
      // Aggiorna lo stato locale
      const updatedCategories = {
        ...categories,
        [type]: addCategory(categories[type] || [], type === 'wallet' ? { ...newCategory, color: getRandomVibrantColor() } : newCategory)
      };
      setCategories(updatedCategories);

      // Salva sempre il backup locale
      localStorage.setItem('categories_backup', JSON.stringify(updatedCategories));

      // Sincronizza con Firestore con retry
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          if (categoriesData && categoriesData.length > 0) {
            const categoriesDoc = categoriesData[0];
            await updateDocument('categories', categoriesDoc.documentId, updatedCategories);
          } else if (!(await ensureDefaultDoc(user.uid, 'categories', updatedCategories))) {
            throw new Error(CATEGORIES_NOT_LOADED);
          }
          break; // Successo, esci dal loop
        } catch (syncError) {
          retryCount++;
          console.warn(`Tentativo ${retryCount} di sincronizzazione fallito:`, syncError);

          if (retryCount >= maxRetries) {
            throw syncError; // Rilancia l'errore se tutti i tentativi sono falliti
          }

          // Aspetta un po' prima di riprovare (backoff esponenziale)
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }

      showSuccess('Categoria aggiunta con successo!');
    } catch (error) {
      console.error('Errore durante l\'aggiunta della categoria:', error);
      showError(error.message || 'Errore durante l\'aggiunta della categoria.');
      // Ripristina lo stato precedente in caso di errore
      setCategories(previousCategories);
      // Ripristina anche il backup locale
      localStorage.setItem('categories_backup', JSON.stringify(previousCategories));
    }
  };

  const handleEditCategory = async (type, id, updatedFields) => {
    // Salva lo stato precedente per il ripristino in caso di errore
    const previousCategories = categories;

    try {
      // Aggiorna lo stato locale
      const updatedCategories = {
        ...categories,
        [type]: editCategory(categories[type] || [], id, updatedFields)
      };
      setCategories(updatedCategories);

      // Salva sempre il backup locale
      localStorage.setItem('categories_backup', JSON.stringify(updatedCategories));

      // Sincronizza con Firestore con retry
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          if (categoriesData && categoriesData.length > 0) {
            const categoriesDoc = categoriesData[0];
            await updateDocument('categories', categoriesDoc.documentId || categoriesDoc.id, updatedCategories);
          }
          break; // Successo, esci dal loop
        } catch (syncError) {
          retryCount++;
          console.warn(`Tentativo ${retryCount} di sincronizzazione fallito:`, syncError);

          if (retryCount >= maxRetries) {
            throw syncError; // Rilancia l'errore se tutti i tentativi sono falliti
          }

          // Aspetta un po' prima di riprovare (backoff esponenziale)
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }

      showSuccess('Categoria modificata con successo!');
    } catch (error) {
      console.error('Errore durante la modifica della categoria:', error);
      showError(error.message || 'Errore durante la modifica della categoria.');
      // Ripristina lo stato precedente in caso di errore
      setCategories(previousCategories);
      // Ripristina anche il backup locale
      localStorage.setItem('categories_backup', JSON.stringify(previousCategories));
    }
  };

  const handleDeleteCategory = async (type, id) => {
    // Salva lo stato precedente per il ripristino in caso di errore
    const previousCategories = categories;

    try {
      // Aggiorna lo stato locale
      const updatedCategories = {
        ...categories,
        [type]: deleteCategory(categories[type] || [], id)
      };
      setCategories(updatedCategories);

      // Salva sempre il backup locale
      localStorage.setItem('categories_backup', JSON.stringify(updatedCategories));

      // Sincronizza con Firestore con retry
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          if (categoriesData && categoriesData.length > 0) {
            const categoriesDoc = categoriesData[0];
            await updateDocument('categories', categoriesDoc.id, updatedCategories);
          }
          break; // Successo, esci dal loop
        } catch (syncError) {
          retryCount++;
          console.warn(`Tentativo ${retryCount} di sincronizzazione fallito:`, syncError);

          if (retryCount >= maxRetries) {
            throw syncError; // Rilancia l'errore se tutti i tentativi sono falliti
          }

          // Aspetta un po' prima di riprovare (backoff esponenziale)
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }

      showSuccess('Categoria eliminata con successo!');
    } catch (error) {
      console.error('Errore durante l\'eliminazione della categoria:', error);
      showError(error.message || 'Errore durante l\'eliminazione della categoria.');
      // Ripristina lo stato precedente in caso di errore
      setCategories(previousCategories);
      // Ripristina anche il backup locale
      localStorage.setItem('categories_backup', JSON.stringify(previousCategories));
    }
  };

  // Gestione debiti e crediti
  const addDebt = async (debtData) => {
    try {
      // Aggiorna lo stato locale
      const newDebts = addDebtTransaction(debts, debtData);
      setDebts(newDebts);

      // Sincronizza con Firestore
      await addDocument('debts', debtData);

      setShowDebtForm(false);
      setEditingDebt(null);
      setPrefilledDebtPerson('');
    } catch (error) {
      console.error('Errore durante l\'aggiunta del movimento:', error);
      showError(error.message || 'Errore durante l\'aggiunta del movimento.');
    }
  };

  const editDebt = async (debtData) => {
    try {
      // Se editingDebt non ha un id, significa che è un nuovo movimento (es. risoluzione situazione)
      if (!editingDebt.id) {
        // Tratta come nuovo movimento
        const newDebts = addDebtTransaction(debts, debtData);
        setDebts(newDebts);

        // Sincronizza con Firestore
        await addDocument('debts', debtData);
      } else {
        // Tratta come modifica di movimento esistente
        const updatedDebts = editDebtTransaction(debts, editingDebt.id, debtData);
        setDebts(updatedDebts);

        // Sincronizza con Firestore
        await updateDocument('debts', editingDebt.id, debtData);
      }

      setShowDebtForm(false);
      setEditingDebt(null);
      setPrefilledDebtPerson('');
    } catch (error) {
      console.error('Errore durante la modifica del movimento:', error);
      showError(error.message || 'Errore durante la modifica del movimento.');
    }
  };

  const deleteDebt = async (debtId) => {
    try {
      // Aggiorna lo stato locale
      const updatedDebts = deleteDebtTransaction(debts, debtId);
      setDebts(updatedDebts);

      // Sincronizza con Firestore
      await deleteDocument('debts', debtId);
    } catch (error) {
      console.error('Errore durante l\'eliminazione del movimento:', error);
      showError(error.message || 'Errore durante l\'eliminazione del movimento.');
    }
  };

  const deletePerson = async (personName) => {
    try {
      // Calcola i trasferimenti automatici necessari
      const { remainingDebts, automaticTransfers } = deletePersonWithAutomaticTransfers(debts, personName);

      // Aggiorna lo stato locale
      setDebts(remainingDebts);

      // Sincronizza con Firestore - elimina tutti i documenti della persona
      const personDebts = debts.filter(d => d.personName === personName);
      for (const debt of personDebts) {
        await deleteDocument('debts', debt.id);
      }

      // Aggiungi i trasferimenti automatici se necessari
      if (automaticTransfers.length > 0) {
        for (const transfer of automaticTransfers) {
          // Crea una transazione di trasferimento
          const transferExpense = {
            amount: transfer.amount,
            category: 'Trasferimento',
            walletId: transfer.fromWalletId,
            date: transfer.date,
            description: transfer.description,
            store: 'Trasferimento'
          };

          const transferIncome = {
            amount: transfer.amount,
            category: 'Trasferimento',
            walletId: transfer.toWalletId,
            date: transfer.date,
            description: transfer.description,
            store: 'Trasferimento'
          };

          // Aggiungi le transazioni usando le funzioni di logica
          const newExpense = {
            ...transferExpense,
            date: transferExpense.date ? new Date(transferExpense.date).toISOString() : new Date().toISOString()
          };
          const newIncome = {
            ...transferIncome,
            date: transferIncome.date ? new Date(transferIncome.date).toISOString() : new Date().toISOString()
          };

          // Sincronizza con Firestore
          await addDocument('expenses', newExpense);
          await addDocument('incomes', newIncome);
        }

        showSuccess(`Persona eliminata con successo! Creati ${automaticTransfers.length} trasferimento/i automatico/i.`);
      } else {
        showSuccess('Persona eliminata con successo!');
      }
    } catch (error) {
      console.error('Errore durante l\'eliminazione della persona:', error);
      showError(error.message || 'Errore durante l\'eliminazione della persona.');
    }
  };

  const openDebtForm = (type = 'debt', personName = '') => {
    setDebtTransactionType(type);
    setEditingDebt(null);
    setPrefilledDebtPerson(personName || '');
    console.log('App: openDebtForm called', { type, personName });
    setShowDebtForm(true);
  };

  const handleAddDebt = (personName = '') => {
    console.log('App: handleAddDebt called', personName);
    openDebtForm('debt', personName);
  };

  const handleAddCredit = (personName = '') => {
    console.log('App: handleAddCredit called', personName);
    openDebtForm('credit', personName);
  };

  const handleEditDebt = (debt) => {
    setEditingDebt(debt);
    setDebtTransactionType(debt.type);
    setPrefilledDebtPerson(debt?.personName || '');
    setShowDebtForm(true);
    //setShowPersonDetail(false); // Commentato o rimosso per mantenere il dettaglio persona aperto
  };

  const handleCloseDebtForm = () => {
    setShowDebtForm(false);
    setEditingDebt(null);
    setPrefilledDebtPerson('');
  };

  const handlePersonClick = (person) => {
    setSelectedPerson(person);
    setShowPersonDetail(true);
  };

  const handleClosePersonDetail = () => {
    setShowPersonDetail(false);
    setSelectedPerson(null);
  };

  // Funzione per mostrare il dettaglio di un movimento di debito
  const handleShowDebtTransactionDetail = (transaction) => {
    setSelectedDebtTransaction(transaction);
    setShowDebtTransactionDialog(true);
  };

  // Funzione per confermare l'eliminazione di un movimento di debito
  const handleConfirmDeleteDebt = (debtId) => {
    setDebtToDelete(debtId);
    setShowDebtConfirmDelete(true);
  };

  const confirmDeleteDebt = async () => {
    try {
      await deleteDebt(debtToDelete);
      setShowDebtConfirmDelete(false);
      setDebtToDelete(null);
    } catch (error) {
      console.error('Errore durante l\'eliminazione del movimento:', error);
    }
  };

  const closeDebtConfirmDelete = () => {
    setShowDebtConfirmDelete(false);
    setDebtToDelete(null);
  };

  const handleResolveSituation = (personName, balance) => {
    // Crea un movimento di risoluzione
    const resolutionTransaction = createResolutionTransaction(personName, balance, '');
    setEditingDebt(resolutionTransaction); // Usa editingDebt per pre-compilare tutto tranne il conto
    setDebtTransactionType(resolutionTransaction.type);
    setShowDebtForm(true);
    setShowPersonDetail(false);
  };

  // Funzione per gestire la ricezione dello stipendio
  const handleSalaryReceived = async (personName, { realAmount, walletId, rescaledAmounts }) => {
    try {
      // 1. Aggiorna ogni transazione income selezionata e il relativo debito collegato
      const updatePromises = [];
      let currentDebtsState = [...debts];

      rescaledAmounts.forEach(({ id, newAmount }) => {
        // Aggiorna l'income su Firestore
        updatePromises.push(updateDocument('incomes', id, { amount: newAmount }));

        // Trova l'income nello stato locale per vedere se ha un debito collegato
        const incomeItem = incomes.find(inc => inc.id === id);
        if (incomeItem?.linkedDebtId) {
          // Aggiorna il debito (credito) su Firestore
          updatePromises.push(updateDocument('debts', incomeItem.linkedDebtId, { amount: newAmount }));

          // Aggiorna lo stato dei debiti locale per feedback immediato
          currentDebtsState = currentDebtsState.map(d =>
            d.id === incomeItem.linkedDebtId ? { ...d, amount: newAmount } : d
          );
        }
      });

      await Promise.all(updatePromises);

      // 2. Crea il debito di chiusura (la persona ci ha pagato: riduce il nostro credito)
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const debtPayload = {
        personName,
        amount: realAmount,
        type: 'debt',
        walletId,
        description: 'Stipendio ricevuto – chiusura periodo',
        date,
        time,
      };

      // Aggiorniamo lo stato locale includendo sia i vecchi debiti ricalcolati che il nuovo debito di chiusura
      const finalDebtsState = addDebtTransaction(currentDebtsState, debtPayload);
      setDebts(finalDebtsState);
      await addDocument('debts', debtPayload);

      // 3. Feedback
      showSuccess?.(
        `Stipendio aggiornato: ${rescaledAmounts.length} transazion${rescaledAmounts.length === 1 ? 'e ricalcolata' : 'i ricalcolate'}, debito di chiusura creato.`,
        'Stipendio applicato'
      );

      setShowPersonDetail(false);
      setSelectedPerson(null);
    } catch (error) {
      console.error('Errore durante l\'applicazione dello stipendio:', error);
      showError(error.message || 'Errore durante l\'applicazione dello stipendio.');
    }
  };

  const handleOpenSalaryWizard = (personName) => {
    setSalaryWizardPerson(personName);
    setShowSalaryWizard(true);
  };

  const handleCloseSalaryWizard = () => {
    setShowSalaryWizard(false);
    setSalaryWizardPerson(null);
  };

  const getExistingPersons = () => uniquePersons;

  // Ottiene i dati filtrati (combina filtri attivi e date range)
  const filteredData = useMemo(() => {
    if (!Array.isArray(expenses) || !Array.isArray(incomes)) {
      return { expenses: [], incomes: [], debts: [] };
    }

    let filteredExpenses = expenses;
    let filteredIncomes = incomes;
    let filteredDebts = Array.isArray(debts) ? debts : [];

    // Filtro per tempo
    if (activeFilters.timeRange !== 'all') {
      const now = getCurrentRomeTime();
      let startDate, endDate;

      switch (activeFilters.timeRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
          break;
        case 'week': {
          const dayOfWeek = now.getDay();
          const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysToSubtract);
          endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 7);
          break;
        }
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          endDate = new Date(now.getFullYear() + 1, 0, 1);
          break;
        case 'custom':
          if (activeFilters.startDate && activeFilters.endDate) {
            startDate = new Date(activeFilters.startDate);
            endDate = new Date(activeFilters.endDate);
            endDate.setDate(endDate.getDate() + 1); // Include end date
          }
          break;
      }

      if (startDate && endDate) {
        filteredExpenses = filteredExpenses.filter(expense => {
          const expenseDate = new Date(expense.date);
          return expenseDate >= startDate && expenseDate < endDate;
        });
        filteredIncomes = filteredIncomes.filter(income => {
          const incomeDate = new Date(income.date);
          return incomeDate >= startDate && incomeDate < endDate;
        });
        filteredDebts = filteredDebts.filter(debt => {
          const debtDate = new Date(debt.date);
          return debtDate >= startDate && debtDate < endDate;
        });
      }
    }

    // Filtro per categorie
    if (activeFilters.selectedCategories.length > 0) {
      filteredExpenses = filteredExpenses.filter(expense =>
        activeFilters.selectedCategories.includes(expense.category)
      );
      filteredIncomes = filteredIncomes.filter(income =>
        activeFilters.selectedCategories.includes(income.category)
      );
      filteredDebts = filteredDebts.filter(debt =>
        activeFilters.selectedCategories.includes(debt.category)
      );
    }

    // Filtro per negozi
    if (activeFilters.selectedStores.length > 0) {
      filteredExpenses = filteredExpenses.filter(expense =>
        activeFilters.selectedStores.some(store =>
          expense.store && expense.store.toLowerCase().startsWith(store.toLowerCase())
        )
      );
      filteredIncomes = filteredIncomes.filter(income =>
        activeFilters.selectedStores.some(store =>
          income.store && income.store.toLowerCase().startsWith(store.toLowerCase())
        )
      );
      filteredDebts = filteredDebts.filter(debt =>
        activeFilters.selectedStores.some(store =>
          debt.store && debt.store.toLowerCase().startsWith(store.toLowerCase())
        )
      );
    }

    // Filtro per conti
    if (activeFilters.selectedWallets && activeFilters.selectedWallets.length > 0) {
      filteredExpenses = filteredExpenses.filter(expense =>
        activeFilters.selectedWallets.includes(expense.walletId)
      );
      filteredIncomes = filteredIncomes.filter(income =>
        activeFilters.selectedWallets.includes(income.walletId)
      );
      filteredDebts = filteredDebts.filter(debt =>
        activeFilters.selectedWallets.includes(debt.walletId)
      );
    }

    // Filtro per trasferimenti
    if (!activeFilters.showTransfers) {
      filteredExpenses = filteredExpenses.filter(expense =>
        expense.category !== 'Trasferimento'
      );
      filteredIncomes = filteredIncomes.filter(income =>
        income.category !== 'Trasferimento'
      );
    }

    // Filtro per debiti e crediti
    if (!activeFilters.showDebtsAndCredits) {
      filteredDebts = [];
    } else {
      // Mostra solo i debiti e crediti collegati ad un portafoglio reale
      filteredDebts = filteredDebts.filter(debt => 
        debt.walletId && 
        debt.walletId !== DEBT_WALLET_ID && 
        debt.walletId !== CREDIT_WALLET_ID
      );
    }

    // Filtro per date range (se presente)
    if (dateRange) {
      filteredExpenses = filteredExpenses.filter(expense => {
        const expenseDate = expense.date.split('T')[0];
        return expenseDate >= dateRange.startDate && expenseDate <= dateRange.endDate;
      });

      filteredIncomes = filteredIncomes.filter(income => {
        const incomeDate = income.date.split('T')[0];
        return incomeDate >= dateRange.startDate && incomeDate <= dateRange.endDate;
      });

      filteredDebts = filteredDebts.filter(debt => {
        const debtDate = debt.date.split('T')[0];
        return debtDate >= dateRange.startDate && debtDate <= dateRange.endDate;
      });
    }

    // Filtro per escludere le transazioni future (solo per la lista generale)
    filteredExpenses = filteredExpenses.filter(expense => !isFutureDate(expense.date));
    filteredIncomes = filteredIncomes.filter(income => !isFutureDate(income.date));
    filteredDebts = filteredDebts.filter(debt => !isFutureDate(debt.date));

    return { expenses: filteredExpenses, incomes: filteredIncomes, debts: filteredDebts };
  }, [expenses, incomes, debts, activeFilters, dateRange]);

  const filteredTransactions = useMemo(() => {
    const allTransactions = [
      ...filteredData.expenses.map(item => ({ ...item, _type: 'expense' })),
      ...filteredData.incomes.map(item => ({ ...item, _type: 'income' })),
      ...filteredData.debts.map(item => ({ 
        ...item, 
        _type: item.type === 'credit' ? 'credit' : 'debt',
        category: item.type === 'credit' ? 'Credito' : 'Debito',
        store: item.personName || item.debtPersonName || 'Debito/Credito',
        date: `${item.date.split('T')[0]}T08:00:00`,
        time: '08:00'
      }))
    ].sort((a, b) => new Date(b.date) - new Date(a.date));

    switch (activeFilters.transactionType) {
      case 'expenses':
        return allTransactions.filter(item => item._type === 'expense' || item._type === 'debt');
      case 'incomes':
        return allTransactions.filter(item => item._type === 'income' || item._type === 'credit');
      default:
        return allTransactions;
    }
  }, [filteredData, activeFilters.transactionType]);

  const filteredExpenseTransactions = useMemo(
    () => filteredTransactions.filter(item => item._type === 'expense'),
    [filteredTransactions]
  );

  const filteredIncomeTransactions = useMemo(
    () => filteredTransactions.filter(item => item._type === 'income'),
    [filteredTransactions]
  );

  useEffect(() => {
    if (activeTab !== 'transactions') {
      return;
    }
    setVisibleTransactionsCount(TRANSACTIONS_PAGE_SIZE);
  }, [activeTab, activeFilters]);

  const totalFilteredTransactions = filteredTransactions.length;

  const handleLoadMoreTransactions = useCallback(() => {
    setVisibleTransactionsCount(prev => Math.min(prev + TRANSACTIONS_PAGE_SIZE, totalFilteredTransactions));
  }, [totalFilteredTransactions]);

  const visibleTransactions = useMemo(
    () => filteredTransactions.slice(0, visibleTransactionsCount),
    [filteredTransactions, visibleTransactionsCount]
  );

  const hasMoreTransactions = visibleTransactionsCount < totalFilteredTransactions;

  const storeEntries = useMemo(() => {
    if (filteredTransactions.length === 0) {
      return [];
    }

    const entriesMap = new Map();

    filteredTransactions.forEach(transaction => {
      // Ignora sempre transazioni di debito/credito pura nella sezione negozi
      if (transaction._type === 'debt' || transaction._type === 'credit') {
        return;
      }
      const storeName = transaction.store?.trim();
      if (!storeName) {
        return;
      }

      if (!entriesMap.has(storeName)) {
        entriesMap.set(storeName, []);
      }

      entriesMap.get(storeName).push(transaction);
    });

    const sortStrategy = STORE_SORT_CONFIG[storeSortOption] || STORE_SORT_CONFIG[DEFAULT_STORE_SORT];

    return Array.from(entriesMap.entries())
      .map(([storeName, transactions]) => {
        const totals = transactions.reduce(
          (acc, transaction) => {
            const amount = parseFloat(transaction.amount) || 0;
            if (transaction._type === 'income') {
              acc.incomes += amount;
            } else {
              acc.expenses += amount;
            }
            return acc;
          },
          { expenses: 0, incomes: 0 }
        );

        const lastTransactionDate = transactions.reduce((latest, transaction) => {
          const timestamp = new Date(transaction.date).getTime();
          if (Number.isNaN(timestamp)) {
            return latest;
          }
          return Math.max(latest, timestamp);
        }, 0);

        return {
          storeName,
          transactions,
          netTotal: totals.incomes - totals.expenses,
          totalExpenses: totals.expenses,
          totalIncomes: totals.incomes,
          transactionCount: transactions.length,
          lastTransactionDate
        };
      })
      .sort((a, b) => {
        const comparison = sortStrategy.compare(a, b);
        if (comparison !== 0) {
          return comparison;
        }
        return STORE_NAME_COLLATOR.compare(a.storeName, b.storeName);
      });
  }, [filteredTransactions, storeSortOption]);

  const selectedStoreSort = useMemo(
    () =>
      STORE_SORT_OPTIONS.find(option => option.value === storeSortOption) ||
      STORE_SORT_OPTIONS.find(option => option.value === DEFAULT_STORE_SORT),
    [storeSortOption]
  );



  // Funzioni gestione conti
  const addWallet = async (wallet) => {
    try {
      const walletWithType = {
        ...wallet,
        type: wallet.type || DEFAULT_WALLET_TYPE_NAME
      };
      // Usa la funzione pura per validare e aggiungere localmente
      const newWallets = addWalletLogic(wallets, { ...walletWithType, isFavorite: false });
      setWallets(newWallets);
      // Sincronizza con Firestore
      const newWallet = newWallets.find(w => !wallets.some(ow => ow.id === w.id));
      const { id: _ignoredWalletId, ...walletWithoutId } = newWallet; // Rimuovi l'id per lasciare che Firestore lo generi
      const walletToStore = {
        ...walletWithoutId,
        isFavorite: Boolean(walletWithoutId?.isFavorite)
      };
      const result = await addDocument('wallets', walletToStore);
      return result;
    } catch (error) {
      console.error('Errore durante la creazione del wallet:', error);
      throw error;
    }
  };



  // Wallet form handlers
  const handleWalletSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!user) {
        showError('Utente non autenticato. Effettua nuovamente l\'accesso.');
        return;
      }

      if (walletCategoryOptions.length === 0) {
        showError('Aggiungi almeno una categoria conto prima di creare un nuovo conto.');
        return;
      }

      // Validation
      const isValidWalletType = walletCategoryOptions.some(option => option.value === walletFormData.type);
      if (!isValidWalletType) {
        // Try to fallback to default if current is invalid but options exist
        const defaultType = getDefaultWalletType();
        if (walletFormData.type !== defaultType) {
          console.warn('Invalid wallet type selected, falling back to default:', defaultType);
          // We continue with default but warn? Or hard block?
          // Original blocked. Let's block but with better msg or try to fix.
        }
        showError('Seleziona una tipologia di conto valida.');
        return;
      }

      if (!walletFormData.type) {
        showError('Seleziona una tipologia di conto.');
        return;
      }

      if (!walletFormData.name.trim()) {
        return;
      }

      const parsedBalance = moneyDigitsToNumber(walletFormData.balance);

      if (editingWallet) {
        // Robust ID Resolution
        let documentId = editingWallet.documentId;

        if (!documentId) {
          // Search in authoritative list
          const firestoreWallet = walletsData.find(w => w.id === editingWallet.id || w.documentId === editingWallet.id);
          documentId = firestoreWallet?.documentId || firestoreWallet?.id;
        }

        if (documentId) {
          console.log('[handleWalletSubmit] Updating wallet', documentId, 'with type:', walletFormData.type);
          
          const isDeposit = walletFormData.type?.toLowerCase().includes('deposito');
          const isSecurity = ['etf', 'azione', 'azioni', 'obbligazione', 'obbligazioni'].some(t => walletFormData.type?.toLowerCase().includes(t));
          
          await updateDocument('wallets', documentId, {
            name: walletFormData.name,
            color: walletFormData.color,
            initialBalance: parsedBalance,
            type: walletFormData.type || DEFAULT_WALLET_TYPE_NAME,
            grossAnnualRate: isDeposit ? (walletFormData.grossAnnualRate || '') : null,
            isinCode: isSecurity ? (walletFormData.isinCode || '') : null,
            isinName: isSecurity ? (walletFormData.isinName || '') : null,
            expectedRate: isSecurity ? (walletFormData.expectedRate || '') : null
          });
        } else {
          console.error('Impossibile determinare l\'ID del documento per il wallet da modificare:', editingWallet);
          showError('Errore interno: ID conto non trovato. Ricarica la pagina.');
          return;
        }

        setEditingWallet(null);
      } else {
        const isDeposit = walletFormData.type?.toLowerCase().includes('deposito');
        const isSecurity = ['etf', 'azione', 'azioni', 'obbligazione', 'obbligazioni'].some(t => walletFormData.type?.toLowerCase().includes(t));
        
        await addWallet({
          ...walletFormData,
          initialBalance: parsedBalance,
          grossAnnualRate: isDeposit ? (walletFormData.grossAnnualRate || '') : null,
          isinCode: isSecurity ? (walletFormData.isinCode || '') : null,
          isinName: isSecurity ? (walletFormData.isinName || '') : null,
          expectedRate: isSecurity ? (walletFormData.expectedRate || '') : null
        });
      }

      setWalletFormData({
        name: '',
        color: WALLET_COLORS[0],
        balance: '',
        type: getDefaultWalletType(),
        grossAnnualRate: '',
        isinCode: '',
        isinName: '',
        expectedRate: ''
      });
      setTickerSearchError(null);
      setShowWalletForm(false);
    } catch (error) {
      console.error('Errore durante il salvataggio del conto:', error);

      // Mostra messaggio specifico per duplicati
      if (error.message === 'Esiste già un conto con questo nome') {
        showError('Esiste già un conto con questo nome. Scegli un nome diverso.');
      } else {
        showError('Errore durante il salvataggio del conto. Riprova.');
      }
    }
  };

  const handleEditWallet = (wallet) => {
    setEditingWallet(wallet);
    setWalletFormData({
      name: wallet.name,
      color: wallet.color,
      balance: amountToMoneyDigits(
        wallet.initialBalance !== undefined ? wallet.initialBalance : wallet.balance || 0
      ),
      type: wallet.type || DEFAULT_WALLET_TYPE_NAME,
      grossAnnualRate: wallet.grossAnnualRate || '',
      isinCode: wallet.isinCode || '',
      isinName: wallet.isinName || '',
      expectedRate: wallet.expectedRate || ''
    });
    setTickerSearchError(null);
    setShowWalletForm(true);
  };

  const handleAddWallet = () => {
    setEditingWallet(null);
    setWalletFormData({
      name: '',
      color: WALLET_COLORS[0],
      balance: '',
      type: getDefaultWalletType(),
      grossAnnualRate: '',
      isinCode: '',
      isinName: '',
      expectedRate: ''
    });
    setTickerSearchError(null);
    setShowWalletForm(true);
  };

  // Funzioni per i dialog dei wallet
  const handleWalletClick = (wallet) => {
    setSelectedWallet(wallet);
    setShowWalletActions(true);
  };

  const resetWalletCheckForm = () => {
    setWalletCheckCounts(createEmptyWalletCheckCounts());
    setWalletCheckExtra('');
    setWalletCheckActiveField(null);
    setShowWalletCheckKeypad(false);
  };

  const handleOpenWalletCheck = () => {
    if (!selectedWallet) {
      return;
    }

    const refreshedWallet = walletsWithCalculatedBalance.find(wallet => wallet.id === selectedWallet.id);
    if (refreshedWallet) {
      setSelectedWallet(refreshedWallet);
    }

    resetWalletCheckForm();
    setShowWalletActions(false);
    setShowWalletCheck(true);
  };

  const handleWalletCheckCountChange = (denomination, value) => {
    const sanitized = sanitizeIntegerInput(value);
    setWalletCheckCounts(prev => ({
      ...prev,
      [denomination]: sanitized
    }));
  };

  const handleWalletCheckExtraChange = (value) => {
    setWalletCheckExtra(extractMoneyDigits(value));
  };

  const closeWalletCheck = () => {
    resetWalletCheckForm();
    setShowWalletCheck(false);
    setSelectedWallet(null);
  };

  const openWalletCheckKeypad = (fieldKey) => {
    if (!isMobileViewport) {
      return;
    }
    setWalletCheckActiveField(fieldKey);
    setShowWalletCheckKeypad(true);
  };

  const handleWalletCheckKeypadChange = (digits) => {
    if (!walletCheckActiveField) {
      return;
    }

    if (walletCheckActiveField === 'extra') {
      setWalletCheckExtra(extractMoneyDigits(digits));
      return;
    }

    setWalletCheckCounts(prev => ({
      ...prev,
      [walletCheckActiveField]: sanitizeIntegerInput(digits)
    }));
  };

  const adjustWalletCheckCount = (denomination, delta) => {
    setWalletCheckCounts(prev => {
      const current = Number.parseInt(prev[denomination], 10) || 0;
      const next = Math.max(0, current + delta);
      return {
        ...prev,
        [denomination]: String(next)
      };
    });
  };

  const closeWalletCheckKeypad = () => {
    setWalletCheckActiveField(null);
    setShowWalletCheckKeypad(false);
  };

  const handleEditWalletFromDialog = () => {
    if (selectedWallet) {
      handleEditWallet(selectedWallet);
      setShowWalletActions(false);
      setSelectedWallet(null);
    }
  };

  const handleDeleteWalletFromDialog = () => {
    setShowWalletActions(false);
    setShowWalletConfirmDelete(true);
  };

  const confirmDeleteWalletFromDialog = async () => {
    if (selectedWallet) {
      setDeletingWallet(true);
      try {
        await deleteWallet(selectedWallet.id);
      } catch (e) {
        console.error('Errore durante l\'eliminazione del wallet:', e);
      }
      setDeletingWallet(false);
      setShowWalletConfirmDelete(false);
      setSelectedWallet(null);
    }
  };

  const closeWalletActions = () => {
    setShowWalletActions(false);
    setSelectedWallet(null);
  };

  const closeWalletConfirmDelete = () => {
    setShowWalletConfirmDelete(false);
    setSelectedWallet(null);
  };

  const resetTransferForm = () => {
    setTransferFormData({ fromWalletId: '', toWalletId: '', amount: '' });
    setShowTransferKeypad(false);
  };

  const handleTransferAmountFocus = (event) => {
    if (!isMobileViewport) {
      return;
    }
    event?.preventDefault?.();
    setShowTransferKeypad(true);
    event?.target?.blur?.();
  };

  const handleTransferAmountChange = (digits) => {
    setTransferFormData(prev => ({
      ...prev,
      amount: extractMoneyDigits(digits)
    }));
  };

  // Funzione per gestire i trasferimenti tra conti
  const handleTransfer = async (transferData) => {
    const { fromWalletId, toWalletId, amount } = transferData;
    const now = new Date().toISOString();
    const numericAmount = moneyDigitsToNumber(amount);

    // Crea la transazione di uscita (spesa) dal conto di origine
    const outgoingTransaction = {
      amount: numericAmount,
      category: 'Trasferimento',
      date: now,
      store: 'Trasferimento',
      walletId: fromWalletId,
      type: 'expense'
    };

    // Crea la transazione di entrata (entrata) nel conto di destinazione
    const incomingTransaction = {
      amount: numericAmount,
      category: 'Trasferimento',
      date: now,
      store: 'Trasferimento',
      walletId: toWalletId,
      type: 'income'
    };

    // Aggiungi entrambe le transazioni
    await addDocument('expenses', outgoingTransaction);
    await addDocument('incomes', incomingTransaction);
  };

  const handleTransferSubmit = (e) => {
    e.preventDefault();
    if (!transferFormData.fromWalletId || !transferFormData.toWalletId || !transferFormData.amount || transferFormData.fromWalletId === transferFormData.toWalletId) {
      return;
    }

    handleTransfer(transferFormData);
    resetTransferForm();
    setShowTransferModal(false);
  };

  const handleTransferChange = (e) => {
    const { name, value } = e.target;

    if (name === 'amount') {
      handleTransferAmountChange(value);
      return;
    }

    setTransferFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleShowTransferModal = () => {
    resetTransferForm();
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    setShowTransferModal(false);
    resetTransferForm();
  };


  const handleUpdateSettings = useCallback(async (newSettings) => {
    try {
      await saveUserSettings(newSettings);
      setUserSettings(newSettings);
    } catch (err) {
      console.error('Errore aggiornamento impostazioni:', err);
      throw err;
    }
  }, [saveUserSettings]);

  const mergeCategoryLists = (current = [], incoming = []) => {
    const map = new Map();

    const makeKey = (item, index, prefix) => {
      if (!item || typeof item !== 'object') {
        return `${prefix}-${index}`;
      }
      const id = typeof item.id === 'string' && item.id.trim() ? item.id.trim() : null;
      if (id) return `id:${id}`;
      const name = typeof item.name === 'string' && item.name.trim() ? item.name.trim().toLowerCase() : null;
      if (name) return `name:${name}`;
      return `${prefix}-${index}`;
    };

    current.forEach((item, idx) => {
      map.set(makeKey(item, idx, 'existing'), item);
    });

    incoming.forEach((item, idx) => {
      map.set(makeKey(item, idx, 'incoming'), item);
    });

    return Array.from(map.values());
  };

  // Funzione per importare i dati
  const importData = async (data) => {
    setIsImportingData(true);
    try {
      await importUserData(data, {
        onStoresUpdate: (updatedStores) => {
          if (Array.isArray(updatedStores)) setStores(updatedStores);
        },
        onCategoriesUpdate: (updatedCategories) => {
          if (!updatedCategories) return;
          setCategories(prev => {
            const merged = {
              expense: mergeCategoryLists(prev?.expense || [], updatedCategories.expense || []),
              income: mergeCategoryLists(prev?.income || [], updatedCategories.income || []),
              wallet: mergeCategoryLists(prev?.wallet || [], updatedCategories.wallet || defaultCategories.wallet || [])
            };
            try {
              localStorage.setItem('categories_backup', JSON.stringify(merged));
            } catch (error) {
              console.error('Errore durante il salvataggio del backup categorie:', error);
            }
            return merged;
          });
        }
      });

      const importedTheme = data?.theme || data?.data?.theme;
      if (importedTheme && (importedTheme === 'light' || importedTheme === 'dark') && importedTheme !== theme) {
        toggleTheme();
      }

      showSuccess('Dati importati con successo!');
      setShowUserProfile(false);
      setActiveTab('transactions');
      return true;
    } catch (error) {
      console.error('Errore durante l\'importazione dei dati:', error);
      showError('Errore durante l\'importazione dei dati: ' + (error?.message || ''));
      return false;
    } finally {
      setIsImportingData(false);
    }
  };

  const handleImportFileSubmit = () => {
    if (!importFile || isImportingData) {
      if (!importFile) {
        showError('Seleziona un file JSON valido prima di importare.');
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const fileContent = event?.target?.result;
        if (typeof fileContent !== 'string') {
          throw new Error('Formato file non supportato.');
        }

        const parsedData = JSON.parse(fileContent);
        const success = await importData(parsedData);
        if (success) {
          setShowImportModal(false);
          setImportFile(null);
        }
      } catch (error) {
        console.error('Errore durante l\'importazione dal file:', error);
        showError('Errore durante l\'importazione: ' + (error?.message || 'Formato file non valido.'));
      }
    };
    reader.onerror = () => {
      showError('Impossibile leggere il file di backup selezionato.');
    };

    reader.readAsText(importFile);
  };

  // Funzione per aggiungere un nuovo store
  const addStore = async (storeName) => {
    if (storeName.trim() && !stores.includes(storeName.trim())) {
      const newStores = [...stores, storeName.trim()];
      setStores(newStores);

      // Aggiorna Firestore
      if (storesData && storesData.length > 0) {
        const storesDoc = storesData[0];
        await updateDocument('stores', storesDoc.id, { stores: newStores });
      } else {
        // Se non esiste ancora un documento stores, crealo
        await addDocument('stores', { stores: newStores });
      }
    }
  };

  // Funzione per applicare i filtri
  const applyFilters = (filters) => {
    const normalizedFilters = normalizeFilters(filters);
    setActiveFilters(normalizedFilters);

    if (user && saveUserSettingsRef.current) {
      saveUserSettingsRef.current({ filters: normalizedFilters }).catch(error => {
        console.error('Errore durante il salvataggio dei filtri:', error);
        showError('Errore durante il salvataggio dei filtri. Riprova.');
      });
    }
  };

  const updateStatisticsPreferences = (updates = {}) => {
    setStatisticsPreferences(prev => {
      const merged = { ...prev, ...updates };
      const normalizedPreferences = normalizeStatisticsPreferences(merged);

      if (user && saveUserSettingsRef.current) {
        saveUserSettingsRef.current({ statisticsPreferences: normalizedPreferences }).catch(error => {
          console.error('Errore durante il salvataggio delle preferenze statistiche:', error);
          showError('Errore durante il salvataggio della visualizzazione delle statistiche. Riprova.');
        });
      }

      return normalizedPreferences;
    });
  };

  // Funzione per resettare tutti i dati
  const resetAllData = async () => {
    try {
      await deleteAllUserData();
      // Pulisci anche il backup locale delle categorie
      localStorage.removeItem('categories_backup');
      // Reset filtri attivi
      const defaultFilters = createDefaultFilters();
      setActiveFilters(defaultFilters);
      const defaultStatsPreferences = createDefaultStatisticsPreferences();
      setStatisticsPreferences(defaultStatsPreferences);
      if (user && saveUserSettingsRef.current) {
        saveUserSettingsRef.current({
          filters: defaultFilters,
          statisticsPreferences: defaultStatsPreferences
        }).catch(error => {
          console.error('Errore durante il ripristino dei filtri salvati:', error);
        });
      }

      showSuccess('Tutti i dati sono stati eliminati con successo. L\'applicazione si ricaricherà automaticamente.');
      window.location.reload();
    } catch (error) {
      showError('Errore durante l\'eliminazione dei dati: ' + error.message);
    }
  };

  // Funzioni per gestire modal dei componenti figli
  const handleShowCategoryForm = (type, category = null) => {
    setCategoryType(type);
    if (category) {
      setEditingCategory(category);
      setCategoryFormData({ name: category.name, icon: category.icon });
    } else {
      setEditingCategory(null);
      setCategoryFormData({ name: '', icon: '📦' });
    }
    setShowCategoryForm(true);
    setShowCategoryFormFromDialog(false);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryFormData.name.trim()) return;

    try {
      if (editingCategory) {
        await handleEditCategory(categoryType, editingCategory.id, categoryFormData);
      } else {
        await handleAddCategory(categoryType, categoryFormData);
      }
      setCategoryFormData({ name: '', icon: '📦' });
      setShowCategoryForm(false);
      setEditingCategory(null);
      setShowCategoryFormFromDialog(false);
    } catch (error) {
      showError(error.message || 'Errore durante la creazione della categoria.');
    }
  };

  const handleCategoryCancel = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
    setCategoryFormData({ name: '', icon: '📦' });
    setShowCategoryFormFromDialog(false);
  };

  const handleCategoryDelete = async () => {
    if (editingCategory) {
      try {
        await handleDeleteCategory(categoryType, editingCategory.id);
        setShowCategoryForm(false);
        setEditingCategory(null);
        setCategoryFormData({ name: '', icon: '📦' });
        setShowCategoryFormFromDialog(false);
      } catch (error) {
        showError(error.message || 'Errore durante l\'eliminazione della categoria.');
      }
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleScroll = () => {
      // Su desktop/tablet, niente auto-collapse allo scroll (gestiamo diversamente se serve)
      if (window.innerWidth >= 1024) {
        return;
      }

      if (scrollFrameRef.current) {
        cancelAnimationFrame(scrollFrameRef.current);
      }

      scrollFrameRef.current = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const isScrollingDown = currentScrollY > lastScrollY.current;
        const scrollThreshold = 50; // Soglia minima di scroll per triggerare l'azione

        // Logica per Header (solo se scrolliamo abbastanza da evitare jitter)
        if (Math.abs(currentScrollY - lastScrollY.current) > 5) {
          if (currentScrollY < 20) {
            setIsHeaderExpanded(true);
          } else if (currentScrollY > 100 && isScrollingDown) {
            setIsHeaderExpanded(false);
          } else if (currentScrollY > 100 && !isScrollingDown) {
            // Opzionale: riaprire header scrollando su? Per ora no, per stabilità.
          }
        }

        // ... unused menu logic ...

        lastScrollY.current = currentScrollY;
      });
    };

    const evaluateScroll = () => {
      scrollFrameRef.current = null;

      if (typeof document === 'undefined') {
        return;
      }

      if (isAnyModalOpen) {
        return;
      }

      // Su tablet/desktop (layout orizzontale), mantieni sempre header compatto e menu visibile
      if (window.innerWidth >= 1024) {
        setIsHeaderExpanded(false);
        setShowFloatingMenu(true);
        return;
      }

      // Su mobile, gestisci lo scroll della pagina intera
      const currentScrollY = window.scrollY;
      const alwaysShowThreshold = 100; // px
      const isScrollable = checkIfScrollable();
      const viewportHeight = getViewportHeight();
      const doc = document.documentElement;
      const isAtBottom = viewportHeight + currentScrollY >= doc.scrollHeight - 2;

      if (isScrollable && isAtBottom) {
        setShowFloatingMenu(false);
        setIsHeaderExpanded(false);
        lastScrollY.current = currentScrollY;
        return;
      }
      // Mostra sempre il menu se sei vicino all'inizio o la pagina non è scrollabile
      if (currentScrollY <= alwaysShowThreshold || !isScrollable) {
        setShowFloatingMenu(true);
        setIsHeaderExpanded(true);
      } else {
        setIsHeaderExpanded(false);
        if (currentScrollY > lastScrollY.current && currentScrollY > alwaysShowThreshold) {
          setShowFloatingMenu(false); // scroll down, nascondi
        } else if (currentScrollY < lastScrollY.current) {
          setShowFloatingMenu(true); // scroll up, mostra
        }
      }
      lastScrollY.current = currentScrollY;
    };

    const scheduleScrollCheck = () => {
      if (scrollFrameRef.current !== null) {
        return;
      }
      scrollFrameRef.current = window.requestAnimationFrame(evaluateScroll);
    };

    const viewport = window.visualViewport;

    // Su mobile, usa il listener della window
    window.addEventListener('scroll', scheduleScrollCheck, { passive: true });
    viewport?.addEventListener('resize', scheduleScrollCheck);
    scheduleScrollCheck();

    return () => {
      window.removeEventListener('scroll', scheduleScrollCheck);
      viewport?.removeEventListener('resize', scheduleScrollCheck);
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
  }, [isAnyModalOpen]);

  const loadEasterEggsWithStatus = async () => {
    try {
      const eggsWithStatus = await getEasterEggsWithCompletionStatus(getCompletedEasterEggs);
      setEasterEggsWithStatus(eggsWithStatus);
    } catch (error) {
      console.error('Errore nel caricamento easter eggs:', error);
    }
  };

  // Hook per caricare gli easter eggs quando l'utente cambia
  useEffect(() => {
    if (user) loadEasterEggsWithStatus();
  }, [user]);

  // Hook per gestire il cambio di layout quando cambia la dimensione della finestra
  useEffect(() => {
    const handleResize = () => {
      // Su tablet/desktop, mantieni sempre header compatto e menu visibile
      if (window.innerWidth >= 1024) {
        setIsHeaderExpanded(false);
        setShowFloatingMenu(true);
      } else {
        // Su mobile, forza un re-check della scrollabilità quando cambia la dimensione
        setTimeout(() => {
          if (!checkIfScrollable() && !isAnyModalOpen) {
            setIsHeaderExpanded(true);
            setShowFloatingMenu(true);
          }
        }, 100);
      }
    };

    const detachViewportListeners = addViewportResizeListener(handleResize);
    return () => detachViewportListeners();
  }, [isAnyModalOpen]);

  // Mostra loading mentre verifica l'autenticazione o carica i dati iniziali
  const showInitialLoading = authLoading || (user && (isDataLoading || settingsLoading));

  if (showInitialLoading) {
    return (
      <div className="min-h-[100dvh] bg-white dark:bg-gray-900 flex flex-col items-center justify-center">
        <div className="flex flex-col items-center animate-in fade-in duration-1000">
          {/* Logo Minimalista */}
          <div className="mb-10 opacity-20 dark:opacity-30">
            <Wallet className="w-12 h-12 text-gray-900 dark:text-white" strokeWidth={1} />
          </div>

          {/* Indicatore e Stato */}
          <div className="flex flex-col items-center gap-4">
            <div className="w-5 h-5 border-2 border-gray-100 dark:border-gray-800 border-t-blue-600 dark:border-t-blue-500 rounded-full animate-spin"></div>
            <p className="text-[11px] font-medium tracking-[0.3em] text-gray-400 dark:text-gray-500 uppercase ml-[0.3em]">
              {isDataLoading ? 'Sincronizzazione' : 'Avvio'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Mostra login se l'utente non è autenticato
  if (!user) {
    return <LoginForm />;
  }

  const handleStoreClick = (storeName) => {
    setExpandedStores(prev => {
      if (prev.includes(storeName)) {
        return prev.filter(name => name !== storeName);
      }
      return [...prev, storeName];
    });
  };

  const handleStoreSortChange = (value) => {
    setStoreSortOption(value);
    setShowStoreSortMenu(false);
  };

  // Funzione per mostrare il dettaglio di una transazione
  const handleShowDetail = (item, type) => {
    setSelectedTransaction({ ...item, _type: type });
    setShowTransactionDialog(true);
  };

  // Funzione per attivare modalità esclusiva
  const activateExclusiveMode = (mode) => {
    if (mode === 'rainbow') {
      if (rainbowMode) {
        setRainbowMode(false);
        setPartyMode(false);
        setRetroMode(false);
      } else {
        setRainbowMode(true);
        setPartyMode(false);
        setRetroMode(false);
        activateEasterEgg('tapSegreto', { setRainbowMode });
        saveEasterEggCompletion('tapSegreto', setEasterEggCompleted).then(() => loadEasterEggsWithStatus());
        const easterEgg = getEasterEgg('tapSegreto');
        if (easterEgg) setTimeout(() => showEasterEgg(easterEgg), 350);
      }
    } else if (mode === 'party') {
      if (partyMode) {
        setRainbowMode(false);
        setPartyMode(false);
        setRetroMode(false);
      } else {
        setRainbowMode(false);
        setPartyMode(true);
        setRetroMode(false);
        activateEasterEgg('tapLungo', { setPartyMode });
        saveEasterEggCompletion('tapLungo', setEasterEggCompleted).then(() => loadEasterEggsWithStatus());
        const easterEgg = getEasterEgg('tapLungo');
        if (easterEgg) setTimeout(() => showEasterEgg(easterEgg), 350);
      }
    } else if (mode === 'retro') {
      if (retroMode) {
        setRainbowMode(false);
        setPartyMode(false);
        setRetroMode(false);
      } else {
        setRainbowMode(false);
        setPartyMode(false);
        setRetroMode(true);
        activateEasterEgg('temaSegreto', { setRetroMode });
        saveEasterEggCompletion('temaSegreto', setEasterEggCompleted).then(() => loadEasterEggsWithStatus());
        const easterEgg = getEasterEgg('temaSegreto');
        if (easterEgg) setTimeout(() => showEasterEgg(easterEgg), 350);
      }
    }
    setTimeout(() => {
    }, 200);
  };

  // Utility per device
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // Funzioni per gestire il dialog delle categorie
  const handleCategoryClick = (category, type) => {
    setSelectedCategory({ ...category, type });
    setShowCategoryActions(true);
  };

  const handleEditCategoryFromDialog = () => {
    if (selectedCategory) {
      setCategoryType(selectedCategory.type);
      setEditingCategory(selectedCategory);
      setCategoryFormData({ name: selectedCategory.name, icon: selectedCategory.icon });
      setShowCategoryForm(true);
      setShowCategoryFormFromDialog(true);
      setShowCategoryActions(false);
      setSelectedCategory(null);
    }
  };

  const handleDeleteCategoryFromDialog = () => {
    setShowCategoryActions(false);
    setShowCategoryConfirmDelete(true);
  };

  const confirmDeleteCategoryFromDialog = async () => {
    if (selectedCategory) {
      setDeletingCategory(true);
      try {
        await handleDeleteCategory(selectedCategory.type, selectedCategory.id);
      } catch (e) {
        console.error('Errore durante l\'eliminazione della categoria:', e);
      }
      setDeletingCategory(false);
      setShowCategoryConfirmDelete(false);
      setSelectedCategory(null);
    }
  };

  const closeCategoryActions = () => {
    setShowCategoryActions(false);
    setSelectedCategory(null);
  };

  const closeCategoryConfirmDelete = () => {
    setShowCategoryConfirmDelete(false);
    setSelectedCategory(null);
  };



  // Utility per inferire il tipo da una categoria
  const inferTypeFromCategory = (categoryName) => {
    if (categories.expense.some(c => c.name === categoryName)) return 'expense';
    if (categories.income.some(c => c.name === categoryName)) return 'income';
    return 'expense'; // fallback
  };

  return (
    <div className={`min-h-[100dvh] ${rainbowMode ? 'bg-gradient-to-br from-red-100 via-yellow-100 via-green-100 via-blue-100 via-purple-100 to-pink-100 dark:from-red-900/20 dark:via-yellow-900/20 dark:via-green-900/20 dark:via-blue-900/20 dark:via-purple-900/20 dark:to-pink-900/20' : 'bg-white dark:bg-gray-900'} ${partyMode ? 'party-mode' : ''} ${retroMode ? 'retro-mode' : ''} transition-colors duration-200 pb-10 lg:pb-0 ${isHeaderExpanded ? 'pt-24' : 'pt-20'}`}>
      {/* Header espandibile con grafica trasparente */}
      <header
        className={`fixed top-0 left-0 w-full z-20 py-0 animate-fade-in header-transition header-parallax ${isHeaderExpanded ? 'h-20' : 'h-16'}`}
        style={{ paddingTop: 'var(--safe-area-top)' }}
      >
        <div className="w-full px-6 mt-2 lg:px-8">
          <div className={`${rainbowMode ? 'bg-gradient-to-r from-red-500/50 via-yellow-500/50 via-green-500/50 via-blue-500/50 via-purple-500/50 to-pink-500/50' : 'bg-gradient-to-r from-blue-600/50 to-purple-600/50'} backdrop-blur-md border ${rainbowMode ? 'border-rainbow-500/40' : 'border-blue-500/40'} rounded-3xl transform hover:scale-102 header-content-transition hover:shadow-xl ${rainbowMode ? 'hover:shadow-rainbow-500/20' : 'hover:shadow-blue-500/20'} active:scale-98 ${isHeaderExpanded ? 'p-3' : 'p-2.5'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div
                  className="flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95"
                  {...(isTouchDevice ? { onTouchEnd: handleWalletTap } : { onClick: handleWalletTap })}
                  title={rainbowMode ? "Tap 5 volte rapidamente per disattivare il tema arcobaleno! 🌙" : "Tap 5 volte rapidamente per un easter egg! 🎉"}
                >
                  <img src="/icon-192.png" alt="Logo MoneyTracker" className={`${isHeaderExpanded ? 'w-10 h-10' : 'w-8 h-8'} rounded-[22%] object-contain`} />
                </div>
                <div className={`flex flex-col ${isHeaderExpanded ? 'gap-1' : 'gap-0'}`}>
                  <h1
                    className={`app-title font-bold animate-fade-in-up flex-shrink-0 ${isHeaderExpanded ? 'text-xl' : 'text-lg'} tracking-tight cursor-pointer transition-all duration-100`}
                    style={{
                      color: isLongPressing || longPressProgress > 0 ? `hsl(${longPressProgress * 3.6}, 100%, 70%)` : 'white'
                    }}
                    onTouchStart={handleLongPressStart}
                    onTouchEnd={handleLongPressEnd}
                    onTouchMove={handleLongPressEnd}
                    onMouseDown={handleLongPressStart}
                    onMouseUp={handleLongPressEnd}
                    onMouseLeave={handleLongPressEnd}
                  >
                    MoneyTracker
                  </h1>
                  {isHeaderExpanded && (
                    <p className="text-white/70 text-xs font-medium animate-fade-in-up tracking-wide">
                      {retroMode ? "Gestisci bene le tue finanze" : "Gestisci le tue finanze in modo intelligente"}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setShowUserProfile(true)}
                    className={`${isHeaderExpanded ? 'w-8 h-8' : 'w-7 h-7'} ${easterEggsWithStatus && easterEggsWithStatus.length > 0 && easterEggsWithStatus.every(egg => egg.isCompleted) ? 'avatar-rainbow-star' : 'bg-white/25'} rounded-lg backdrop-blur-sm border border-white/40 flex items-center justify-center hover:bg-white/35 transform hover:scale-105 active:scale-95 cursor-pointer shadow-sm`}
                  >
                    <span className={`text-white font-semibold ${isHeaderExpanded ? 'text-sm' : 'text-xs'}`}>
                      {(() => {
                        const name = user.displayName || user.email;
                        const words = name.split(' ').filter(word => word.length > 0);
                        if (words.length >= 2) {
                          return (words[0][0] + words[1][0]).toUpperCase();
                        } else if (words.length === 1) {
                          return words[0].substring(0, 2).toUpperCase();
                        } else {
                          return name.substring(0, 2).toUpperCase();
                        }
                      })()}
                    </span>
                  </button>
                </div>
                {/* Indicatore di sincronizzazione */}
                <div className="flex items-center gap-1">
                  <div className={`${isHeaderExpanded ? 'w-2 h-2' : 'w-1.5 h-1.5'} rounded-full ${lastSyncTime ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse transition-all duration-200 shadow-sm`} title={lastSyncTime ? `Ultima sincronizzazione: ${new Date(lastSyncTime).toLocaleTimeString()}` : 'Sincronizzazione in corso...'} />
                </div>
                <button
                  onClick={toggleTheme}
                  className={`${isHeaderExpanded ? 'w-8 h-8' : 'w-7 h-7'} bg-white/25 rounded-lg backdrop-blur-sm hover:bg-white/35 transform hover:scale-105 active:scale-95 flex-shrink-0 shadow-sm flex items-center justify-center`}
                >
                  {theme === 'dark' ? <Sun className={`${isHeaderExpanded ? 'w-4 h-4' : 'w-3.5 h-3.5'} text-white`} /> : <Moon className={`${isHeaderExpanded ? 'w-4 h-4' : 'w-3.5 h-3.5'} text-white`} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Layout principale - Responsive */}
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-6rem)] lg:pt-0">
        {/* Zona sinistra - Conti (fissa su tablet/desktop) */}
        <div className="lg:w-96 lg:flex-shrink-0">
          <div className="w-full px-6 mt-4 pb-6 lg:max-w-none lg:px-8 lg:mt-2 lg:pb-0 lg:h-full lg:overflow-y-auto lg:pb-24">
            <div className={`${rainbowMode ? 'bg-gradient-to-r from-red-500/20 via-yellow-500/20 via-green-500/20 via-blue-500/20 via-purple-500/20 to-pink-500/20 border border-rainbow-500/40 rounded-2xl' : 'floating-card'} ${balanceCollapsed ? 'p-3' : 'p-6'} lg:mt-2`}>
              <div
                className="flex items-center justify-between select-none"
                style={{ cursor: 'pointer' }}
                onPointerDown={handleBalanceHeaderPointerDown}
                onPointerUp={handleBalanceHeaderPointerUp}
                onPointerLeave={handleBalanceHeaderPointerLeave}
                onPointerCancel={handleBalanceHeaderPointerLeave}
              >
                <div className="flex items-center gap-3">
                  <button
                    aria-label={showBalance ? "Nascondi saldo" : "Mostra saldo"}
                    onClick={e => { e.stopPropagation(); setShowBalance(v => !v); }}
                    className="focus:outline-none"
                    style={{ background: 'none', border: 'none', padding: 0, margin: 0 }}
                  >
                    {showBalance ? (
                      <Eye className="w-6 h-6 text-blue-600" />
                    ) : (
                      <EyeOff className="w-6 h-6 text-blue-600" />
                    )}
                  </button>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Liquidità</h2>
                </div>
                <div className="flex items-center gap-2">
                  {balanceCollapsed && (
                    <span
                      className="text-lg font-bold text-blue-700 dark:text-blue-300 transition-all duration-200"
                      style={{ filter: showBalance ? 'none' : 'blur(8px)', userSelect: showBalance ? 'auto' : 'none' }}
                    >
                      {formatCurrency(walletsWithCalculatedBalance.filter(w => !['Azioni', 'Obbligazioni', 'ETF'].includes(w.type)).reduce((sum, w) => sum + (w.balance || 0), 0))}
                    </span>
                  )}
                  {/* Freccia solo visiva, non cliccabile */}
                  {balanceCollapsed ? (
                    <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  )}
                </div>
              </div>

              {!balanceCollapsed && (
                <>
                  <div className="text-center mt-4 animate-fade-in-up">
                    <div className="text-4xl font-bold mb-4 text-blue-700 dark:text-blue-300 transition-all duration-200"
                      style={{ filter: showBalance ? 'none' : 'blur(10px)', userSelect: showBalance ? 'auto' : 'none' }}>
                      {formatCurrency(walletsWithCalculatedBalance.filter(w => !['Azioni', 'Obbligazioni', 'ETF'].includes(w.type)).reduce((sum, w) => sum + (w.balance || 0), 0))}
                    </div>
                  </div>
                  {/* Sezione gestione conti */}
                  <div className="mt-8 animate-fade-in-up">
                    {(() => {
                      const walletsWithBalance = walletsWithCalculatedBalance;
                      if (walletsWithBalance.length === 0) {
                        return (
                          <div className="text-center py-4">
                            <div className="flex justify-between items-center mb-3 max-w-xs mx-auto">
                              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Gestione Conti</div>
                              <button
                                onClick={addWallet ? handleAddWallet : undefined}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600/90 backdrop-blur-sm text-white rounded-xl shadow-lg hover:bg-blue-700/90 transition-all duration-200 transform hover:scale-105"
                              >
                                <Wallet className="w-4 h-4" />
                                <span className="font-medium">Nuovo</span>
                              </button>
                            </div>
                            <div className="mb-3">
                              <div className="w-14 h-14 mx-auto bg-muted rounded-full flex items-center justify-center mb-2">
                                <Wallet className="w-7 h-7 text-gray-500 dark:text-gray-400" />
                              </div>
                              <h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Nessun conto salvato
                              </h3>
                              <p className="text-gray-500 dark:text-gray-400 text-sm">
                                Aggiungi un conto per iniziare a tracciare le tue finanze
                              </p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <WalletManager
                          wallets={walletsWithBalance}
                          isCompactMobileList={balanceViewportCategoryRef.current === 'mobile'}
                          onAdd={addWallet}
                          onDelete={deleteWallet}
                          onTransfer={handleTransfer}
                          onShowForm={handleAddWallet}
                          onEditWallet={handleEditWallet}
                          onShowTransferModal={handleShowTransferModal}
                          onWalletClick={handleWalletClick}
                          isSorting={isWalletSortingMode}
                          onToggleSorting={handleToggleWalletSorting}
                          onMoveWallet={handleMoveWallet}
                          onToggleFavorite={handleToggleFavoriteWallet}
                        />
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Zona destra - Contenuto principale (scrollabile su tablet/desktop) */}
        <div className="flex-1 lg:overflow-y-auto lg:pb-20">
          <div className="w-full px-6 py-4 -mt-6 relative z-10 lg:max-w-none lg:px-8 lg:py-0 lg:mt-0 lg:pt-0">

            {/* Content */}
            {activeTab === 'transactions' && (
              <div className="animate-fade-in-up">
                <div
                  ref={transactionsHeaderRef}
                  className={`sticky ${isTransactionsHeaderCollapsed ? 'top-[4.3rem]' : 'top-20'} lg:top-4 z-20 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl w-full px-6 mb-3 lg:mb-8 shadow-lg lg:max-w-none transition-all duration-200 ${isTransactionsHeaderCollapsed ? 'py-2' : 'py-3'}`}
                >
                  {(!isTransactionsHeaderCollapsed || !isMobileViewport) && (
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Transazioni</h2>
                      <div className="flex items-center gap-2">
                        <FilterButton
                          onClick={() => setShowFilterDialog(true)}
                          className="ml-3"
                        >
                          Filtri
                        </FilterButton>
                      </div>
                    </div>
                  )}

                  {/* Bottoni per aggiungere transazioni */}
                  <div className={`flex gap-3 ${isTransactionsHeaderCollapsed ? 'mt-1' : ''}`}>
                    <button
                      onClick={() => handleButtonClick('expense')}
                      onMouseDown={() => handleButtonLongPressStart('expense')}
                      onMouseUp={handleButtonLongPressEnd}
                      onMouseLeave={handleButtonLongPressEnd}
                      onTouchStart={() => handleButtonLongPressStart('expense')}
                      onTouchEnd={handleButtonLongPressEnd}
                      className="flex-1 flex items-center justify-center gap-2 px-2 py-2 sm:px-4 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-red-500 to-red-600 backdrop-blur-sm text-white rounded-xl shadow-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-red-500/25 active:scale-95 font-medium"
                    >
                      <TrendingDown className="w-4 h-4" />
                      <span>Nuova Spesa</span>
                    </button>
                    <button
                      onClick={() => handleButtonClick('income')}
                      onMouseDown={() => handleButtonLongPressStart('income')}
                      onMouseUp={handleButtonLongPressEnd}
                      onMouseLeave={handleButtonLongPressEnd}
                      onTouchStart={() => handleButtonLongPressStart('income')}
                      onTouchEnd={handleButtonLongPressEnd}
                      className="flex-1 flex items-center justify-center gap-2 px-2 py-2 sm:px-4 sm:py-3 text-sm sm:text-base bg-gradient-to-r from-green-500 to-green-600 backdrop-blur-sm text-white rounded-xl shadow-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-green-500/25 active:scale-95 font-medium"
                    >
                      <TrendingUp className="w-4 h-4" />
                      <span>Nuova Entrata</span>
                    </button>
                  </div>
                </div>

                {isSelectionModeActive && (
                  <div
                    ref={selectionBarRef}
                    className="sticky top-32 z-30 bg-blue-50/80 dark:bg-blue-900/30 border border-blue-200/60 dark:border-blue-700/50 rounded-2xl px-4 py-3 mb-4 shadow-lg backdrop-blur-md"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-lg font-bold text-blue-700 dark:text-blue-200 min-w-[2rem] text-center">
                        {selectedTransactionCount}
                      </span>
                      <div className="flex flex-nowrap items-center justify-end gap-2 overflow-x-auto no-scrollbar scroll-smooth">
                        <button
                          type="button"
                          onClick={clearSelectedTransactions}
                          className="p-2 text-sm font-medium rounded-lg bg-white/70 dark:bg-gray-800/60 text-blue-700 dark:text-blue-200 border border-blue-200/60 dark:border-blue-800/60 hover:bg-white dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
                          title="Annulla selezione"
                        >
                          <X className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={handleBulkDuplicateRequest}
                          className="p-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white shadow hover:bg-emerald-700 transition-colors whitespace-nowrap"
                          title="Duplica selezionati"
                        >
                          <Copy className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={handleBulkEditRequest}
                          className="p-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white shadow hover:bg-indigo-700 transition-colors whitespace-nowrap"
                          title="Modifica selezionati"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={handleBulkDeleteRequest}
                          className="p-2 text-sm font-semibold rounded-lg bg-red-600 text-white shadow hover:bg-red-700 transition-colors whitespace-nowrap"
                          title="Elimina selezionati"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <ExpenseList
                  items={visibleTransactions}
                  onDelete={confirmDelete}
                  onEdit={handleEdit}
                  type="mixed"
                  categories={categories}
                  onShowDetail={handleShowDetail}
                  selectionMode={isSelectionModeActive}
                  selectedTransactionKeys={selectedTransactionKeys}
                  onSelectionStart={handleSelectionStart}
                  onToggleSelection={handleSelectionToggle}
                  dateTileOffset={dateTileOffset}
                  onLoadMore={handleLoadMoreTransactions}
                  hasMore={hasMoreTransactions}
                />
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="animate-fade-in-up">
                <div className="sticky top-20 lg:top-4 z-20 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl w-full px-6 py-3 mb-3 shadow-lg lg:max-w-none">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Statistiche</h2>
                    <button
                      onClick={() => setShowFilterDialog(true)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${hasHighlightedFilters
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                      <Filter className="w-4 h-4" />
                      Filtri
                      {hasHighlightedFilters && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </button>
                  </div>
                </div>
                <div className="mt-2 lg:mt-8">
                  <Statistics
                    expenses={expenses}
                    incomes={incomes}
                    categories={categories}
                    activeFilters={activeFilters}
                    isMobile={isMobileViewport}
                    recurringTransactions={recurringTransactions}
                    statisticsPreferences={statisticsPreferences}
                    onStatisticsPreferencesChange={updateStatisticsPreferences}
                    stores={stores}
                    wallets={walletsWithCalculatedBalance}
                    debts={debts}
                    storesData={stores}
                    categoriesData={categories}
                  />
                </div>
              </div>

            )}

            {activeTab === 'categories' && (
              <div className="animate-fade-in-up">
                <div className="sticky top-20 lg:top-4 z-20 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl w-full px-6 py-3 mb-3 shadow-lg lg:max-w-none">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Categorie</h2>
                </div>
                <div className="space-y-8">
                  <CategoryManager
                    categories={categories.expense}
                    onAddCategory={(cat) => handleAddCategory('expense', cat)}
                    onEditCategory={(id, fields) => handleEditCategory('expense', id, fields)}
                    onDeleteCategory={(id) => handleDeleteCategory('expense', id)}
                    type="expense"
                    onShowForm={handleShowCategoryForm}
                    onCategoryClick={handleCategoryClick}
                  />
                  <CategoryManager
                    categories={categories.income}
                    onAddCategory={(cat) => handleAddCategory('income', cat)}
                    onEditCategory={(id, fields) => handleEditCategory('income', id, fields)}
                    onDeleteCategory={(id) => handleDeleteCategory('income', id)}
                    type="income"
                    onShowForm={handleShowCategoryForm}
                    onCategoryClick={handleCategoryClick}
                  />
                  <CategoryManager
                    categories={categories.wallet}
                    onAddCategory={(cat) => handleAddCategory('wallet', cat)}
                    onEditCategory={(id, fields) => handleEditCategory('wallet', id, fields)}
                    onDeleteCategory={(id) => handleDeleteCategory('wallet', id)}
                    type="wallet"
                    onShowForm={handleShowCategoryForm}
                    onCategoryClick={handleCategoryClick}
                  />
                </div>
              </div>
            )}

            {activeTab === 'investments' && (
              <div className="animate-fade-in-up">
                <div className="sticky top-20 lg:top-4 z-20 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl w-full px-6 py-3 mb-3 shadow-lg lg:max-w-none">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    Investimenti
                  </h2>
                </div>
                <InvestmentDashboard
                  wallets={walletsWithCalculatedBalance}
                  expenses={expenses}
                  incomes={incomes}
                  debts={debts}
                  investmentPlans={investmentPlansData || []}
                  addDocument={addDocument}
                  updateDocument={updateDocument}
                  deleteDocument={deleteDocument}
                  userSettings={userSettings}
                  onUpdateSettings={handleUpdateSettings}
                />
              </div>
            )}

            {activeTab === 'debts' && (
              <div className="animate-fade-in-up">
                <DebtList
                  debts={debts}
                  onPersonClick={handlePersonClick}
                  onAddDebt={handleAddDebt}
                  onAddCredit={handleAddCredit}
                  onEditDebt={handleEditDebt}
                  onDeleteDebt={deleteDebt}
                  onShowDetail={handleShowDetail}
                />
              </div>
            )}

            {activeTab === 'data' && (
              <div className="animate-fade-in-up">
                <div className="sticky top-20 lg:top-4 z-20 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl w-full px-6 py-3 mb-3 shadow-lg lg:max-w-none">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestione Dati</h2>
                </div>
                <DataManager
                  onExportData={exportData}
                  onExportStats={handleExportStats}
                  onResetData={resetAllData}
                  onShowImportModal={() => setShowImportModal(true)}
                  onRecoverCategories={handleRecoverCategories}
                />
              </div>
            )}

            {activeTab === 'stores' && (
              <div className="animate-fade-in-up">
                {/* Titolo sticky e bottoni azione, con controlli compatti */}
                <div className="sticky top-20 lg:top-4 z-20 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl w-full px-6 py-3 mb-3 lg:mb-7 shadow-lg flex items-center justify-between gap-3 lg:max-w-none">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">Negozi</h2>
                  <div className="flex items-center gap-2">
                    <div className="relative" ref={storeSortMenuRef}>
                      <button
                        type="button"
                        className={`p-1.5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ${showStoreSortMenu
                          ? 'bg-gray-200 dark:bg-gray-700'
                          : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                          }`}
                        onClick={() => setShowStoreSortMenu(prev => !prev)}
                        aria-label={`Ordina negozi - ${selectedStoreSort?.label || 'Seleziona ordinamento'}`}
                        aria-haspopup="menu"
                        aria-expanded={showStoreSortMenu}
                        title={`Ordinamento: ${selectedStoreSort?.label || 'N/D'}`}
                      >
                        <ArrowUpDown className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                      </button>
                      {showStoreSortMenu && (
                        <div
                          className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xl p-2 z-30"
                          role="menu"
                          aria-label="Opzioni ordinamento negozi"
                        >
                          <div className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Ordina per
                          </div>
                          <ul className="space-y-1 max-h-80 overflow-y-auto">
                            {STORE_SORT_OPTIONS.map(option => {
                              const isActive = option.value === storeSortOption;
                              return (
                                <li key={option.value}>
                                  <button
                                    type="button"
                                    onClick={() => handleStoreSortChange(option.value)}
                                    className={`w-full text-left px-3 py-2 rounded-xl transition-colors ${isActive
                                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-200'
                                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200'
                                      }`}
                                  >
                                    <div className="flex items-center justify-between text-sm font-medium">
                                      <span>{option.label}</span>
                                      {isActive && <Check className="w-4 h-4 text-blue-600 dark:text-blue-300" />}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {option.description}
                                    </p>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                      onClick={() => setShowFilterDialog(true)}
                      aria-label="Filtra negozi"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707l-6.414 6.414A2 2 0 0013 14.586V19a1 1 0 01-1.447.894l-2-1A1 1 0 009 18v-3.414a2 2 0 00-.586-1.414L2 6.707A1 1 0 012 6V4z" />
                      </svg>
                    </button>
                  </div>
                </div>
                {/* Filtro come nelle altre sezioni */}
                <div className="w-full mb-2 lg:max-w-none">
                  <FilterDialog
                    filters={activeFilters}
                    setFilters={setActiveFilters}
                    categories={categories}
                    wallets={walletsForDisplay}
                    stores={[]}
                    isOpen={showFilterDialog}
                    setIsOpen={setShowFilterDialog}
                    showStoreFilter={false}
                  />
                </div>
                {/* Usa transazioni già filtrate per negozi e saldi, compreso il filtro Tutte/Spese/Entrate */}
                {(() => {
                  if (storeEntries.length === 0) {
                    return (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                        Nessun negozio trovato nelle transazioni
                      </div>
                    );
                  }
                  return (
                    <ul className="space-y-3 mt-4 lg:mt-10">
                      {storeEntries.map(({ storeName, transactions, netTotal }) => {
                        const isExpanded = expandedStores.includes(storeName);
                        return (
                          <li key={storeName} className="w-full">
                            <button
                              type="button"
                              onClick={() => handleStoreClick(storeName)}
                              className={`card py-2 px-4 w-full flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 rounded-2xl shadow transition-all lg:max-w-none text-left focus:outline-none focus:ring-2 focus:ring-blue-400 ${isExpanded ? 'ring-2 ring-blue-400/80' : ''
                                }`}
                              title={`Mostra le transazioni per ${storeName}`}
                              aria-expanded={isExpanded}
                            >
                              <span className="font-medium text-gray-900 dark:text-gray-100 truncate text-base">{storeName}</span>
                              <div className="flex items-center gap-3">
                                <span className={`inline-block min-w-[90px] text-center px-3 py-1 rounded-lg font-semibold text-sm ${netTotal > 0
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                  : netTotal < 0
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-300'
                                  }`}>
                                  {netTotal.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 })}
                                </span>
                                <ChevronDown
                                  className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                  aria-hidden="true"
                                />
                              </div>
                            </button>
                            {isExpanded && (
                              <StoreTransactionsPanel
                                transactions={transactions}
                                onSelectTransaction={(transaction) => handleShowDetail(transaction, transaction._type || 'expense')}
                              />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs fluttuante in basso */}
      <div
        className={`fixed left-0 w-full z-30 py-3 transition-all duration-300 ${showFloatingMenu ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-full pointer-events-none'} lg:left-8 lg:right-auto lg:w-80 bottom-[calc(var(--safe-area-bottom,0px)+0.75rem)] lg:bottom-10`}
      >
        <div className="max-w-md mx-auto px-6 lg:max-w-none lg:px-0">
          <div className={`${rainbowMode ? 'bg-gradient-to-r from-red-500/30 via-yellow-500/30 via-green-500/30 via-blue-500/30 via-purple-500/30 to-pink-500/30 backdrop-blur-md border border-rainbow-500/40 rounded-2xl' : 'glass-card'} p-2 lg:rounded-xl`}>
            <div className="grid grid-cols-5 gap-2 lg:flex lg:flex-row lg:gap-1 lg:w-full">
              <button
                onClick={() => setActiveTab('transactions')}
                className={`py-4 px-2 text-sm font-semibold rounded-xl transition-all duration-300 lg:py-2 lg:px-2 lg:flex-1 ${activeTab === 'transactions'
                  ? `${rainbowMode ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg' : 'tab-active'}`
                  : 'tab-inactive'
                  }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="hidden sm:inline lg:hidden">Trans.</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('stores')}
                className={`py-4 px-2 text-sm font-semibold rounded-xl transition-all duration-300 lg:py-2 lg:px-2 lg:flex-1 ${activeTab === 'stores'
                  ? `${rainbowMode ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg' : 'tab-active'}`
                  : 'tab-inactive'
                  }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <Store className="w-4 h-4" />
                  <span className="hidden sm:inline lg:hidden">Negozi</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-4 px-2 text-sm font-semibold rounded-xl transition-all duration-300 lg:py-2 lg:px-2 lg:flex-1 ${activeTab === 'stats'
                  ? `${rainbowMode ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg' : 'tab-active'}`
                  : 'tab-inactive'
                  }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  <span className="hidden sm:inline lg:hidden">Stats</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('investments')}
                className={`py-4 px-2 text-sm font-semibold rounded-xl transition-all duration-300 lg:py-2 lg:px-2 lg:flex-1 ${activeTab === 'investments'
                  ? `${rainbowMode ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg' : 'tab-active'}`
                  : 'tab-inactive'
                  }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="hidden sm:inline lg:hidden">Invest.</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('debts')}
                className={`py-4 px-2 text-sm font-semibold rounded-xl transition-all duration-300 lg:py-2 lg:px-2 lg:flex-1 ${activeTab === 'debts'
                  ? `${rainbowMode ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg' : 'tab-active'}`
                  : 'tab-inactive'
                  }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline lg:hidden">Debiti</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Form */}
      {
        showForm && (
          (() => {
            return (
              <ExpenseForm
                onSubmit={editingItem ? updateItem : (transactionType === 'expense' ? addExpense : addIncome)}
                onClose={() => {
                  setShowForm(false);
                  setEditingItem(null);

                  // Se veniamo da un modello, riapri il dialog dei modelli
                  if (modelFormData) {
                    setModelFormData(null);
                    setShowModelsDialog(true);
                  }
                }}
                type={transactionType}
                editingItem={modelFormData ? null : editingItem}
                modelFormData={modelFormData}
                stores={stores}
                categories={transactionType === 'expense' ? categories.expense : categories.income}
                wallets={wallets}
                selectedWalletId={preferredWalletId}
                onAddStore={addStore}
                existingPersons={uniquePersons}
              />
            );
          })()
        )
      }

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={handleDelete}
        title={deleteDialogTitle}
        message={deleteDialogMessage}
      />

      <BulkEditDialog
        isOpen={showBulkEditDialog}
        onClose={() => setShowBulkEditDialog(false)}
        onApply={handleBulkEditApply}
        selectedCount={selectedTransactionCount}
        categories={bulkEditCategoryOptions}
        wallets={wallets}
        existingPersons={uniquePersons}
      />

      {/* User Profile Modal */}
      <UserProfile
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        easterEggsWithStatus={easterEggsWithStatus}
        onExportData={exportData}
        onExportStats={handleExportStats}
        onImportData={importUserData}
        onResetData={resetAllData}
        onShowImportModal={() => setShowImportModal(true)}
        userSettings={userSettings}
        onUpdateSettings={handleUpdateSettings}
        stores={stores}
        onAddStore={addStore}
        existingPersons={uniquePersons}
        categoriesFallback={categories}
        onRecoverCategories={handleRecoverCategories}
        onOpenCategories={() => {
          setActiveTab('categories');
          setShowUserProfile(false);
        }}
      />

      {/* Wallet Manager Modal */}
      {
        showWalletForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" onClick={() => {
            setShowWalletForm(false);
            setEditingWallet(null);
            setWalletFormData({ name: '', color: WALLET_COLORS[0], balance: '', type: getDefaultWalletType(), grossAnnualRate: '', isinCode: '', isinName: '', expectedRate: '' });
            setTickerSearchError(null);
          }}>
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col transform transition-all duration-300 border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
              <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl shrink-0">
                <div className="flex items-center justify-between">
                  <button onClick={() => {
                    setShowWalletForm(false);
                    setEditingWallet(null);
                    setWalletFormData({ name: '', color: WALLET_COLORS[0], balance: '', type: getDefaultWalletType(), grossAnnualRate: '', isinCode: '', isinName: '', expectedRate: '' });
                    setTickerSearchError(null);
                  }} className="text-white/80 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                  <h2 className="text-xl font-bold">{editingWallet ? 'Modifica' : 'Nuovo'} Conto</h2>
                  <div className="w-6"></div>
                </div>
              </div>
              <form onSubmit={handleWalletSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Nome</label>
                  <input type="text" value={walletFormData.name} onChange={e => setWalletFormData({ ...walletFormData, name: e.target.value })} placeholder="Nome conto" className="input" required />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Colore</label>
                  <div className="flex gap-2 flex-wrap">
                    {WALLET_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => {
                          if (color === 'rainbow') {
                            setShowColorPicker(true);
                          } else {
                            setWalletFormData({ ...walletFormData, color });
                          }
                        }}
                        className={`w-8 h-8 rounded-full border-2 ${walletFormData.color === color ? 'border-primary scale-110' : 'border-transparent'} transition-all flex items-center justify-center`}
                        style={{
                          background: color === 'rainbow'
                            ? 'white'
                            : color
                        }}
                      >
                        {color === 'rainbow' && <Palette className="w-4 h-4 text-gray-600" />}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Tipologia</label>
                  <CustomSelect
                    value={walletFormData.type}
                    onChange={(value) => setWalletFormData(prev => ({ ...prev, type: value }))}
                    options={walletCategoryOptions.length > 0 ? walletCategoryOptions : [{ value: '', label: 'Nessuna categoria conto disponibile' }]}
                    placeholder="Seleziona tipologia"
                    required
                    disabled={walletCategoryOptions.length === 0}
                  />
                  {walletCategoryOptions.length === 0 && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      Aggiungi una categoria conto per poter creare un nuovo conto.
                    </p>
                  )}
                </div>

                {/* Sezione Autocomplete Ticker/ISIN per ETF, Azioni, Obbligazioni */}
                {(['etf', 'azione', 'azioni', 'obbligazione', 'obbligazioni'].some(t => walletFormData.type?.toLowerCase().includes(t))) && (
                  <div className="space-y-3 border-l-2 border-blue-500 pl-3 py-1 bg-blue-50/30 dark:bg-blue-900/10 rounded-r-xl p-2.5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1.5 flex items-center justify-between">
                        <span>Codice / Ticker (es: AAPL, SWDA.MI)</span>
                        {isTickerSearching && <span className="text-xs text-blue-500 animate-pulse">Recupero titolo...</span>}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Es: AAPL, SWDA.MI, BTP..."
                          value={walletFormData.isinCode || ''}
                          onChange={(e) => setWalletFormData({ ...walletFormData, isinCode: e.target.value.toUpperCase() })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.stopPropagation();
                              searchTicker(walletFormData.isinCode);
                            }
                          }}
                          onBlur={() => {
                            if (walletFormData.isinCode && walletFormData.isinCode.trim().length >= 2) {
                              searchTicker(walletFormData.isinCode);
                            }
                          }}
                          className="input flex-1 font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (walletFormData.isinCode) {
                              searchTicker(walletFormData.isinCode);
                            }
                          }}
                          disabled={isTickerSearching || !walletFormData.isinCode || walletFormData.isinCode.trim().length < 2}
                          className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-semibold shadow-sm hover:shadow transition-all text-sm shrink-0 flex items-center justify-center"
                        >
                          Recupera Titolo
                        </button>
                      </div>
                      
                      {/* Messaggio di Errore o Avviso */}
                      {tickerSearchError && (
                        <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400 font-medium leading-relaxed">
                          {tickerSearchError}
                        </p>
                      )}
                      
                      <div className="mt-3">
                        <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1.5">
                          Nome del titolo
                        </label>
                        <input
                          type="text"
                          placeholder="Es: Apple Inc., Vanguard FTSE All-World..."
                          value={walletFormData.isinName || ''}
                          onChange={(e) => setWalletFormData({ ...walletFormData, isinName: e.target.value })}
                          className="input"
                        />
                      </div>

                      <div className="mt-3">
                        <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1.5">
                          Rendimento atteso annuo (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Es: 7.5"
                          value={walletFormData.expectedRate || ''}
                          onChange={(e) => setWalletFormData({ ...walletFormData, expectedRate: e.target.value })}
                          className="input"
                        />
                        <p className="mt-1 text-xs text-gray-400">Usato come tasso nella simulazione investimenti (sostituisce il valore default)</p>
                      </div>

                      <p className="mt-2.5 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        L'inserimento del codice ticker consente di recuperare in automatico il nome formale del titolo e abilitare i grafici futuri.
                      </p>
                    </div>
                  </div>
                )}

                {/* Sezione Tasso Lordo per Conto Deposito */}
                {walletFormData.type?.toLowerCase().includes('deposito') && (
                  <div className="form-group border-l-2 border-green-500 pl-3 py-1 bg-green-50/30 dark:bg-green-900/10 rounded-r-xl p-2.5">
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1.5">Tasso Lordo Annuo (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={walletFormData.grossAnnualRate || ''}
                      onChange={e => setWalletFormData({ ...walletFormData, grossAnnualRate: e.target.value })}
                      placeholder="Es: 3.5"
                      className="input"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Saldo iniziale</label>
                  <div className="form-input-group">
                    <Euro className="form-input-icon" />
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formatMoneyInput(walletFormData.balance)}
                      onChange={(e) => setWalletFormData({ ...walletFormData, balance: extractMoneyDigits(e.target.value) })}
                      className="input form-input-with-icon"
                      autoComplete="off"
                      required
                      placeholder={editingWallet ? formatMoneyInput(amountToMoneyDigits(editingWallet.initialBalance ?? editingWallet.balance ?? 0)) || '0,00' : '0,00'}
                    />
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={() => {
                    setShowWalletForm(false);
                    setEditingWallet(null);
                    setWalletFormData({ name: '', color: WALLET_COLORS[0], balance: '', type: getDefaultWalletType(), grossAnnualRate: '', isinCode: '', isinName: '', expectedRate: '' });
                    setTickerSearchError(null);
                  }} className="btn btn-secondary flex-1">Annulla</button>
                  <button type="submit" disabled={walletCategoryOptions.length === 0} className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600">{editingWallet ? 'Modifica' : 'Nuovo'}</button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Transfer Modal */}
      {
        showTransferModal && (
          <>
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" onClick={closeTransferModal}>
              <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md transform transition-all duration-300 border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
                <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl">
                  <div className="flex items-center justify-between">
                    <button onClick={closeTransferModal} className="text-white/80 hover:text-white transition-colors">
                      <X className="w-6 h-6" />
                    </button>
                    <h2 className="text-xl font-bold">Trasferimento tra Conti</h2>
                    <div className="w-6"></div>
                  </div>
                </div>
                <form onSubmit={handleTransferSubmit} className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Conto di origine</label>
                    <CustomSelect
                      value={transferFormData.fromWalletId}
                      onChange={(value) => setTransferFormData(prev => ({ ...prev, fromWalletId: value }))}
                      options={[
                        { value: '', label: 'Seleziona conto di origine' },
                        ...walletsWithCalculatedBalance.map(wallet => ({
                          value: wallet.id,
                          label: wallet.name,
                          subtitle: formatCurrency(wallet.balance),
                          icon: (
                            <div
                              className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                              style={{ backgroundColor: wallet.color }}
                            />
                          )
                        }))
                      ]}
                      required
                    />
                  </div>
                  <div className="flex justify-center">
                    <ArrowDown className="w-6 h-6 text-gray-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Conto di destinazione</label>
                    <CustomSelect
                      value={transferFormData.toWalletId}
                      onChange={(value) => setTransferFormData(prev => ({ ...prev, toWalletId: value }))}
                      options={[
                        { value: '', label: 'Seleziona conto di destinazione' },
                        ...walletsWithCalculatedBalance.map(wallet => ({
                          value: wallet.id,
                          label: wallet.name,
                          subtitle: formatCurrency(wallet.balance),
                          icon: (
                            <div
                              className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                              style={{ backgroundColor: wallet.color }}
                            />
                          )
                        }))
                      ]}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Importo</label>
                    <div className="form-input-group">
                      <Euro className="form-input-icon" />
                      <input
                        type="text"
                        inputMode={isMobileViewport ? 'none' : 'decimal'}
                        name="amount"
                        value={formatMoneyInput(transferFormData.amount)}
                        onChange={handleTransferChange}
                        onFocus={handleTransferAmountFocus}
                        onClick={handleTransferAmountFocus}
                        onTouchEnd={handleTransferAmountFocus}
                        readOnly={isMobileViewport}
                        placeholder="0,00"
                        autoComplete="off"
                        className="input form-input-with-icon"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button type="button" onClick={closeTransferModal} className="btn btn-secondary flex-1">Annulla</button>
                    <button type="submit" disabled={!transferFormData.fromWalletId || !transferFormData.toWalletId || !transferFormData.amount || transferFormData.fromWalletId === transferFormData.toWalletId} className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1">Trasferisci</button>
                  </div>
                </form>
              </div>
            </div>
            <MobileAmountKeypad
              isOpen={showTransferKeypad && isMobileViewport}
              value={transferFormData.amount}
              onChange={handleTransferAmountChange}
              onClose={() => setShowTransferKeypad(false)}
            />
          </>
        )
      }

      {/* Color Picker Modal */}
      {
        showColorPicker && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" onClick={() => setShowColorPicker(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-sm transform transition-all duration-300 border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
              <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <button onClick={() => setShowColorPicker(false)} className="text-white/80 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                  <h2 className="text-xl font-bold">Scegli Colore</h2>
                  <div className="w-6"></div>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Colore personalizzato</label>
                  <input
                    type="color"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    className="w-full h-12 rounded-lg border-2 border-gray-200 dark:border-gray-600 cursor-pointer"
                  />
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={() => setShowColorPicker(false)} className="btn btn-secondary flex-1">Annulla</button>
                  <button
                    type="button"
                    onClick={() => {
                      setWalletFormData({ ...walletFormData, color: customColor });
                      setShowColorPicker(false);
                    }}
                    className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1"
                  >
                    Conferma
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Category Form Modal */}
      {
        showCategoryForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" onClick={handleCategoryCancel}>
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md transform transition-all duration-300 border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
              <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <button onClick={handleCategoryCancel} className="text-white/80 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                  <h2 className="text-xl font-bold">
                    {editingCategory ? 'Modifica' : 'Nuovo'} Categoria
                  </h2>
                  <div className="w-6"></div>
                </div>
              </div>

              <form onSubmit={handleCategorySubmit} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Nome Categoria
                  </label>
                  <input
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                    placeholder="Nome categoria"
                    className="input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Icona
                  </label>

                  {/* Selezione o digitazione libera dell'emoji */}
                  <div className="flex gap-3 mb-4">
                    <input
                      type="text"
                      maxLength={4}
                      value={categoryFormData.icon}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, icon: e.target.value })}
                      placeholder="Digita o incolla un'emoji..."
                      className="flex-1 p-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      title="Digita liberamente l'emoji dalla tastiera"
                    />
                    <div className="w-11 h-11 flex items-center justify-center text-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm">
                      {categoryFormData.icon || '📦'}
                    </div>
                  </div>

                  <div className="relative">
                    <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto scrollbar-hide emoji-scroll-container">
                      {[
                        // Cibo e bevande
                        '🍽️', '🍔', '🍕', '🍣', '🍦', '🍰', '🍩', '🍪', '🍫', '🍬', '🍭', '🍮', '🍯', '🍼', '🥛', '☕', '🍵', '🍶', '🍷', '🍸', '🍹', '🍺', '🍻', '🥂', '🥃',
                        // Trasporti (senza duplicati)
                        '🚗', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🛴', '🚲', '🛵', '🏍️', '🚨', '🚔', '🚍', '🚘', '🚖', '🚡', '🚠', '🚟', '🚃',
                        // Shopping e acquisti
                        '🛍️', '🛒', '💄', '👗', '👠', '👡', '👢', '👕', '👖', '🧥', '🧦', '🧤', '🧣', '👒', '🎩', '👑', '💍', '💎', '💐', '🌹', '🌷', '🌼', '🌻', '🌺',
                        // Tecnologia
                        '💻', '🖥️', '⌨️', '🖱️', '📱', '📲', '📟', '📠', '🔋', '💡', '🔌', '🖨️', '📷', '📹', '🎥', '📺', '📻', '🎙️', '🎚️', '🎛️',
                        // Casa e famiglia
                        '🏠', '🏡', '🏘️', '🏚️', '🏗️', '🏭', '🏢', '🏬', '🏣', '🏤', '🏥', '🏨', '🏪', '🏫', '🏩', '💒', '⛪', '🕌', '🕍', '🛕', '⛩️', '🕋', '⛲', '⛺', '🌁',
                        // Sport e attività
                        '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🎯', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽',
                        // Musica e intrattenimento (senza duplicati)
                        '🎸', '🎤', '🎧', '🎼', '🎹', '🥁', '🪘', '🎷', '🎺', '🎻', '🪕', '🎬', '🎭', '🎨', '🎪', '🎟️', '🎫', '🎗️', '🎖️', '🏆', '🏅', '🥇', '🥈', '🥉', '⚱️',
                        // Animali e natura
                        '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤', '🐣', '🦆', '🦅', '🦉', '🦇', '🐺',
                        // Viaggi e luoghi (senza duplicati)
                        '✈️', '🛩️', '🛫', '🛬', '🛰️', '🚀', '🛸', '🚁', '🚂', '🚄', '🚅', '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🚋',
                        // Salute e benessere (senza duplicati)
                        '💊', '💉', '🩺', '🩹', '🩻', '🩼', '🩽', '🩾', '🩿', '🪑', '🪒', '🧴', '🧼', '🛁', '🚿', '🪣', '🧽', '🪞', '🪟', '🪠', '🪡', '🪢', '🪤',
                        // Lavoro e ufficio (senza duplicati)
                        '💼', '📁', '📂', '📄', '📃', '📑', '📋', '📝', '✏️', '🖊️', '🖋️', '✒️', '🖌️', '🖍️', '📏', '📐', '✂️', '🗃️', '🗄️', '🗑️', '🔒', '🔓', '🔏', '🔐', '🔑',
                        // Finanza e denaro (senza duplicati)
                        '💰', '🪙', '💴', '💵', '💶', '💷', '💳', '🧾', '💸',
                        // Educazione e studio (senza duplicati)
                        '📚', '📖', '📕', '📗', '📘', '📙', '📓', '📔', '📒', '🔖', '🏷️', '📎', '🖇️',
                        // Tempo libero e hobby
                        '🎮', '🕹️', '🎲', '🧩'
                      ].map((icon) => (
                        <button
                          key={icon}
                          type="button"
                          onClick={() => setCategoryFormData({ ...categoryFormData, icon })}
                          className={`p-2 rounded-lg text-lg transition-all emoji-scroll-item ${categoryFormData.icon === icon
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-secondary hover:bg-gray-100 dark:hover:bg-gray-700/80'
                            }`}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none"></div>
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={handleCategoryCancel} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200">
                    Annulla
                  </button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-purple-600/90 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-purple-700/90 transition-all duration-200 transform hover:scale-105">
                    {editingCategory ? 'Modifica' : 'Nuovo'}
                  </button>
                </div>

                {/* Pulsante elimina solo in modifica e non dal dialog */}
                {editingCategory && !showCategoryFormFromDialog && (
                  <div className="pt-2">
                    <button
                      type="button"
                      className="w-full px-4 py-2 bg-red-600/90 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-red-700/90 transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2"
                      onClick={handleCategoryDelete}
                    >
                      <Trash2 className="w-4 h-4" />
                      Elimina categoria
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        )
      }

      {/* Import Modal */}
      {
        showImportModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" onClick={() => setShowImportModal(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md transform transition-all duration-300 border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
              <div className="bg-blue-600/90 backdrop-blur-sm text-white p-4 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <button onClick={() => setShowImportModal(false)} className="text-white/80 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                  </button>
                  <h2 className="text-xl font-bold">Importa Dati</h2>
                  <div className="w-6"></div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Seleziona File di Backup
                  </label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) {
                        setImportFile(null);
                        return;
                      }

                      if (file.type && file.type !== 'application/json' && file.type !== 'text/json') {
                        showError('Seleziona un file JSON valido.');
                        e.target.value = '';
                        setImportFile(null);
                        return;
                      }

                      setImportFile(file);
                    }}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowImportModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200"
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={handleImportFileSubmit}
                    disabled={!importFile || isImportingData}
                    className="flex-1 px-4 py-2 bg-green-600/90 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-green-700/90 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                  >
                    {isImportingData ? (
                      <span className="font-medium">Import in corso...</span>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        <span className="font-medium">Importa</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* Filter Dialog */}
      <FilterDialog
        isOpen={showFilterDialog}
        onClose={() => setShowFilterDialog(false)}
        onApplyFilters={applyFilters}
        categories={categories}
        activeTab={activeTab}
        stores={stores}
        wallets={walletsForDisplay}
        currentFilters={activeFilters}
      />

      {/* Footer con credits - Mobile */}
      {/* Footer con credits */}
      {/* Footer con credits */}
      <footer
        className={`fixed bottom-0 left-0 w-full z-40 pt-[2px] pb-0 cursor-pointer hover:bg-opacity-90 transition-all duration-200 ${rainbowMode ? 'bg-gradient-to-r from-red-500/80 via-yellow-500/80 via-green-500/80 via-blue-500/80 via-purple-500/80 to-pink-500/80 dark:from-red-900/80 dark:via-yellow-900/80 dark:via-blue-900/80 dark:via-purple-900/80 dark:to-pink-900/80' : 'bg-white/80 dark:bg-gray-900/80'} backdrop-blur-md border-t ${rainbowMode ? 'border-rainbow-500/40' : 'border-gray-200 dark:border-gray-700'}`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) * 0.6)' }}
        onClick={handleFooterTap}
      >
        <div className="max-w-md mx-auto px-6 lg:max-w-none lg:px-8 text-center">
          <button
            onClick={() => setShowDevModal(true)}
            className="bg-transparent border-none text-gray-500 dark:text-gray-400 font-mono text-xs cursor-pointer tracking-widest opacity-60 hover:opacity-100 transition-opacity duration-200"
          >
            ALX STUDIO
          </button>
        </div>
      </footer>

      {showDevModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDevModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-[90%] max-w-[350px] p-8 text-center border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mt-0 text-xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
              Sviluppo
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              Questa app è stata sviluppata da<br />
              <strong className="text-blue-600 dark:text-blue-400 font-mono text-lg block mt-1">ALX Studio</strong>
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.open('https://alx-studio.netlify.app', '_blank')}
                className="py-3 px-6 rounded-xl border-none bg-blue-600 text-white cursor-pointer text-base font-semibold w-full hover:bg-blue-700 transition-colors shadow-lg hover:shadow-blue-500/30"
              >
                Apri sito web
              </button>
              <button
                onClick={() => setShowDevModal(false)}
                className="py-3 px-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-transparent text-gray-900 dark:text-gray-100 cursor-pointer text-base font-medium w-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {
        showTransactionDialog && selectedTransaction && (
          <TransactionDetailDialog
            transaction={selectedTransaction}
            onClose={() => setShowTransactionDialog(false)}
            onEdit={() => { handleEdit(selectedTransaction); setShowTransactionDialog(false); }}
            onDelete={() => { confirmDelete(selectedTransaction); setShowTransactionDialog(false); }}
            categories={categories}
            wallets={walletsForDisplay}
          />
        )
      }

      {
        showDebtTransactionDialog && selectedDebtTransaction && (
          <DebtTransactionDetailDialog
            transaction={selectedDebtTransaction}
            onClose={() => setShowDebtTransactionDialog(false)}
            onEdit={handleEditDebt}
            onDelete={deleteDebt}
            wallets={walletsForDisplay}
          />
        )
      }

      {/* Modal Azioni Wallet */}
      {
        showWalletActions && selectedWallet && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999]" onClick={closeWalletActions}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xs p-6 border border-gray-200 dark:border-gray-700 relative" onClick={e => e.stopPropagation()}>
              <button onClick={closeWalletActions} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
              <div className="flex flex-col items-center gap-2 mb-6">
                <span className="w-10 h-10 rounded-full" style={{ background: selectedWallet.color, display: 'inline-block' }}></span>
                <div className="font-bold text-lg text-gray-900 dark:text-gray-100 text-center leading-snug">{selectedWallet.name}</div>
                {selectedWallet.type && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider">
                    {selectedWallet.type}
                  </div>
                )}
                
                {/* Dettagli tecnici specifici per tipologia (ETF/Azioni/Obbligazioni/Conto Deposito) */}
                {(selectedWallet.grossAnnualRate || selectedWallet.isinCode || selectedWallet.isinName) && (
                  <div className="flex flex-col items-center justify-center gap-1.5 mt-0.5 mb-1 w-full text-center">
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      {selectedWallet.grossAnnualRate && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800/60">
                          Tasso: {selectedWallet.grossAnnualRate}%
                        </span>
                      )}
                      {selectedWallet.isinCode && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold font-mono bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800/60">
                          {selectedWallet.isinCode}
                        </span>
                      )}
                    </div>
                    {selectedWallet.isinName && (
                      <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 px-2 break-words max-w-[220px]">
                        {selectedWallet.isinName}
                      </div>
                    )}
                  </div>
                )}
                
                <div
                  className={`text-sm font-bold ${selectedWallet.balance > 0
                    ? 'text-green-600'
                    : selectedWallet.balance < 0
                      ? 'text-red-600'
                      : 'text-gray-500 dark:text-gray-400'
                    }`}
                >
                  {formatCurrency(selectedWallet.balance)}
                </div>
              </div>
              <button
                onClick={handleEditWalletFromDialog}
                className="w-full flex items-center gap-2 justify-center py-3 mb-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold"
              >
                <Edit className="w-5 h-5" /> Modifica
              </button>
              <button
                onClick={handleOpenWalletCheck}
                className="w-full flex items-center gap-2 justify-center py-3 mb-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-semibold"
              >
                <CheckCircle className="w-5 h-5" /> Controlla
              </button>
              <button
                onClick={handleDeleteWalletFromDialog}
                className="w-full flex items-center gap-2 justify-center py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-semibold"
              >
                <Trash2 className="w-5 h-5" /> Elimina
              </button>
            </div>
          </div>
        )
      }

      {
        showWalletCheck && walletCheckWallet && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100000]" onClick={closeWalletCheck}>
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg p-5 border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <span className="w-4 h-4 rounded-full border border-white shadow-sm" style={{ backgroundColor: walletCheckWallet.color }}></span>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Conto selezionato</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{walletCheckWallet.name}</div>
                  </div>
                </div>
                <button onClick={closeWalletCheck} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className={`rounded-2xl border p-4 mb-4 ${walletCheckTone.container}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${walletCheckTone.icon}`}>
                    {walletCheckIsMatching ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Scostamento</div>
                    <div className={`text-2xl font-bold ${walletCheckIsMatching ? walletCheckTone.accent : 'text-red-700 dark:text-red-200'}`}>
                      {walletCheckDifferenceLabel}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {walletCheckGridItems.map((item) => {
                  if (item.type === 'note') {
                    const denomination = item.value;
                    return (
                      <div
                        key={`note-${denomination}`}
                        className="rounded-2xl border border-gray-200 dark:border-gray-800 p-3"
                      >
                        <div className="flex items-center justify-center mb-2">
                          <div className="font-semibold text-gray-900 dark:text-gray-100">Banconote da €{denomination}</div>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => adjustWalletCheckCount(denomination, -1)}
                            className="w-9 h-9 rounded-xl border border-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 transition"
                            aria-label={`Diminuisci banconote da ${denomination} euro`}
                          >
                            <Minus className="w-4 h-4 mx-auto" />
                          </button>
                          <input
                            type="text"
                            inputMode={isMobileViewport ? 'none' : 'numeric'}
                            value={walletCheckCounts[denomination] || ''}
                            onChange={(event) => handleWalletCheckCountChange(denomination, event.target.value)}
                            onFocus={() => openWalletCheckKeypad(denomination)}
                            onClick={() => openWalletCheckKeypad(denomination)}
                            onTouchEnd={(event) => { event.preventDefault(); openWalletCheckKeypad(denomination); }}
                            readOnly={isMobileViewport}
                            placeholder="0"
                            className="w-16 px-2 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                          />
                          <button
                            type="button"
                            onClick={() => adjustWalletCheckCount(denomination, 1)}
                            className="w-9 h-9 rounded-xl border border-gray-200 bg-gray-100 text-gray-700 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 transition"
                            aria-label={`Aumenta banconote da ${denomination} euro`}
                          >
                            <Plus className="w-4 h-4 mx-auto" />
                          </button>
                          <span className="text-sm text-gray-500 dark:text-gray-400">pz</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key="extra" className="rounded-2xl border border-gray-200 dark:border-gray-800 p-3">
                      <div className="font-semibold text-gray-900 dark:text-gray-100 mb-2 text-center">Extra</div>
                      <div className="form-input-group">
                        <Euro className="form-input-icon" />
                        <input
                          type="text"
                          inputMode={isMobileViewport ? 'none' : 'decimal'}
                          value={formatMoneyInput(walletCheckExtra)}
                          onChange={(event) => handleWalletCheckExtraChange(event.target.value)}
                          onFocus={() => openWalletCheckKeypad('extra')}
                          onClick={() => openWalletCheckKeypad('extra')}
                          onTouchEnd={(event) => { event.preventDefault(); openWalletCheckKeypad('extra'); }}
                          readOnly={isMobileViewport}
                          placeholder="0,00"
                          className="input form-input-with-icon"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={resetWalletCheckForm}
                  className="px-4 py-2 rounded-xl border border-gray-200 bg-gray-100 text-gray-800 hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 transition-all"
                >
                  Azzera
                </button>
                <button
                  type="button"
                  onClick={closeWalletCheck}
                  className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        )
      }
      <MobileAmountKeypad
        isOpen={showWalletCheckKeypad && isMobileViewport}
        value={walletCheckKeypadValue}
        onChange={handleWalletCheckKeypadChange}
        onClose={closeWalletCheckKeypad}
      />

      {/* Modal Conferma Eliminazione Wallet */}
      {
        showWalletConfirmDelete && selectedWallet && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999]" onClick={closeWalletConfirmDelete}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xs p-6 border border-gray-200 dark:border-gray-700 relative" onClick={e => e.stopPropagation()}>
              <button onClick={closeWalletConfirmDelete} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
              <div className="mb-6 text-center">
                <div className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-2">Conferma eliminazione</div>
                <div className="text-gray-600 dark:text-gray-300">Sei sicuro di voler eliminare il conto <span className='font-semibold'>{selectedWallet.name}</span>? Questa azione non può essere annullata.</div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={closeWalletConfirmDelete}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all font-semibold"
                  disabled={deletingWallet}
                >
                  Annulla
                </button>
                <button
                  onClick={confirmDeleteWalletFromDialog}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-semibold"
                  disabled={deletingWallet}
                >
                  {deletingWallet ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> : 'Elimina'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Modal Azioni Categorie */}
      {
        showCategoryActions && selectedCategory && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999]" onClick={closeCategoryActions}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xs p-6 border border-gray-200 dark:border-gray-700 relative" onClick={e => e.stopPropagation()}>
              <button onClick={closeCategoryActions} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
              <div className="flex flex-col items-center gap-2 mb-6">
                <span className="text-4xl">{selectedCategory.icon}</span>
                <div className="font-bold text-lg text-gray-900 dark:text-gray-100">{selectedCategory.name}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Categoria {selectedCategory.type === 'expense' ? 'Spese' : selectedCategory.type === 'income' ? 'Entrate' : 'Conti'}
                </div>
              </div>
              <button
                onClick={handleEditCategoryFromDialog}
                className="w-full flex items-center gap-2 justify-center py-3 mb-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold"
              >
                <Edit className="w-5 h-5" /> Modifica
              </button>
              <button
                onClick={handleDeleteCategoryFromDialog}
                className="w-full flex items-center gap-2 justify-center py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-semibold"
              >
                <Trash2 className="w-5 h-5" /> Elimina
              </button>
            </div>
          </div>
        )
      }

      {/* Modal Conferma Eliminazione Categoria */}
      {
        showCategoryConfirmDelete && selectedCategory && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999]" onClick={closeCategoryConfirmDelete}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xs p-6 border border-gray-200 dark:border-gray-700 relative" onClick={e => e.stopPropagation()}>
              <button onClick={closeCategoryConfirmDelete} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
              <div className="mb-6 text-center">
                <div className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-2">Conferma eliminazione</div>
                <div className="text-gray-600 dark:text-gray-300">Sei sicuro di voler eliminare la categoria <span className='font-semibold'>{selectedCategory.name}</span>? Questa azione non può essere annullata.</div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={closeCategoryConfirmDelete}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all font-semibold"
                  disabled={deletingCategory}
                >
                  Annulla
                </button>
                <button
                  onClick={confirmDeleteCategoryFromDialog}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-semibold"
                  disabled={deletingCategory}
                >
                  {deletingCategory ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> : 'Elimina'}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Models Dialog */}
      <ModelsDialog
        isOpen={showModelsDialog}
        onClose={handleCloseModelsDialog}
        type={modelsDialogType}
        models={models}
        onAddModel={handleAddModel}
        onEditModel={handleEditModel}
        onDeleteModel={handleDeleteModel}
        onUseModel={handleUseModel}
        onMoveModel={handleMoveModel}
        categories={categories[modelsDialogType] || []}

      />

      {/* Model Form */}
      <ModelForm
        isOpen={showModelForm}
        onClose={handleCloseModelForm}
        onSubmit={handleSubmitModel}
        type={modelsDialogType}
        categories={categories[modelsDialogType] || []}
        stores={stores}
        editingModel={editingModel}
        wallets={walletsWithCalculatedBalance}
        selectedWalletId={preferredWalletId}
      />

      {/* Modal Conferma Eliminazione Modello */}
      {
        showModelConfirmDelete && modelToDelete && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999]" onClick={closeModelConfirmDelete}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xs p-6 border border-gray-200 dark:border-gray-700 relative" onClick={e => e.stopPropagation()}>
              <button onClick={closeModelConfirmDelete} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
              <div className="mb-6 text-center">
                <div className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-2">Conferma eliminazione</div>
                <div className="text-gray-600 dark:text-gray-300">Sei sicuro di voler eliminare il modello <span className='font-semibold'>{modelToDelete.name}</span>? Questa azione non può essere annullata.</div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={closeModelConfirmDelete}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all font-semibold"
                >
                  Annulla
                </button>
                <button
                  onClick={confirmDeleteModel}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-semibold"
                >
                  Elimina
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Modal Conferma Eliminazione Movimento Debito */}
      {
        showDebtConfirmDelete && debtToDelete && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[99999]" onClick={closeDebtConfirmDelete}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-xs p-6 border border-gray-200 dark:border-gray-700 relative" onClick={e => e.stopPropagation()}>
              <button onClick={closeDebtConfirmDelete} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
              <div className="mb-6 text-center">
                <div className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-2">Conferma eliminazione</div>
                <div className="text-gray-600 dark:text-gray-300">Sei sicuro di voler eliminare questo movimento? Questa azione non può essere annullata.</div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={closeDebtConfirmDelete}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-all font-semibold"
                >
                  Annulla
                </button>
                <button
                  onClick={confirmDeleteDebt}
                  className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-semibold"
                >
                  Elimina
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Debt Form Modal */}
      {
        showDebtForm && (
          <DebtForm
            onSubmit={editingDebt ? editDebt : addDebt}
            onClose={handleCloseDebtForm}
            editingItem={editingDebt}
            wallets={wallets}
            existingPersons={getExistingPersons()}
            transactionType={debtTransactionType}
            defaultPersonName={prefilledDebtPerson}
          />
        )
      }

      {/* Person Detail Modal */}
      {
        showPersonDetail && selectedPerson && (
          <PersonDetail
            person={selectedPerson}
            debts={debts}
            incomes={incomes}
            wallets={walletsForDisplay}
            onClose={handleClosePersonDetail}
            onEditDebt={handleEditDebt}
            onDeleteDebt={handleConfirmDeleteDebt}
            onShowDebtTransactionDetail={handleShowDebtTransactionDetail}
            onResolveSituation={handleResolveSituation}
            onDeletePerson={deletePerson}
            onSalaryReceived={handleSalaryReceived}
            onOpenSalaryWizard={() => handleOpenSalaryWizard(selectedPerson.name)}
          />
        )
      }

      {/* Salary Wizard Modal */}
      {
        showSalaryWizard && salaryWizardPerson && (
          <SalaryWizard
            personName={salaryWizardPerson}
            incomes={incomes}
            wallets={wallets}
            onClose={handleCloseSalaryWizard}
            onApply={(payload) => {
              handleCloseSalaryWizard();
              handleSalaryReceived(salaryWizardPerson, payload);
            }}
          />
        )
      }

      {/* Dialog recupero categorie mancanti */}
      {showRecoverCategoriesDialog && (
        <RecoverCategoriesDialog
          missingCategories={missingCategories}
          onConfirm={handleConfirmRecoverCategories}
          onClose={() => setShowRecoverCategoriesDialog(false)}
          loading={isRecoveringCategories}
        />
      )}
    </div >
  );
}

// Wrapper component con PopupProvider
const AppWithPopup = () => {
  return (
    <PopupProvider>
      <App />
    </PopupProvider>
  );
};

export default AppWithPopup;
